import {
    ArrowLeft,
    ArrowRight,
    Blocks,
    Bomb,
    Castle,
    ChartNoAxesColumnIncreasing,
    ChevronDown,
    ChevronUp,
    Copy,
    createIcons,
    Eraser,
    Flag as FlagIcon,
    Hand,
    Home,
    Info,
    MousePointer2,
    Music,
    Play,
    Redo2,
    RotateCcw,
    Scan,
    ShieldCheck,
    SlidersHorizontal,
    Sparkles,
    Square,
    Swords,
    Trophy,
    Trash2,
    Undo2,
    Volume2,
    VolumeX,
    X,
    ZoomIn,
    ZoomOut
} from 'lucide'
import {
    BLOCK_SHAPES,
    BLOCK_SIZE,
    BOMBS,
    BUILDER_BUDGET,
    DEFAULT_BLOCK_SHAPE,
    FLAG,
    MATERIALS,
    WORLD
} from './constants.js'
import { GameAudio } from './audio.js'
import { GameScene } from './scene.js'
import {
    createBuildOpenGraphImage,
    createBuildPreviewImage,
    createResultOpenGraphImage
} from './open-graph.js'
import {
    calculateBombsGranted,
    calculateBudgetUsed,
    calculateMaterialCounts,
    canPublishBuild,
    createId,
    createShareUrl,
    decodeMap,
    getBlockCost,
    normalizeBuildRow,
    normalizeText
} from './maps.js'
import {
    createResultShareUrl,
    createXShareUrl,
    updateBuildShareMeta,
    updateDefaultShareMeta,
    updateResultShareMeta
} from './social.js'
import {
    applyDocumentLocale,
    getInitialLocale,
    getLocale,
    getLocaleInfo,
    getSupportedLocales,
    localizeHtml,
    localizeText,
    setLocale,
    t
} from './i18n.js'
import {
    fetchAttemptById,
    fetchBuildById,
    fetchBuilds,
    fetchLeaderboards,
    getAccessProfile,
    getAudioAssetUrl,
    getProfile,
    isBackendConfigured,
    publishBuild,
    recordAttempt,
    saveAccessProfile,
    saveProfile
} from './storage.js'
import {
    buildPhysicsGraph,
    computeStability,
    getSupportedBlockIdsFromGraph,
    predictVerticalImpact,
    propagateHorizontalImpact
} from './physics-analysis.js'
import {
    calculateCollisionDamage,
    calculateFlagImpactDamage,
    calculateImpactDamage,
    getBlockMaxHealth,
    getDamageTuning,
    resetDamageTuning,
    setDamageTuningValue
} from './physics-damage.js'
import { predictProjectilePath, airFrictionToDamping, SIM_STEP_SECONDS } from './physics/world.js'
import {
    addBlockBody,
    addBuilderBounds,
    addDestroyerBounds,
    addFlagBody,
    createStageWorld,
    DEFAULT_GRAVITY,
    DEFAULT_TIME_SCALE,
    getBlockBodyProps
} from './physics/stage.js'
import { Vector } from './physics/vector.js'

const DESTROYER_GRAVITY = DEFAULT_GRAVITY
const DESTROYER_TIME_SCALE = DEFAULT_TIME_SCALE
const BULLET_LAUNCH_POWER = 1.25
const MIN_BULLET_LAUNCH_SPEED = 0.23
const MAX_BULLET_PULL_DISTANCE = 3.22
const DEFAULT_BOMB_DENSITY = 0.018
const BOMB_AIR_RESISTANCE = 0.0025
const LAUNCH_WEIGHT_MIN_FACTOR = 0.96
const LAUNCH_WEIGHT_MAX_FACTOR = 1.29
const TRAJECTORY_PREVIEW_RATIO = 1
const TRAJECTORY_PREVIEW_MIN_SPEED = 0.105
const TRAJECTORY_MAX_SECONDS = 6
const READY_BULLET_ID = 'ready-bullet'
const BULLET_MAX_ACTIVE_MS = 2500
const BULLET_MIN_ACTIVE_MS = 250
// A bullet that has hit something and slowed below this (step units) is done.
const BULLET_REST_SPEED = 0.6
// v2: physics moved to Planck; tuning saved for the old engine no longer applies.
const TUNING_STORAGE_KEY = 'build-destroy-runtime-tuning-v2'
const TUNING_TABS = ['physics', 'bullets', 'materials']
// The tuning panel is a developer tool: dev builds, or ?tune in the URL.
const SHOW_TUNING_TOOLS = import.meta.env.DEV || new URLSearchParams(window.location.search).has('tune')
const DEFAULT_DESTROYER_TUNING = {
    gravity: DESTROYER_GRAVITY,
    timeScale: DESTROYER_TIME_SCALE,
    launchPower: BULLET_LAUNCH_POWER,
    minLaunchSpeed: MIN_BULLET_LAUNCH_SPEED,
    maxPullDistance: MAX_BULLET_PULL_DISTANCE,
    defaultAirResistance: BOMB_AIR_RESISTANCE,
    launchWeightMinFactor: LAUNCH_WEIGHT_MIN_FACTOR,
    launchWeightMaxFactor: LAUNCH_WEIGHT_MAX_FACTOR,
    trajectoryPreviewRatio: TRAJECTORY_PREVIEW_RATIO,
    trajectoryMinSpeed: TRAJECTORY_PREVIEW_MIN_SPEED,
    bulletMaxActiveMs: BULLET_MAX_ACTIVE_MS,
    bulletMinActiveMs: BULLET_MIN_ACTIVE_MS,
    bulletRestSpeed: BULLET_REST_SPEED
}
const destroyerTuning = { ...DEFAULT_DESTROYER_TUNING }
const ORIGINAL_BOMBS = clonePlain(BOMBS)
const ORIGINAL_MATERIALS = clonePlain(MATERIALS)
const PHYSICS_TUNING_FIELDS = [
    { key: 'gravity', label: 'Gravity', min: -2.4, max: -0.35, step: 0.01 },
    { key: 'timeScale', label: 'Time scale', min: 0.15, max: 0.8, step: 0.01 }
]
const SLING_TUNING_FIELDS = [
    { key: 'launchPower', label: 'Launch power', min: 0.2, max: 1.8, step: 0.01 },
    { key: 'maxPullDistance', label: 'Max pull distance', min: 1.5, max: 5.2, step: 0.01 },
    { key: 'minLaunchSpeed', label: 'Minimum launch speed', min: 0, max: 0.75, step: 0.01 },
    { key: 'defaultAirResistance', label: 'Default air resistance', min: 0, max: 0.01, step: 0.0001 },
    { key: 'launchWeightMinFactor', label: 'Min weight launch factor', min: 0.2, max: 2.2, step: 0.01 },
    { key: 'launchWeightMaxFactor', label: 'Max weight launch factor', min: 0.2, max: 2.8, step: 0.01 },
    { key: 'trajectoryPreviewRatio', label: 'Trajectory preview length', min: 0.1, max: 1, step: 0.01 },
    { key: 'trajectoryMinSpeed', label: 'Preview minimum speed', min: 0, max: 0.35, step: 0.005 },
    { key: 'bulletMaxActiveMs', label: 'Bullet max active ms', min: 600, max: 9000, step: 50 },
    { key: 'bulletMinActiveMs', label: 'Bullet min active ms', min: 100, max: 2500, step: 50 },
    { key: 'bulletRestSpeed', label: 'Bullet spent speed', min: 0, max: 2, step: 0.01 }
]
const DAMAGE_TUNING_FIELDS = [
    { key: 'damageScale', label: 'Damage compensation', min: 10, max: 600, step: 1 },
    { key: 'flagDirectDamageScale', label: 'Flag damage scale', min: 0.1, max: 5, step: 0.05 },
    { key: 'collisionDamageScale', label: 'Collision damage scale', min: 0, max: 2000, step: 10 }
]
const BULLET_TUNING_GROUPS = [
    { title: 'Body and flight', paths: ['radius', 'density', 'airResistance', 'groundBounce'] },
    { title: 'Impact', paths: ['impactStrength', 'impactForce', 'materialMultiplier', 'offMaterialMultiplier'] },
    { title: 'Material matchups', prefixes: ['materialMultipliers.'] },
    { title: 'Blast', paths: ['blastRadius', 'blastDamage', 'blastImpulse'] }
]
const MATERIAL_TUNING_GROUPS = [
    { title: 'Body physics', paths: ['density', 'friction', 'frictionAir', 'restitution'] },
    { title: 'Strength', paths: ['baseHealth', 'health', 'toughness', 'compressionStrength', 'shearStrength', 'supportStrength'] },
    { title: 'Break behavior', paths: ['brittleness', 'shatterThreshold', 'brittleImpactSpeed', 'brittleImpactDamageMultiplier', 'fallDamage', 'impactDamping'] },
    { title: 'Economy', paths: ['cost'] }
]
const MATERIAL_LINKED_CONTROLS = [
    {
        key: 'grip',
        label: 'Grip',
        help: 'Linked control for surface friction. Higher values help pieces resist sideways sliding.',
        bindings: [
            { path: 'friction', min: 0.2, max: 1.2 }
        ]
    },
    {
        key: 'stability',
        label: 'Stability',
        help: 'Linked control for mass, grip, and support strength. Higher values make this material hold stacks more firmly.',
        bindings: [
            { path: 'density', min: 0.003, max: 0.05 },
            { path: 'friction', min: 0.3, max: 1.1 },
            { path: 'compressionStrength', min: 0.42, max: 1.55 },
            { path: 'shearStrength', min: 0.35, max: 1.35 },
            { path: 'supportStrength', min: 0.34, max: 1.55 }
        ]
    },
    {
        key: 'durability',
        label: 'Durability',
        help: 'Linked control for health and toughness. Higher values make blocks harder to break and better under load.',
        bindings: [
            { path: 'baseHealth', min: 18, max: 145 },
            { path: 'health', min: 18, max: 145 },
            { path: 'toughness', min: 0.28, max: 1.55 },
            { path: 'compressionStrength', min: 0.45, max: 1.58 },
            { path: 'shearStrength', min: 0.34, max: 1.35 },
            { path: 'supportStrength', min: 0.34, max: 1.55 }
        ]
    },
    {
        key: 'fragility',
        label: 'Fragility',
        help: 'Linked control for brittle failure. Higher values make blocks shatter sooner and take stress damage earlier.',
        bindings: [
            { path: 'brittleness', min: 0, max: 1.8 },
            { path: 'shatterThreshold', min: 0.055, max: 0.003 },
            { path: 'fallDamage', min: 0.6, max: 1.8 },
            { path: 'toughness', min: 1.35, max: 0.28 },
            { path: 'impactDamping', min: 1.2, max: 0.38 }
        ]
    }
]
const TUNING_HELP_TEXT = {
    gravity: 'Downward pull for destroyer physics. This world uses negative Y for downward gravity.',
    timeScale: 'Simulation speed while attacking. Lower values make motion slower and easier to read; higher values make it faster.',
    launchPower: 'Multiplier applied to pull distance when converting the sling pull into launch velocity.',
    maxPullDistance: 'Maximum distance the sling can be pulled back before the shot stops gaining power.',
    minLaunchSpeed: 'Minimum launch speed given to a released shot, even for a tiny pull.',
    defaultAirResistance: 'Fallback drag applied to shots that do not define their own air resistance.',
    launchWeightMinFactor: 'Launch boost for the lightest shots; higher values make light bullets leave faster.',
    launchWeightMaxFactor: 'Launch boost for the heaviest shots; higher values make dense bullets leave faster.',
    trajectoryPreviewRatio: 'How much of the predicted trajectory line is drawn before firing.',
    trajectoryMinSpeed: 'Trajectory preview stops once predicted speed drops below this value.',
    bulletMaxActiveMs: 'Longest time a fired shot can keep damaging blocks before it settles.',
    bulletMinActiveMs: 'Minimum time a fired shot remains active before rest checks can remove it.',
    bulletRestSpeed: 'Once a shot has hit something, it is retired when it slows below this speed.',
    damageScale: 'Global multiplier converting impact energy into block damage.',
    flagDirectDamageScale: 'Multiplier for direct bullet damage to the flag.',
    collisionDamageScale: 'Damage from blocks hitting each other, the ground or the flag. Heavier and faster impacts hurt more.',
    cost: 'Budget cost for placing this material in Builder Mode.',
    health: 'Legacy health value and fallback if base health is missing.',
    baseHealth: 'Main starting health used to calculate the max health for each block after shape and size scaling.',
    density: 'Mass per area. Higher density makes objects heavier, harder to shove, and more damaging on impact.',
    friction: 'Sliding resistance while objects are already moving against another surface.',
    frictionAir: 'Drag from air. Higher values slow moving blocks or shots faster.',
    restitution: 'Bounciness after collisions. Higher values rebound more; lower values absorb impacts.',
    toughness: 'Resistance against incoming impact damage. Higher values reduce damage taken.',
    brittleness: 'Extra shatter damage when impact energy crosses the shatter threshold.',
    compressionStrength: 'How well a block survives vertical load from blocks stacked above it.',
    shearStrength: 'How well a block resists sideways force and sliding support failures.',
    supportStrength: 'How much structural support this material gives to pieces resting on it.',
    shatterThreshold: 'Impact energy threshold where brittle shatter bonuses begin.',
    brittleImpactSpeed: 'Minimum hit speed that can trigger the brittle impact damage floor.',
    brittleImpactDamageMultiplier: 'Minimum damage multiplier for clean high-speed hits on brittle material.',
    fallDamage: 'Fragility in collisions: multiplier for damage this material takes when it hits, or is hit by, blocks and the ground.',
    impactDamping: 'Share of impact energy absorbed by this material. Lower values pass more force through.',
    radius: 'Projectile size. Larger shots hit a bigger area and usually have more mass.',
    impactStrength: 'Projectile damage strength used when impact energy is converted into block damage.',
    impactForce: 'Impulse applied on collision. Higher values shove blocks harder.',
    materialMultiplier: 'Fallback damage multiplier against the preferred target material for this bullet.',
    offMaterialMultiplier: 'Fallback damage multiplier against non-preferred materials.',
    airResistance: 'Drag applied while this projectile flies.',
    groundBounce: 'How much velocity this projectile keeps after hitting the ground.',
    blastRadius: 'Explosion radius around the impact point.',
    blastDamage: 'Explosion damage before distance falloff.',
    blastImpulse: 'Explosion push: speed kick given to blocks at the centre of the blast.'
}
const EXCLUDED_TUNING_NUMERIC_KEYS = new Set(['color'])

loadRuntimeTuningFromStorage()
const SHOT_UI_HIDE_MAX_MS = 1500
// After the last bullet is gone, the next shot unlocks when the fortress
// stops moving, or after this much game time at most.
const SHOT_SETTLE_MAX_MS = 1200
// Rolling resistance for bullets (Planck circles otherwise roll forever).
const BULLET_ANGULAR_DAMPING = 2.5
const FLAG_CAPTURE_SCORE = 500
const BLOCK_SCORE_BASE = {
    stone: 120,
    wood: 90,
    glass: 70
}
const DESTROYER_STABLE_SPEED = 0.035
const DESTROYER_STABLE_ANGULAR_SPEED = 0.018
// Fine step: angle snapping, 45 degree blocks and Shift+rotate. The default
// rotate action turns blocks by the coarse step.
const ROTATION_STEP = Math.PI / 4
const ROTATION_STEP_COARSE = Math.PI / 2
const BUILDER_HISTORY_LIMIT = 50
const BUILDER_LAYOUT_EPSILON = 0.001
const BUILDER_DRAG_START_DISTANCE = WORLD.grid * 0.14
const BUILDER_PHYSICS_GRID_SNAP_EPSILON = WORLD.grid * 0.12
const BUILDER_PHYSICS_ANGLE_SNAP_EPSILON = ROTATION_STEP * 0.025
const BUILDER_BEAM_HIT_MIN_HEIGHT = WORLD.grid * 0.9
const BUILDER_CREATIVE_DRAG_ANGLE_EPSILON = 0.08
const DESTROYER_FLAG_HEALTH = 30
const FLAG_HIT_MIN_DAMAGE = DESTROYER_FLAG_HEALTH * 0.15
// Share of leftover speed a bullet keeps after smashing through a block.
const BULLET_BREAKTHROUGH_RETENTION = 0.85
const FLAG_GRAZE_MIN_DAMAGE = DESTROYER_FLAG_HEALTH * 0.18
const FLAG_GRAZE_COOLDOWN_MS = 360
const FLAG_GRAZE_MARGIN = WORLD.grid * 0.22
const FLAG_EXPOSED_COVER_THRESHOLD = 0.52
const FLAG_LOW_HEALTH_THRESHOLD = 45
const DESTROYER_RESULT_DELAY_MS = 1500
const CAMERA_LAUNCH_FOLLOW_EASE = 6.2
const CAMERA_PAN_START_PIXELS = 6
const CAMERA_WHEEL_ZOOM_SPEED = 0.0018
const BUILDER_CAMERA_MIN_ZOOM = 0.85
const BUILDER_CAMERA_MAX_ZOOM = 3.4
const BUILDER_CAMERA_ZOOM_BUTTON_FACTOR = 1.18
const BUILDER_KEYBOARD_PREVIEW_ID = 'builder-keyboard-preview'
const BUILDER_SNAP_STEP = WORLD.grid / 2
const BUILDER_KEYBOARD_STEP = BUILDER_SNAP_STEP
const BUILDER_PUBLISH_PHYSICS_TEST_MS = 1800
const BUILDER_PUBLISH_MIN_HEALTH_RATIO = 0.5
const BUILDER_PUBLISH_MAX_DRIFT = WORLD.grid * 0.5
const BUILDER_PUBLISH_MAX_TILT = 0.3
const BUILDER_PUBLISH_FLAG_FULL_HEALTH_EPSILON = 0.001
const BUILDER_SHAPE_SHORTCUTS = ['square', 'rectangle', 'beam']
const BUILDER_MATERIAL_SHORTCUTS = {
    s: 'stone',
    w: 'wood',
    g: 'glass'
}
const LEADERBOARD_FILTERS = ['all', 'low', 'medium', 'high']
const LEADERBOARD_PREVIEW_ROWS = 4
const UI_ICONS = {
    ArrowLeft,
    ArrowRight,
    Blocks,
    Bomb,
    Castle,
    ChartNoAxesColumnIncreasing,
    ChevronDown,
    ChevronUp,
    Copy,
    Eraser,
    Flag: FlagIcon,
    Hand,
    Home,
    Info,
    MousePointer2,
    Music,
    Play,
    Redo2,
    RotateCcw,
    Scan,
    ShieldCheck,
    SlidersHorizontal,
    Sparkles,
    Square,
    Swords,
    Trophy,
    Trash2,
    Undo2,
    Volume2,
    VolumeX,
    X,
    ZoomIn,
    ZoomOut
}

export class GameApp {
    constructor({ canvas, ui }) {
        this.canvas = canvas
        this.ui = ui
        this.locale = getInitialLocale()
        setLocale(this.locale)
        this.scene = new GameScene(canvas)
        this.audio = new GameAudio({ getAssetUrl: getAudioAssetUrl })
        this.accessProfile = getAccessProfile()
        this.profile = getProfile()
        this.identityDraft = this.createInitialProfileDraft()
        this.destroyerName = this.identityDraft.displayName
        this.destroyerXHandle = this.identityDraft.xHandle
        this.mode = 'home'
        this.selectedMaterial = 'wood'
        this.selectedBlockShape = DEFAULT_BLOCK_SHAPE
        this.selectedBlockId = null
        this.selectedFlag = false
        this.builderTitle = t('defaults.myFortress')
        this.builderBlocks = []
        this.builderFlag = { x: WORLD.flagX, y: WORLD.flagY }
        this.builderProfileOpen = true
        this.builderPanelCollapsed = false
        this.builderPanMode = false
        this.builderPhysicsEnabled = false
        this.destroyerAmmoOpen = false
        this.builderPhysics = null
        this.builderDrag = null
        this.builderRestoreSnapshot = null
        this.builderPublishValidation = null
        this.builderKeyboardActive = hasKeyboardControlSurface()
        this.builderKeyboardCursor = null
        this.builderKeyboardRotation = 0
        this.builderHistory = { undo: [], redo: [] }
        this.builderHover = null
        this.builderGhost = null
        this.gallery = []
        this.galleryFilter = 'all'
        this.leaderboards = { builders: [], destroyers: [] }
        this.leaderboardFilter = 'all'
        this.expandedLeaderboardBoards = { builders: false, destroyers: false }
        this.activeBuild = null
        this.shareUrl = ''
        this.status = ''
        this.aboutOpen = false
        this.builderClearConfirmOpen = false
        this.audioControlsOpen = false
        this.tuningTab = 'physics'
        this.tuningBulletType = 'basic'
        this.tuningMaterialKey = 'stone'
        this.tuningPanelOpen = false
        this.tuningPanelPosition = null
        this.resultWon = false
        this.resultAttempt = null
        this.resultBombsUsed = 0
        this.resultDurationMs = 0
        this.resultScore = 0
        this.physicsWorld = null
        this.physics = null
        this.draggingLaunch = null
        this.draggingTuningPanel = null
        this.resultTimer = null
        this.cameraPan = null
        this.cameraPanOverride = false
        if (this.accessProfile?.displayName) {
            this.applyUserIdentity(this.accessProfile)
        } else {
            this.profile = {
                ...this.profile,
                displayName: this.identityDraft.displayName,
                xHandle: this.identityDraft.xHandle
            }
        }
        this.lastTime = performance.now()

        this.bindEvents()
        this.bootstrapFromUrl()
        this.tick()
    }

    bindEvents() {
        this.ui.addEventListener('click', (event) => this.handleUiClick(event))
        this.ui.addEventListener('input', (event) => this.handleUiInput(event))
        this.ui.addEventListener('submit', (event) => {
            this.handleUiSubmit(event)
        })
        this.ui.addEventListener('pointerdown', (event) => this.handleUiPointerDown(event))
        this.ui.addEventListener('pointermove', (event) => this.handlePointerMove(event))
        this.ui.addEventListener('pointerup', (event) => this.handlePointerUp(event))
        this.ui.addEventListener('pointercancel', (event) => this.handlePointerUp(event))
        this.canvas.addEventListener('pointerdown', (event) => this.handlePointerDown(event))
        this.canvas.addEventListener('pointermove', (event) => this.handlePointerMove(event))
        this.canvas.addEventListener('pointerup', (event) => this.handlePointerUp(event))
        this.canvas.addEventListener('pointercancel', (event) => this.handlePointerUp(event))
        this.canvas.addEventListener('pointerleave', () => this.clearBuilderHover())
        this.canvas.addEventListener('wheel', (event) => this.handleCanvasWheel(event), { passive: false })
        window.addEventListener('keydown', (event) => this.handleKeyDown(event))
        this.audio.bindUnlock([this.canvas, this.ui])
    }

    renderUi(content) {
        this.setAimingUi(false)
        applyDocumentLocale()
        this.ui.innerHTML = localizeHtml(`${content}${renderAudioControls(this.audio.getState(), this.audioControlsOpen)}`)
        this.refreshIcons()
    }

    setAimingUi(active) {
        this.ui.classList.toggle('is-aiming', Boolean(active))
    }

    refreshAudioControls() {
        const controller = this.ui.querySelector('.audio-controller')
        if (controller) {
            controller.outerHTML = localizeHtml(renderAudioControls(this.audio.getState(), this.audioControlsOpen))
            this.refreshIcons()
        }
    }

    refreshIcons() {
        createIcons({
            icons: UI_ICONS,
            attrs: {
                'stroke-width': 2.8,
                'aria-hidden': 'true'
            }
        })
    }

    setMode(mode) {
        if (this.mode !== mode) {
            this.audioControlsOpen = false
        }
        if (mode !== 'builder') {
            this.builderHover = null
            this.builderGhost = null
        }
        this.mode = mode
    }

    closeAudioControls() {
        if (!this.audioControlsOpen) {
            return
        }

        this.audioControlsOpen = false
        this.refreshAudioControls()
    }

    closeAudioControlsFromOutsidePointer(event) {
        if (!this.audioControlsOpen || event.target.closest?.('.audio-controller')) {
            return false
        }

        this.closeAudioControls()
        return true
    }

    createInitialProfileDraft() {
        const savedDestroyerName = localStorage.getItem('build-destroy-destroyer-name') || ''
        const savedDestroyerXHandle = localStorage.getItem('build-destroy-destroyer-x-handle') || ''

        return {
            displayName: this.accessProfile?.displayName
                || normalizeText(this.profile.displayName, '', 40)
                || normalizeText(savedDestroyerName, '', 40)
                || '',
            xHandle: this.accessProfile?.xHandle
                || this.profile.xHandle
                || savedDestroyerXHandle
                || ''
        }
    }

    applyUserIdentity(profile) {
        const accessProfile = saveAccessProfile(profile)

        if (!accessProfile) {
            return false
        }

        this.accessProfile = accessProfile
        this.identityDraft = accessProfile
        this.profile = saveProfile({
            ...this.profile,
            displayName: accessProfile.displayName,
            xHandle: accessProfile.xHandle
        })
        this.destroyerName = accessProfile.displayName
        this.destroyerXHandle = accessProfile.xHandle
        localStorage.setItem('build-destroy-destroyer-name', accessProfile.displayName)
        localStorage.setItem('build-destroy-destroyer-x-handle', accessProfile.xHandle)
        return true
    }

    async bootstrapFromUrl() {
        const params = new URLSearchParams(window.location.search)
        const buildId = params.get('build')
        const encodedMap = params.get('map')
        const attemptId = params.get('attempt')

        if (buildId) {
            this.renderHome('Loading shared build...')
            try {
                const build = await fetchBuildById(buildId)
                if (build) {
                    const attempt = attemptId ? await this.fetchShareAttempt(attemptId, build.id) : null
                    const shareUrl = attempt ? createResultShareUrl(build, attempt) : createShareUrl(build)
                    if (attempt) {
                        updateResultShareMeta({ build, attempt, shareUrl })
                    } else {
                        updateBuildShareMeta(build, shareUrl)
                    }
                    this.startDestroyer(build)
                    return
                }
                this.renderHome('Shared build was not found.')
            } catch (error) {
                this.renderHome(`Unable to load shared build: ${error.message}`)
            }
            return
        }

        if (encodedMap) {
            const build = decodeMap(encodedMap)
            if (build) {
                updateBuildShareMeta(build, createShareUrl(build))
                this.startDestroyer(build)
                return
            }
            this.renderHome('The shared map link could not be decoded.')
            return
        }

        this.renderHome()
    }

    async fetchShareAttempt(attemptId, buildId) {
        try {
            const attempt = await fetchAttemptById(attemptId)
            return attempt?.buildId === buildId ? attempt : null
        } catch (error) {
            console.warn('Unable to load shared attempt', error)
            return null
        }
    }

    renderHome(message = '') {
        this.clearResultTimer()
        this.builderPublishValidation = null
        this.setMode('home')
        this.canvas.style.cursor = ''
        this.cameraPan = null
        this.cameraPanOverride = false
        this.builderPanMode = false
        this.destroyPhysics()
        this.destroyBuilderPhysics()
        this.scene.clearWorld()
        this.scene.setBuildZoneVisible(false)
        this.scene.setLauncherVisible(false)
        this.scene.resetCamera()
        updateDefaultShareMeta()
        this.status = message
        const storageStatus = isBackendConfigured() ? 'backend: connected' : 'backend: local demo'
        this.renderUi(`
            <main class="home-panel fortress-home">
                <section class="brand-stack">
                    <p class="eyebrow">Fortress Duel</p>
                    <h1>Build & Destroy</h1>
                    <p class="lede">Stack stone, wood, and glass around your flag. Then test rival fortresses with a siege launcher and the ammo their choices unlock.</p>
                </section>
                <section class="role-grid" aria-label="Choose role">
                    <button class="role-card builder-card" data-action="start-builder" style="--mode-art:url('/art/builder-mode-card.png')">
                        <span class="mode-card-art" aria-hidden="true"></span>
                        <span>Builder Mode</span>
                        <small>Craft a ${BUILDER_BUDGET}-point fortress and protect the flag.</small>
                        <b>Start Builder</b>
                    </button>
                    <button class="role-card destroyer-card" data-action="start-gallery" style="--mode-art:url('/art/destroyer-mode-card.png')">
                        <span class="mode-card-art" aria-hidden="true"></span>
                        <span>Destroyer Mode</span>
                        <small>Choose ammo, aim the launcher, and capture flags.</small>
                        <b>Start Attack</b>
                    </button>
                    <button class="role-card leaderboard-card" data-action="show-leaderboards" style="--mode-art:url('/art/leaderboard-card.png')">
                        <span class="mode-card-art" aria-hidden="true"></span>
                        <span>Leaderboard</span>
                        <small>Best defenses and cleanest captures.</small>
                        <b>Ranks</b>
                    </button>
                </section>
                <footer class="home-footer" aria-label="Build and service information">
                    <span>${storageStatus}</span>
                    <span>vibe coded by <a href="https://x.com/domenicodd" target="_blank" rel="noreferrer">@domenicodd</a></span>
                    <button class="footer-link" data-action="show-about" type="button">About</button>
                </footer>
                ${this.status ? `<p class="toast inline">${this.status}</p>` : ''}
                ${this.aboutOpen ? renderAboutModal() : ''}
            </main>
        `)
    }

    renderBuilder() {
        this.clearResultTimer()
        const shouldFrameCamera = this.mode !== 'builder'
        this.setMode('builder')
        this.updateBuilderCanvasCursor()
        this.destroyPhysics()
        if (this.builderPhysicsEnabled) {
            this.ensureBuilderPhysics()
        } else {
            this.destroyBuilderPhysics()
        }
        const budgetUsed = calculateBudgetUsed(this.builderBlocks)
        const materialCounts = calculateMaterialCounts(this.builderBlocks)
        const publishState = canPublishBuild(this.builderBlocks)
        const isTestingPublishPhysics = Boolean(this.builderPublishValidation)
        const canUseBuilderActions = !isTestingPublishPhysics
        const selectedMaterial = MATERIALS[this.selectedMaterial] || MATERIALS.wood
        const selectedShape = getBlockShape(this.selectedBlockShape)
        const selectedBlockCost = getBlockCost({
            material: this.selectedMaterial,
            shape: this.selectedBlockShape,
            width: selectedShape.width,
            height: selectedShape.height
        })
        this.builderGhost = this.getBuilderGhost()
        const sceneBlocks = this.getBuilderSceneBlocks()
        const sceneSelectedId = this.getBuilderSceneSelectedId()
        this.scene.showBuilder(sceneBlocks, sceneSelectedId, this.builderFlag, this.selectedFlag, {
            frameCamera: shouldFrameCamera && !this.cameraPanOverride
        })
        this.scene.setBuilderGhost(this.builderGhost?.block, this.builderGhost?.valid)

        this.renderUi(`
            <main class="game-shell builder-shell ${this.builderPanelCollapsed ? 'builder-panel-is-collapsed' : ''} ${this.builderPanMode ? 'builder-pan-is-active' : ''}">
                <aside class="builder-command-panel" aria-label="Builder controls">
                    <button class="builder-panel-collapse" data-action="toggle-builder-panel" aria-label="${this.builderPanelCollapsed ? 'Open builder kit' : 'Collapse builder kit'}" aria-expanded="${!this.builderPanelCollapsed}">
                        <span>${this.builderPanelCollapsed ? 'Kit' : 'Hide'}</span>
                    </button>
                    <header class="builder-panel-banner">
                        <button class="builder-back-button" data-action="home" aria-label="Back home">
                            <i data-lucide="arrow-left" aria-hidden="true"></i>
                            <b>Back</b>
                        </button>
                        <div class="builder-title-stack">
                            <h2>Builder Mode</h2>
                            <small>${escapeHtml(this.builderTitle)}</small>
                        </div>
                    </header>
                    <section class="builder-budget-card" aria-label="Build budget">
                        <div class="builder-section-title">
                            <span>Budget</span>
                            <strong>${budgetUsed}/${BUILDER_BUDGET}</strong>
                        </div>
                        <div class="builder-budget-meter"><span style="width:${Math.min(100, (budgetUsed / BUILDER_BUDGET) * 100)}%"></span></div>
                        <em class="${publishState.ok ? 'ok' : ''}">${publishState.ok ? 'Ready to publish.' : publishState.reason}</em>
                    </section>
                    <section class="builder-kit-panel">
                        <div class="panel-heading">
                            <div>
                                <p class="eyebrow">Blocks Kit</p>
                                <h3>${selectedMaterial.label} Kit</h3>
                            </div>
                            <span class="selected-material-chip" style="--accent:${selectedMaterial.accent}">${selectedBlockCost} pts</span>
                        </div>
                        <div class="hotbar-bank">
                            <div class="hotbar-slots materials-hotbar" role="group" aria-label="Materials">
                                ${renderMaterialSlots(this.selectedMaterial, materialCounts, this.selectedBlockShape)}
                            </div>
                            <div class="hotbar-slots shapes-hotbar" role="group" aria-label="Block size">
                                ${renderShapeButtons(this.selectedBlockShape, this.selectedMaterial)}
                            </div>
                        </div>
                    </section>
                    <section class="builder-actions" aria-label="Builder actions">
                        <div class="builder-actions-label"><span>Actions</span></div>
                        <button class="builder-action-button action-static ${this.builderPhysicsEnabled ? 'is-selected' : ''}" data-action="toggle-builder-physics" ${canUseBuilderActions ? '' : 'disabled'}><i data-lucide="${this.builderPhysicsEnabled ? 'play' : 'square'}" aria-hidden="true"></i><span>${this.builderPhysicsEnabled ? 'Physics' : 'Static'}</span></button>
                        <button class="builder-action-button action-undo" data-action="undo-builder" aria-label="Undo" title="Undo (Ctrl/Cmd+Z)" ${this.builderHistory.undo.length && canUseBuilderActions ? '' : 'disabled'}><i data-lucide="undo-2" aria-hidden="true"></i><span>Undo</span></button>
                        <button class="builder-action-button action-redo" data-action="redo-builder" aria-label="Redo" title="Redo (Shift+Ctrl/Cmd+Z)" ${this.builderHistory.redo.length && canUseBuilderActions ? '' : 'disabled'}><i data-lucide="redo-2" aria-hidden="true"></i><span>Redo</span></button>
                        <button class="builder-action-button action-rotate" data-action="rotate-block" title="Rotate 90 degrees (Shift for 45)" ${this.selectedBlockId && canUseBuilderActions ? '' : 'disabled'}><i data-lucide="rotate-ccw" aria-hidden="true"></i><span>Rotate</span></button>
                        <button class="builder-action-button action-delete" data-action="delete-block" ${this.selectedBlockId && canUseBuilderActions ? '' : 'disabled'}><i data-lucide="trash-2" aria-hidden="true"></i><span>Delete</span></button>
                        <button class="builder-action-button action-clear" data-action="clear-build" ${canUseBuilderActions ? '' : 'disabled'}><i data-lucide="eraser" aria-hidden="true"></i><span>Clear</span></button>
                        ${this.builderRestoreSnapshot ? `<button class="builder-action-button action-restore" data-action="restore-builder-snapshot" ${canUseBuilderActions ? '' : 'disabled'}><i data-lucide="rotate-ccw" aria-hidden="true"></i><span>Restore saved layout</span></button>` : ''}
                        <button class="primary-button save-fortress-button" data-action="publish-build" ${publishState.ok && canUseBuilderActions ? '' : 'disabled'}><span>${isTestingPublishPhysics ? 'Testing physics' : 'Save Fortress'}</span><i data-lucide="flag" aria-hidden="true"></i></button>
                    </section>
                </aside>
                <section class="builder-view-controls" aria-label="View controls">
                    <button class="builder-view-button action-pan ${this.builderPanMode ? 'is-selected' : ''}" data-action="toggle-builder-pan" type="button" aria-label="${this.builderPanMode ? 'Disable pan mode' : 'Enable pan mode'}" aria-pressed="${this.builderPanMode}" title="${this.builderPanMode ? 'Disable pan mode' : 'Enable pan mode'}">
                        <i data-lucide="${this.builderPanMode ? 'mouse-pointer-2' : 'hand'}" aria-hidden="true"></i>
                    </button>
                    <button class="builder-view-button action-zoom-out" data-action="builder-zoom-out" type="button" aria-label="Zoom out" title="Zoom out">
                        <i data-lucide="zoom-out" aria-hidden="true"></i>
                    </button>
                    <button class="builder-view-button action-zoom-reset" data-action="builder-reset-view" type="button" aria-label="Reset view" title="Reset view">
                        <i data-lucide="scan" aria-hidden="true"></i>
                    </button>
                    <button class="builder-view-button action-zoom-in" data-action="builder-zoom-in" type="button" aria-label="Zoom in" title="Zoom in">
                        <i data-lucide="zoom-in" aria-hidden="true"></i>
                    </button>
                </section>
                <div class="builder-workbench-tip" aria-live="polite">
                    <span aria-hidden="true"></span>
                    <p>${this.status ? escapeHtml(this.status) : 'Place blocks inside the highlighted build zone.'}</p>
                </div>
                ${this.builderClearConfirmOpen ? renderClearBuildModal() : ''}
            </main>
        `)
    }

    async renderGallery(message = '') {
        this.builderPublishValidation = null
        this.setMode('gallery')
        this.canvas.style.cursor = ''
        this.cameraPan = null
        this.cameraPanOverride = false
        this.builderPanMode = false
        this.destroyPhysics()
        this.destroyBuilderPhysics()
        this.scene.clearWorld()
        this.scene.setBuildZoneVisible(false)
        this.scene.setLauncherVisible(false)
        this.scene.resetCamera()
        this.status = message || 'Loading builds...'
        this.renderUi(renderGalleryShell(this.galleryFilter, [], this.status, this.aboutOpen))

        try {
            this.gallery = await fetchBuilds({ complexity: this.galleryFilter })
            this.status = this.gallery.length ? '' : 'No builds match this filter yet.'
        } catch (error) {
            this.gallery = []
            this.status = `Unable to load builds: ${error.message}`
        }

        this.renderUi(renderGalleryShell(this.galleryFilter, this.gallery, this.status, this.aboutOpen))
    }

    async renderLeaderboards(message = '') {
        this.builderPublishValidation = null
        this.setMode('leaderboards')
        this.canvas.style.cursor = ''
        this.cameraPan = null
        this.cameraPanOverride = false
        this.builderPanMode = false
        this.destroyPhysics()
        this.destroyBuilderPhysics()
        this.scene.clearWorld()
        this.scene.setBuildZoneVisible(false)
        this.scene.setLauncherVisible(false)
        this.scene.resetCamera()
        this.status = message || 'Loading leaders...'
        this.renderUi(renderLeaderboardsShell(
            { builders: [], destroyers: [] },
            this.status,
            this.leaderboardFilter,
            this.expandedLeaderboardBoards
        ))

        try {
            this.leaderboards = await fetchLeaderboards({ limit: 24 })
            const hasRows = this.leaderboards.builders.length || this.leaderboards.destroyers.length
            this.status = hasRows ? '' : 'No leaderboard scores yet.'
        } catch (error) {
            this.leaderboards = { builders: [], destroyers: [] }
            this.status = `Unable to load leaderboards: ${error.message}`
        }

        this.renderUi(renderLeaderboardsShell(
            this.leaderboards,
            this.status,
            this.leaderboardFilter,
            this.expandedLeaderboardBoards
        ))
    }

    renderDestroyer() {
        const inventory = this.physics?.inventory || {}
        const activeType = this.physics?.currentBombType || 'basic'
        const remainingBullets = countInventory(inventory)
        const build = this.activeBuild
        const targetStats = getTargetFortressStats(this.physics, build)
        const outOfAmmo = remainingBullets <= 0
        const shotUiHidden = this.isDestroyerShotUiHidden()
        const tuningBulletType = BOMBS[this.tuningBulletType] ? this.tuningBulletType : 'basic'
        const tuningMaterialKey = MATERIALS[this.tuningMaterialKey] ? this.tuningMaterialKey : 'stone'
        const flagState = targetStats.flagCaptured ? 'captured' : targetStats.flagExposed ? 'exposed' : 'guarded'
        const flagLabel = { captured: 'Flag captured', exposed: 'Flag exposed', guarded: 'Flag guarded' }[flagState]
        const activeBomb = BOMBS[activeType] || BOMBS.basic

        this.renderUi(`
            <main class="game-shell destroyer-shell vintage-hud ${shotUiHidden ? 'is-shot-cinematic' : ''}">
                <div class="launcher-drag-zone" data-launch-zone aria-hidden="true"></div>
                <nav class="v-corner v-corner-left" aria-label="Navigation">
                    <button class="v-seal-button" data-action="start-gallery" type="button" aria-label="Back to builds" title="Back to builds">
                        <i data-lucide="arrow-left" aria-hidden="true"></i>
                    </button>
                </nav>
                <section class="v-banner" aria-label="Target fortress">
                    <p class="v-banner-kicker">The Siege of</p>
                    <h2>${escapeHtml(build.title)}</h2>
                    <p class="v-banner-by">raised by <span>${escapeHtml(build.builderName)}</span></p>
                    <div class="v-banner-rule" aria-hidden="true"></div>
                    <div class="v-tally" aria-label="Run status">
                        <span>Score <b data-destroyer-score>${formatScore(targetStats.score)}</b></span>
                        <span>Blocks <b data-destroyer-blocks>${targetStats.destroyedBlocks}/${targetStats.totalBlocks}</b></span>
                        <span>Shots <b data-destroyer-shots>${targetStats.bombsUsed}</b></span>
                    </div>
                    ${renderTargetStats(targetStats)}
                    <span class="v-stamp is-${flagState}">${flagLabel}</span>
                </section>
                <nav class="v-corner v-corner-right" aria-label="Fortress actions">
                    <button class="v-seal-button" data-action="restart-build" type="button" aria-label="Retry fortress" title="Retry">
                        <i data-lucide="rotate-ccw" aria-hidden="true"></i>
                    </button>
                    <button class="v-seal-button" data-action="duplicate-build" type="button" aria-label="Copy this fortress into the builder" title="Copy into builder">
                        <i data-lucide="copy" aria-hidden="true"></i>
                    </button>
                </nav>
                <footer class="v-ammo-tray ${outOfAmmo ? 'is-empty' : ''}" aria-label="Choose ammo">
                    <p class="v-ammo-advice">${outOfAmmo
                        ? 'Out of shot. Retry, or pick another fortress.'
                        : escapeHtml(activeBomb.tip || activeBomb.description || '')}</p>
                    <div class="v-ammo-tokens" role="group" aria-label="Ammo">
                        ${renderAmmoTokens(inventory, activeType)}
                    </div>
                </footer>
                ${!SHOW_TUNING_TOOLS ? '' : this.tuningPanelOpen
                    ? renderTuningPanel(this.tuningTab, tuningBulletType, tuningMaterialKey, this.tuningPanelPosition)
                    : renderTuningLauncher(this.tuningPanelPosition)}
                ${this.status ? `<p class="toast">${this.status}</p>` : ''}
            </main>
        `)

        this.syncReadyBullet()
    }

    // Game clock for destroyer timers. The balance lab swaps in a simulated
    // clock so trials can run faster than real time.
    now() {
        return this.trialClock ?? performance.now()
    }

    isDestroyerShotUiHidden(now = this.now()) {
        if (!this.physics?.shotUiHidden) {
            return false
        }

        if (this.shouldRevealDestroyerShotUi(now)) {
            this.physics.shotUiHidden = false
            return false
        }

        return true
    }

    shouldRevealDestroyerShotUi(now = this.now()) {
        if (!this.physics?.shotUiHidden) {
            return true
        }

        if (this.physics.bombs.size > 0) {
            return false
        }

        return now >= (this.physics.shotUiRevealAt || 0) || isDestroyerPhysicsStable(this.physics)
    }

    updateDestroyerShotUi(now = this.now()) {
        if (this.mode !== 'destroyer' || !this.physics?.shotUiHidden) {
            return
        }

        if (!this.shouldRevealDestroyerShotUi(now)) {
            return
        }

        this.physics.shotUiHidden = false
        this.renderDestroyer()
    }

    updateDestroyerLiveHud() {
        if (this.mode !== 'destroyer' || !this.physics) {
            return
        }

        const updates = [
            ['[data-destroyer-score]', formatScore(this.physics.score || 0)],
            ['[data-destroyer-blocks]', `${this.physics.blocksDestroyed || 0}/${this.activeBuild?.blockCount || 0}`],
            ['[data-destroyer-shots]', `${this.physics.bombsUsed || 0}`]
        ]

        for (const [selector, value] of updates) {
            const node = this.ui.querySelector(selector)
            if (node && node.textContent !== value) {
                node.textContent = value
            }
        }
    }

    renderResult({ won }) {
        const build = this.activeBuild
        this.setMode('result')
        this.scene.removeMesh(READY_BULLET_ID)
        const recorded = Boolean(this.resultAttempt)
        const attempt = recorded && won ? this.resultAttempt : null
        const result = won
            ? {
                won,
                destroyerName: normalizeText(this.destroyerName, t('defaults.destroyer'), 40),
                bombsUsed: this.resultBombsUsed
            }
            : null
        this.shareUrl = attempt?.id ? createResultShareUrl(build, attempt) : createShareUrl(build)
        if (recorded) {
            if (attempt) {
                updateResultShareMeta({ build, attempt, shareUrl: this.shareUrl })
            } else {
                updateBuildShareMeta(build, this.shareUrl)
            }
        }
        const title = won ? 'Flag captured' : 'Build survived'
        const recap = renderDestroyerRecap({
            build,
            won,
            bombsUsed: this.resultBombsUsed,
            durationMs: this.resultDurationMs,
            score: this.resultScore
        })

        this.renderUi(`
            ${renderScreenHomeButton()}
            <main class="result-panel vintage-result ${won ? 'is-won' : 'is-held'}">
                <p class="eyebrow">${won ? 'Dispatch from the front' : 'Report from the walls'}</p>
                <h1>${title}</h1>
                <p>${won ? 'The destroyer broke through the defense.' : 'The builder kept the flag protected.'}</p>
                ${recap}
                ${recorded ? `
                    <div class="result-actions">
                        <button class="primary-button" data-action="restart-build">Try again</button>
                        <button data-action="start-gallery">More builds</button>
                        <button data-action="copy-share">Copy link</button>
                        <a class="button-link" href="${createXShareUrl({ build, shareUrl: this.shareUrl, result, attempt })}" target="_blank" rel="noreferrer">Share on X</a>
                    </div>
                    <input class="share-input" readonly value="${escapeAttribute(this.shareUrl)}">
                ` : `
                    <form class="identity-form" data-form-action="confirm-destroyer-result">
                        ${renderIdentityFields('Destroyer name', this.destroyerName, this.destroyerXHandle)}
                        <div class="result-actions">
                            <button class="primary-button" type="submit">Record result</button>
                            <button type="button" data-action="restart-build">Try again</button>
                            <button type="button" data-action="start-gallery">More builds</button>
                        </div>
                    </form>
                `}
                ${this.status ? `<p class="toast inline">${this.status}</p>` : ''}
            </main>
        `)
    }

    async handleUiClick(event) {
        const target = event.target.closest('[data-action]')
        if (!target) {
            return
        }

        const action = target.dataset.action
        const isAudioAction = Boolean(target.closest('.audio-controller'))

        if (!isAudioAction) {
            this.closeAudioControls()
        }

        if (action === 'set-locale') {
            this.changeLocale(target.dataset.locale)
        } else if (action === 'toggle-audio-muted') {
            this.audio.toggleMuted()
            this.refreshAudioControls()
        } else if (action === 'toggle-audio-panel') {
            this.audioControlsOpen = !this.audioControlsOpen
            this.refreshAudioControls()
        } else if (action === 'close-audio-panel') {
            this.closeAudioControls()
        } else if (action === 'home') {
            this.aboutOpen = false
            this.renderHome()
        } else if (action === 'show-about') {
            this.aboutOpen = true
            this.refreshLocalizedView()
        } else if (action === 'close-about') {
            this.aboutOpen = false
            this.refreshLocalizedView()
        } else if (action === 'start-builder') {
            this.aboutOpen = false
            this.status = ''
            if (!this.builderBlocks.length) {
                this.seedBuilder()
                this.clearBuilderHistory()
            }
            this.renderBuilder()
        } else if (action === 'start-gallery') {
            this.aboutOpen = false
            await this.renderGallery()
        } else if (action === 'show-leaderboards') {
            this.aboutOpen = false
            await this.renderLeaderboards()
        } else if (action === 'set-leaderboard-filter') {
            const nextFilter = LEADERBOARD_FILTERS.includes(target.dataset.filter) ? target.dataset.filter : 'all'
            this.leaderboardFilter = nextFilter
            this.expandedLeaderboardBoards = { builders: false, destroyers: false }
            this.renderUi(renderLeaderboardsShell(
                this.leaderboards,
                this.status,
                this.leaderboardFilter,
                this.expandedLeaderboardBoards
            ))
        } else if (action === 'toggle-leaderboard-board') {
            const board = target.dataset.board
            if (board === 'builders' || board === 'destroyers') {
                this.expandedLeaderboardBoards = {
                    ...this.expandedLeaderboardBoards,
                    [board]: !this.expandedLeaderboardBoards[board]
                }
                this.renderUi(renderLeaderboardsShell(
                    this.leaderboards,
                    this.status,
                    this.leaderboardFilter,
                    this.expandedLeaderboardBoards
                ))
            }
        } else if (action === 'select-material') {
            this.selectedMaterial = target.dataset.material
            this.selectedBlockId = null
            this.selectedFlag = false
            this.status = ''
            this.renderBuilder()
        } else if (action === 'select-shape') {
            this.selectedBlockShape = BLOCK_SHAPES[target.dataset.shape]
                ? target.dataset.shape
                : DEFAULT_BLOCK_SHAPE
            this.selectedBlockId = null
            this.selectedFlag = false
            this.status = ''
            this.renderBuilder()
        } else if (action === 'toggle-builder-profile') {
            this.builderProfileOpen = !this.builderProfileOpen
            this.renderBuilder()
        } else if (action === 'toggle-builder-panel') {
            this.builderPanelCollapsed = !this.builderPanelCollapsed
            this.renderBuilder()
        } else if (action === 'toggle-builder-pan') {
            this.toggleBuilderPanMode()
        } else if (action === 'builder-zoom-in') {
            this.zoomBuilderCamera(BUILDER_CAMERA_ZOOM_BUTTON_FACTOR)
        } else if (action === 'builder-zoom-out') {
            this.zoomBuilderCamera(1 / BUILDER_CAMERA_ZOOM_BUTTON_FACTOR)
        } else if (action === 'builder-reset-view') {
            this.resetBuilderView()
        } else if (action === 'undo-builder') {
            this.undoBuilder()
        } else if (action === 'redo-builder') {
            this.redoBuilder()
        } else if (action === 'rotate-block') {
            this.rotateSelectedBlock({ fine: event.shiftKey })
        } else if (action === 'toggle-builder-physics') {
            this.toggleBuilderPhysics()
        } else if (action === 'delete-block') {
            this.deleteSelectedBlock()
        } else if (action === 'restore-builder-snapshot') {
            this.restoreBuilderSnapshot()
        } else if (action === 'clear-build') {
            this.builderClearConfirmOpen = true
            this.renderBuilder()
        } else if (action === 'cancel-clear-build') {
            this.builderClearConfirmOpen = false
            this.renderBuilder()
        } else if (action === 'confirm-clear-build') {
            this.builderClearConfirmOpen = false
            if (this.builderPhysicsEnabled) {
                this.syncBuilderPhysicsState()
            }
            this.recordBuilderHistory()
            this.builderBlocks = []
            this.selectedBlockId = null
            this.selectedFlag = false
            this.builderRestoreSnapshot = null
            this.builderFlag = { x: WORLD.flagX, y: WORLD.flagY }
            this.resetBuilderPhysics()
            this.status = 'Build cleared.'
            this.renderBuilder()
        } else if (action === 'publish-build') {
            await this.publishCurrentBuild()
        } else if (action === 'cancel-publish-review') {
            this.status = ''
            this.renderBuilder()
        } else if (action === 'set-filter') {
            this.galleryFilter = target.dataset.filter
            await this.renderGallery()
        } else if (action === 'load-build') {
            const build = this.gallery.find((item) => item.id === target.dataset.buildId)
            if (build) {
                this.startDestroyer(build)
            }
        } else if (action === 'select-bullet' || action === 'select-bomb') {
            this.selectBullet(target.dataset.bullet || target.dataset.bomb)
        } else if (action === 'toggle-destroyer-ammo') {
            this.destroyerAmmoOpen = !this.destroyerAmmoOpen
            this.renderDestroyer()
        } else if (action === 'close-tuning-panel') {
            this.tuningPanelOpen = false
            this.draggingTuningPanel = null
            this.renderDestroyer()
        } else if (action === 'open-tuning-panel') {
            this.tuningPanelOpen = true
            this.renderDestroyer()
        } else if (action === 'set-tuning-tab') {
            this.tuningTab = TUNING_TABS.includes(target.dataset.tuningTab) ? target.dataset.tuningTab : 'physics'
            this.renderDestroyer()
        } else if (action === 'set-tuning-bullet') {
            const bulletType = target.dataset.tuningBullet
            if (BOMBS[bulletType]) {
                this.tuningBulletType = bulletType
                this.renderDestroyer()
            }
        } else if (action === 'set-tuning-material') {
            const materialKey = target.dataset.tuningMaterial
            if (MATERIALS[materialKey]) {
                this.tuningMaterialKey = materialKey
                this.renderDestroyer()
            }
        } else if (action === 'reset-tuning') {
            resetRuntimeTuning()
            this.syncAllTunedBodies()
            this.scene.removeMesh(READY_BULLET_ID)
            this.status = 'Tuning reset to defaults.'
            this.renderDestroyer()
        } else if (action === 'copy-tuning-values') {
            await this.copyTuningValues()
        } else if (action === 'restart-build') {
            this.startDestroyer(this.activeBuild)
        } else if (action === 'duplicate-build') {
            this.duplicateActiveBuildToBuilder()
        } else if (action === 'copy-share') {
            await this.copyShareUrl()
        }
    }

    async handleUiSubmit(event) {
        const action = event.target.dataset.formAction

        if (action === 'confirm-publish-build') {
            event.preventDefault()
            await this.confirmPublishBuild(event.target)
        } else if (action === 'confirm-destroyer-result') {
            event.preventDefault()
            await this.recordDestroyerResult(event.target)
        }
    }

    handleUiInput(event) {
        const audioField = event.target.dataset.audioField
        if (audioField === 'musicVolume') {
            this.audio.setMusicVolume(Number(event.target.value) / 100)
            event.target.closest('.audio-slider')?.querySelector('b')?.replaceChildren(event.target.value)
            return
        }

        if (audioField === 'effectsVolume') {
            this.audio.setEffectsVolume(Number(event.target.value) / 100)
            event.target.closest('.audio-slider')?.querySelector('b')?.replaceChildren(event.target.value)
            return
        }

        if (event.target.dataset.tuningScope) {
            this.handleTuningInput(event.target)
            return
        }

        const field = event.target.dataset.field
        if (!field) {
            return
        }

        if (field === 'displayName' || field === 'xHandle') {
            this.profile = {
                ...this.profile,
                [field]: event.target.value
            }
            saveProfile(this.profile)
            this.applyUserIdentity(this.profile)
        } else if (field === 'buildTitle') {
            this.builderTitle = event.target.value
        } else if (field === 'destroyerName') {
            this.destroyerName = event.target.value
            localStorage.setItem('build-destroy-destroyer-name', this.destroyerName)
            this.applyUserIdentity({
                ...this.profile,
                displayName: this.destroyerName,
                xHandle: this.destroyerXHandle
            })
        } else if (field === 'destroyerXHandle') {
            this.destroyerXHandle = event.target.value
            localStorage.setItem('build-destroy-destroyer-x-handle', this.destroyerXHandle)
            this.applyUserIdentity({
                ...this.profile,
                displayName: this.destroyerName,
                xHandle: this.destroyerXHandle
            })
        }
    }

    handleTuningInput(input) {
        const value = Number(input.value)
        if (!Number.isFinite(value)) {
            return
        }

        const scope = input.dataset.tuningScope
        const key = input.dataset.tuningKey
        const path = input.dataset.tuningPath || key

        if ((scope === 'physics' || scope === 'sling') && Object.hasOwn(destroyerTuning, key)) {
            destroyerTuning[key] = value
            if (scope === 'physics') {
                this.syncTunedPhysics()
            } else {
                this.scene.clearSlingGuide()
            }
        } else if (scope === 'damage') {
            setDamageTuningValue(key, value)
        } else if (scope === 'bullet') {
            const bullet = BOMBS[input.dataset.bullet]
            if (bullet) {
                setNestedNumericValue(bullet, path, value)
                this.scene.removeMesh(READY_BULLET_ID)
                this.syncReadyBullet()
            }
        } else if (scope === 'material') {
            const materialKey = input.dataset.material
            const material = MATERIALS[materialKey]
            if (material) {
                setNestedNumericValue(material, path, value)
                this.syncTunedMaterialBodies(materialKey)
            }
        } else if (scope === 'materialMacro') {
            const materialKey = input.dataset.material
            const material = MATERIALS[materialKey]
            const control = getMaterialLinkedControl(key)
            if (material && control) {
                applyMaterialLinkedControl(material, control, value)
                this.syncTunedMaterialBodies(materialKey)
            }
        }

        this.syncVisibleTuningInputs()
        saveRuntimeTuning()
    }

    syncVisibleTuningInputs() {
        const panel = this.ui.querySelector('[data-tuning-panel]')
        if (!panel) {
            return
        }

        for (const input of panel.querySelectorAll('input[data-tuning-scope]')) {
            const value = getCurrentTuningInputValue(input)
            if (!Number.isFinite(value)) {
                continue
            }

            input.value = value
            input.closest('.tuning-slider')?.querySelector('output')?.replaceChildren(formatTuningValue(value))
        }
    }

    syncTunedPhysics() {
        for (const world of [this.physicsWorld, this.builderPhysics?.world]) {
            world?.setGravity(getDestroyerGravity())
            world?.setTimeScale(destroyerTuning.timeScale)
        }

        this.scene.clearSlingGuide()
    }

    syncTunedMaterialBodies(materialKey) {
        if (!this.physics?.blockBodies) {
            return
        }

        const material = MATERIALS[materialKey]
        if (!material) {
            return
        }

        for (const body of this.physics.blockBodies.values()) {
            const game = body.plugin.game
            if (game?.material !== materialKey) {
                continue
            }

            body.setMaterial(getBlockBodyProps(materialKey))

            const previousMax = Number(game.maxHealth) || 0
            const healthRatio = Number.isFinite(game.health) && previousMax > 0
                ? clamp(game.health / previousMax, 0, 1)
                : 1
            const nextMax = getBlockMaxHealth({
                material: materialKey,
                shape: game.shape,
                width: game.width,
                height: game.height
            })

            game.breakable = material.breakable !== false
            game.maxHealth = nextMax
            game.health = game.breakable ? nextMax * healthRatio : Infinity
            body.setAwake(true)
        }
    }

    syncAllTunedBodies() {
        this.syncTunedPhysics()
        for (const materialKey of Object.keys(MATERIALS)) {
            this.syncTunedMaterialBodies(materialKey)
        }
        this.syncReadyBullet()
    }

    async copyTuningValues() {
        const text = JSON.stringify(collectRuntimeTuningSnapshot(), null, 2)
        const copied = await copyTextToClipboard(text)

        this.status = copied
            ? 'Tuning JSON copied.'
            : 'Copy failed. JSON was not opened on screen.'
        this.renderDestroyer()
    }

    changeLocale(locale) {
        const previousLocale = getLocale()
        const previousDefaultTitle = t('defaults.myFortress', {}, previousLocale)

        if (!setLocale(locale)) {
            return
        }

        this.locale = getLocale()

        if (this.builderTitle === previousDefaultTitle || this.builderTitle === 'My Fortress' || this.builderTitle === 'La mia fortezza') {
            this.builderTitle = t('defaults.myFortress')
        }

        this.refreshLocalizedView()
    }

    refreshLocalizedView() {
        this.refreshShareMeta()

        if (this.mode === 'home') {
            this.renderHome(this.status)
        } else if (this.mode === 'builder') {
            this.renderBuilder()
        } else if (this.mode === 'gallery') {
            this.renderUi(renderGalleryShell(this.galleryFilter, this.gallery, this.status, this.aboutOpen))
        } else if (this.mode === 'leaderboards') {
            this.renderUi(renderLeaderboardsShell(
                this.leaderboards,
                this.status,
                this.leaderboardFilter,
                this.expandedLeaderboardBoards
            ))
        } else if (this.mode === 'destroyer') {
            this.renderDestroyer()
        } else if (this.mode === 'result') {
            this.renderResult({ won: this.resultWon })
        } else if (this.mode === 'publish-review') {
            this.renderPublishReview(this.status)
        } else if (this.mode === 'published' && this.activeBuild) {
            this.renderPublishedBuild(this.activeBuild)
        } else {
            this.refreshAudioControls()
        }
    }

    refreshShareMeta() {
        if ((this.mode === 'destroyer' || this.mode === 'published') && this.activeBuild) {
            updateBuildShareMeta(this.activeBuild, this.shareUrl || createShareUrl(this.activeBuild))
            return
        }

        if (this.mode === 'result' && this.activeBuild) {
            if (this.resultAttempt?.won) {
                updateResultShareMeta({ build: this.activeBuild, attempt: this.resultAttempt, shareUrl: this.shareUrl })
            } else {
                updateBuildShareMeta(this.activeBuild, this.shareUrl || createShareUrl(this.activeBuild))
            }
            return
        }

        updateDefaultShareMeta()
    }

    handleUiPointerDown(event) {
        if (this.closeAudioControlsFromOutsidePointer(event)) {
            return
        }

        const tuningDragHandle = event.target.closest('[data-tuning-drag-handle]')
        if (tuningDragHandle && this.startTuningPanelDrag(event, tuningDragHandle.closest('[data-tuning-panel]'))) {
            return
        }

        if (!event.target.closest('[data-launch-zone]')) {
            return
        }

        event.preventDefault()
        this.startBulletDrag(event, { allowAnywhere: true })
    }

    startTuningPanelDrag(event, panel) {
        if (
            !panel
            || event.button > 0
            || event.target.closest('button, input, select, textarea, a')
        ) {
            return false
        }

        const rect = panel.getBoundingClientRect()
        const startPosition = this.clampTuningPanelPosition(rect.left, rect.top, rect.width, rect.height)
        this.tuningPanelPosition = startPosition
        this.draggingTuningPanel = {
            pointerId: event.pointerId,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startLeft: startPosition.left,
            startTop: startPosition.top,
            width: rect.width,
            height: rect.height,
            panel
        }
        this.applyTuningPanelPosition(panel, startPosition)
        panel.classList.add('is-dragging')
        panel.setPointerCapture?.(event.pointerId)
        event.preventDefault()
        return true
    }

    updateTuningPanelDrag(event) {
        const drag = this.draggingTuningPanel
        if (!drag || event.pointerId !== drag.pointerId) {
            return
        }

        const nextPosition = this.clampTuningPanelPosition(
            drag.startLeft + event.clientX - drag.startClientX,
            drag.startTop + event.clientY - drag.startClientY,
            drag.width,
            drag.height
        )
        this.tuningPanelPosition = nextPosition
        this.applyTuningPanelPosition(drag.panel, nextPosition)
        event.preventDefault()
    }

    finishTuningPanelDrag(event) {
        const drag = this.draggingTuningPanel
        if (!drag || event.pointerId !== drag.pointerId) {
            return false
        }

        drag.panel.classList.remove('is-dragging')
        if (drag.panel.hasPointerCapture?.(event.pointerId)) {
            drag.panel.releasePointerCapture(event.pointerId)
        }
        this.draggingTuningPanel = null
        return true
    }

    clampTuningPanelPosition(left, top, width, height) {
        const margin = 8
        const maxLeft = Math.max(margin, window.innerWidth - width - margin)
        const maxTop = Math.max(margin, window.innerHeight - height - margin)

        return {
            left: Math.round(clamp(left, margin, maxLeft)),
            top: Math.round(clamp(top, margin, maxTop))
        }
    }

    applyTuningPanelPosition(panel, position) {
        panel.style.left = `${position.left}px`
        panel.style.top = `${position.top}px`
        panel.style.right = 'auto'
        panel.style.bottom = 'auto'
    }

    handleKeyDown(event) {
        if (event.key === 'Escape' && this.audioControlsOpen) {
            event.preventDefault()
            this.closeAudioControls()
            return
        }

        if (this.mode === 'builder' && !isTypingTarget(event.target) && (event.metaKey || event.ctrlKey) && !event.altKey) {
            const historyKey = event.key.toLowerCase()

            if (historyKey === 'z' || historyKey === 'y') {
                event.preventDefault()
                if (historyKey === 'y' || event.shiftKey) {
                    this.redoBuilder()
                } else {
                    this.undoBuilder()
                }
                return
            }
        }

        if (this.mode !== 'builder' || isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) {
            return
        }

        if (this.builderPublishValidation) {
            event.preventDefault()
            return
        }

        const key = event.key

        if (key === 'Tab' || key === 'Enter' || key.startsWith('Arrow')) {
            this.builderHover = null
        }

        if (key === 'Tab') {
            event.preventDefault()
            this.selectBuilderBlockFromKeyboard(event.shiftKey ? -1 : 1)
            return
        }

        if (key === 'Enter') {
            event.preventDefault()
            this.placeBuilderKeyboardTarget()
            return
        }

        if (key.startsWith('Arrow')) {
            const direction = {
                ArrowLeft: { x: -BUILDER_KEYBOARD_STEP, y: 0 },
                ArrowRight: { x: BUILDER_KEYBOARD_STEP, y: 0 },
                ArrowUp: { x: 0, y: BUILDER_KEYBOARD_STEP },
                ArrowDown: { x: 0, y: -BUILDER_KEYBOARD_STEP }
            }[key]

            if (direction) {
                event.preventDefault()
                this.moveBuilderKeyboardTarget(direction.x, direction.y)
            }
            return
        }

        const shortcut = key.toLowerCase()

        if (shortcut === 'r') {
            event.preventDefault()
            this.rotateBuilderKeyboardTarget({ fine: event.shiftKey })
        } else if (shortcut === 'p') {
            event.preventDefault()
            this.builderKeyboardActive = true
            this.toggleBuilderPhysics()
        } else if (BUILDER_MATERIAL_SHORTCUTS[shortcut]) {
            event.preventDefault()
            this.selectBuilderMaterialFromKeyboard(BUILDER_MATERIAL_SHORTCUTS[shortcut])
        } else if (/^[1-3]$/.test(shortcut)) {
            event.preventDefault()
            this.selectBuilderShapeFromKeyboard(Number(shortcut) - 1)
        }
    }

    handlePointerDown(event) {
        if (this.closeAudioControlsFromOutsidePointer(event)) {
            return
        }

        if (this.mode === 'builder') {
            this.handleBuilderPointerDown(event)
        } else if (this.mode === 'destroyer') {
            this.handleDestroyerPointerDown(event)
        }
    }

    handlePointerMove(event) {
        if (this.draggingTuningPanel) {
            this.updateTuningPanelDrag(event)
            return
        }

        if (this.builderDrag) {
            this.updateBuilderDrag(event)
            return
        }

        if (this.draggingLaunch) {
            this.updateBulletDrag(event)
            return
        }

        if (this.cameraPan) {
            this.updateCameraPan(event)
            return
        }

        if (this.mode === 'destroyer') {
            this.updateDestroyerHover(event)
        } else if (this.mode === 'builder') {
            this.updateBuilderHover(event)
        }
    }

    handlePointerUp(event) {
        if (this.draggingTuningPanel && this.finishTuningPanelDrag(event)) {
            return
        }

        if (this.builderDrag) {
            this.finishBuilderDrag(event)
            return
        }

        if (this.draggingLaunch) {
            this.finishBulletDrag(event)
            return
        }

        if (this.cameraPan) {
            this.finishCameraPan(event)
        }
    }

    handleCanvasWheel(event) {
        if (this.mode !== 'builder') {
            return
        }

        event.preventDefault()
        const deltaUnit = event.deltaMode === 1
            ? 16
            : event.deltaMode === 2 ? window.innerHeight : 1
        const delta = event.deltaY * deltaUnit
        const zoomFactor = Math.exp(-delta * CAMERA_WHEEL_ZOOM_SPEED)
        this.zoomBuilderCamera(zoomFactor, { clientX: event.clientX, clientY: event.clientY })
    }

    toggleBuilderPanMode() {
        if (this.mode !== 'builder') {
            return
        }

        this.builderPanMode = !this.builderPanMode
        this.status = this.builderPanMode ? 'Pan mode enabled.' : 'Pan mode disabled.'
        this.updateBuilderCanvasCursor()
        this.renderBuilder()
    }

    zoomBuilderCamera(factor, anchor = null) {
        if (this.mode !== 'builder' || !Number.isFinite(factor) || factor <= 0) {
            return
        }

        const rect = this.canvas.getBoundingClientRect()
        const zoomAnchor = anchor || {
            clientX: rect.left + rect.width / 2,
            clientY: rect.top + rect.height / 2
        }

        this.cameraPanOverride = true
        this.scene.zoomCameraAt(
            zoomAnchor.clientX,
            zoomAnchor.clientY,
            factor,
            getCameraPanBounds('builder'),
            {
                minZoom: BUILDER_CAMERA_MIN_ZOOM,
                maxZoom: BUILDER_CAMERA_MAX_ZOOM
            }
        )
    }

    resetBuilderView() {
        if (this.mode !== 'builder') {
            return
        }

        this.cameraPanOverride = false
        this.scene.frameBuildArea({ minZoom: 1.2, maxZoom: 2.35 })
        this.status = 'View reset.'
        this.renderBuilder()
    }

    updateBuilderCanvasCursor() {
        if (this.mode !== 'builder') {
            return
        }

        if (this.cameraPan) {
            this.canvas.style.cursor = 'grabbing'
        } else if (this.builderPanMode) {
            this.canvas.style.cursor = 'grab'
        } else {
            this.canvas.style.cursor = ''
        }
    }

    handleDestroyerPointerDown(event) {
        const splittable = [...(this.physics?.bombs?.values() || [])].find((body) => canBulletSplit(body))
        if (splittable) {
            splittable.plugin.game.splitRequested = true
            return
        }

        this.startBulletDrag(event)
    }

    updateDestroyerHover(event) {
        if (!this.physics || this.physics.ended || this.physics.bombs.size > 0 || countInventory(this.physics.inventory) <= 0) {
            this.canvas.style.cursor = ''
            return
        }

        const type = firstAvailableBomb(this.physics.inventory, this.physics.currentBombType)
        const point = this.scene.screenToWorld(event.clientX, event.clientY)
        this.canvas.style.cursor = type && isPointOnReadyBullet(point, type) ? 'grab' : ''
    }

    handleBuilderPointerDown(event) {
        if (this.builderPublishValidation) {
            event.preventDefault()
            return
        }

        this.clearBuilderHover()

        if (this.builderPanMode || event.button === 1 || event.shiftKey) {
            event.preventDefault()
            this.startCameraPan(event)
            return
        }

        const point = this.scene.screenToWorld(event.clientX, event.clientY)
        const flagHit = isPointOnFlag(this.builderFlag, point)

        if (flagHit) {
            this.selectedBlockId = null
            this.selectedFlag = true
            this.startBuilderDrag(event, 'flag', null, point)
            this.status = 'Flag selected.'
            this.renderBuilder()
            return
        }

        const existingBlock = findBlockAtPoint(this.builderBlocks, point)

        if (existingBlock) {
            this.selectedBlockId = existingBlock.id
            this.selectedBlockShape = BLOCK_SHAPES[existingBlock.shape]
                ? existingBlock.shape
                : inferBlockShape(existingBlock)
            this.selectedFlag = false
            this.startBuilderDrag(event, 'block', existingBlock.id, point)
            this.status = `${MATERIALS[existingBlock.material].label} selected.`
            this.renderBuilder()
            return
        }

        if (!isInsideBuildZone(point)) {
            this.selectedBlockId = null
            this.selectedFlag = false
            this.startCameraPan(event)
            return
        }

        this.startCameraPan(event, { pendingBuilderPoint: point })
    }

    // Where a block of the selected kind ends up for a given point. Shared by
    // placement and the hover ghost so the preview matches the click. Pointer
    // placement settles on the surface below; the keyboard cursor stays put.
    getBuilderPlacementBlock(point) {
        const shape = getBlockShape(this.selectedBlockShape)
        const rotation = snapAngle(point.rotation ?? this.builderKeyboardRotation ?? 0)
        const block = {
            material: this.selectedMaterial,
            shape: this.selectedBlockShape,
            ...snapBlockPosition({ ...point, width: shape.width, height: shape.height }, rotation),
            width: shape.width,
            height: shape.height,
            rotation
        }

        return point.surfaceSnap === false
            ? block
            : { ...block, ...dropBlockOnSurface(block, this.builderBlocks) }
    }

    getBuilderGhost() {
        if (
            !this.builderHover
            || this.mode !== 'builder'
            || this.builderPhysicsEnabled
            || this.builderDrag
            || this.cameraPan
            || this.builderPanMode
            || this.builderPublishValidation
        ) {
            return null
        }

        const point = this.scene.screenToWorld(this.builderHover.clientX, this.builderHover.clientY)

        if (
            !isInsideBuildZone(point)
            || isPointOnFlag(this.builderFlag, point)
            || findBlockAtPoint(this.builderBlocks, point)
        ) {
            return null
        }

        const block = this.getBuilderPlacementBlock(point)
        const valid = isBlockPlacementValid(block, this.builderBlocks)
            && calculateBudgetUsed([...this.builderBlocks, block]) <= BUILDER_BUDGET

        return { block, valid }
    }

    updateBuilderHover(event) {
        if (event.target !== this.canvas || event.pointerType === 'touch') {
            this.clearBuilderHover()
            return
        }

        this.builderHover = { clientX: event.clientX, clientY: event.clientY }
        this.refreshBuilderGhost()
    }

    clearBuilderHover() {
        if (!this.builderHover && !this.builderGhost) {
            return
        }

        this.builderHover = null
        this.refreshBuilderGhost()
    }

    refreshBuilderGhost() {
        const wasShown = Boolean(this.builderGhost)
        this.builderGhost = this.getBuilderGhost()

        if (wasShown !== Boolean(this.builderGhost)) {
            // The keyboard preview is hidden while the ghost is up.
            this.scene.showBuilder(this.getBuilderSceneBlocks(), this.getBuilderSceneSelectedId(), this.builderFlag, this.selectedFlag, {
                frameCamera: false
            })
        }

        this.scene.setBuilderGhost(this.builderGhost?.block, this.builderGhost?.valid)
    }

    placeBuilderBlock(point) {
        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }

        const nextBlock = {
            id: createId('block'),
            ...this.getBuilderPlacementBlock(point)
        }
        const nextBudget = calculateBudgetUsed([...this.builderBlocks, nextBlock])

        if (nextBudget > BUILDER_BUDGET) {
            this.status = 'That piece would exceed the budget.'
            this.renderBuilder()
            return
        }

        if (!isBlockPlacementValid(nextBlock, this.builderBlocks)) {
            this.status = 'That grid slot is already occupied.'
            this.renderBuilder()
            return
        }

        this.recordBuilderHistory()
        this.builderBlocks.push(nextBlock)
        this.selectedBlockId = point.selectPlaced === false ? null : nextBlock.id
        this.selectedFlag = false
        if (point.selectPlaced === false) {
            this.advanceBuilderKeyboardCursor(nextBlock)
        }
        if (this.builderPhysicsEnabled) {
            this.resetBuilderPhysics()
        }
        this.status = `${MATERIALS[this.selectedMaterial].label} placed.`
        this.renderBuilder()
    }

    startCameraPan(event, options = {}) {
        if (this.mode !== 'builder' && this.mode !== 'destroyer') {
            return
        }

        this.cameraPan = {
            pointerId: event.pointerId,
            mode: this.mode,
            moved: false,
            startClientX: event.clientX,
            startClientY: event.clientY,
            lastClientX: event.clientX,
            lastClientY: event.clientY,
            pendingBuilderPoint: options.pendingBuilderPoint || null,
            captureTarget: getPointerCaptureTarget(event, this.canvas)
        }
        this.cameraPan.captureTarget.setPointerCapture?.(event.pointerId)
        this.updateBuilderCanvasCursor()
    }

    updateCameraPan(event) {
        if (!this.cameraPan || event.pointerId !== this.cameraPan.pointerId) {
            return
        }

        const totalDistance = Math.hypot(
            event.clientX - this.cameraPan.startClientX,
            event.clientY - this.cameraPan.startClientY
        )

        if (!this.cameraPan.moved && totalDistance < CAMERA_PAN_START_PIXELS) {
            return
        }

        const deltaX = event.clientX - this.cameraPan.lastClientX
        const deltaY = event.clientY - this.cameraPan.lastClientY
        this.cameraPan.moved = true
        this.cameraPanOverride = true
        this.cameraPan.lastClientX = event.clientX
        this.cameraPan.lastClientY = event.clientY
        this.scene.panCameraByPixels(deltaX, deltaY, getCameraPanBounds(this.cameraPan.mode))
        event.preventDefault()
    }

    finishCameraPan(event) {
        if (!this.cameraPan || event.pointerId !== this.cameraPan.pointerId) {
            return
        }

        const pan = this.cameraPan
        if (pan.captureTarget?.hasPointerCapture?.(event.pointerId)) {
            pan.captureTarget.releasePointerCapture(event.pointerId)
        }
        this.cameraPan = null
        this.updateBuilderCanvasCursor()

        if (pan.moved) {
            return
        }

        if (event.type === 'pointercancel') {
            return
        }

        if (pan.pendingBuilderPoint) {
            this.placeBuilderBlock(pan.pendingBuilderPoint)
            return
        }

        if (pan.mode === 'builder') {
            this.status = 'Place blocks inside the highlighted build zone.'
            this.renderBuilder()
        }
    }

    seedBuilder() {
        this.builderFlag = { x: WORLD.flagX, y: WORLD.flagY }
        this.builderBlocks = [
            makeBuilderBlock('wood', 'beam', 9.75, -5.7125),
            makeBuilderBlock('stone', 'square', 8.65, -5.025),
            makeBuilderBlock('stone', 'square', 10.85, -5.025),
            makeBuilderBlock('glass', 'rectangle', 9.75, -4.2),
            makeBuilderBlock('wood', 'square', 9.75, -3.375)
        ]
        if (this.builderPhysicsEnabled) {
            this.resetBuilderPhysics()
        }
    }

    rotateSelectedBlock({ fine = false } = {}) {
        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }
        const block = this.builderBlocks.find((item) => item.id === this.selectedBlockId)
        if (!block) {
            return
        }

        const step = fine ? ROTATION_STEP : ROTATION_STEP_COARSE
        const nextRotation = snapAngle((block.rotation || 0) + step)
        const nextPosition = snapBlockPosition(block, nextRotation, this.builderBlocks, block.id)
        const nextBlock = {
            ...block,
            ...nextPosition,
            rotation: nextRotation
        }

        if (!isBlockPlacementValid(nextBlock, this.builderBlocks, block.id)) {
            this.status = 'Rotation would overlap another block.'
            this.renderBuilder()
            return
        }

        this.recordBuilderHistory()
        Object.assign(block, nextBlock)
        if (this.builderPhysicsEnabled) {
            this.resetBuilderPhysics()
        }
        this.status = fine ? 'Block rotated 45 degrees.' : 'Block rotated 90 degrees.'
        this.renderBuilder()
    }

    getBuilderSceneBlocks() {
        if (!this.shouldShowBuilderKeyboardPreview()) {
            return this.builderBlocks
        }

        return [...this.builderBlocks, this.getBuilderKeyboardPreviewBlock()]
    }

    getBuilderSceneSelectedId() {
        if (this.selectedBlockId) {
            return this.selectedBlockId
        }

        return this.shouldShowBuilderKeyboardPreview()
            ? BUILDER_KEYBOARD_PREVIEW_ID
            : null
    }

    shouldShowBuilderKeyboardPreview() {
        return this.mode === 'builder'
            && !this.builderGhost
            && !this.selectedBlockId
            && !this.selectedFlag
            && (this.builderKeyboardActive || hasKeyboardControlSurface())
    }

    getBuilderKeyboardPreviewBlock() {
        const shape = getBlockShape(this.selectedBlockShape)
        this.ensureBuilderKeyboardCursor(shape)

        return {
            id: BUILDER_KEYBOARD_PREVIEW_ID,
            material: this.selectedMaterial,
            shape: this.selectedBlockShape,
            x: this.builderKeyboardCursor.x,
            y: this.builderKeyboardCursor.y,
            width: shape.width,
            height: shape.height,
            rotation: this.builderKeyboardRotation,
            preview: true
        }
    }

    ensureBuilderKeyboardCursor(shape = getBlockShape(this.selectedBlockShape)) {
        const rotation = snapAngle(this.builderKeyboardRotation || 0)

        if (!this.builderKeyboardCursor) {
            const floorPosition = {
                x: WORLD.flagX,
                y: WORLD.buildMinY + getBlockFootprint({
                    width: shape.width,
                    height: shape.height,
                    rotation
                }).height / 2
            }
            const snapped = snapBlockPosition({ ...floorPosition, width: shape.width, height: shape.height }, rotation, this.builderBlocks)
            const candidate = {
                material: this.selectedMaterial,
                shape: this.selectedBlockShape,
                ...snapped,
                width: shape.width,
                height: shape.height,
                rotation
            }
            const openPosition = findOpenBlockPosition(candidate, this.builderBlocks)
            this.builderKeyboardCursor = openPosition || snapped
        } else {
            this.builderKeyboardCursor = snapBlockPosition({
                ...this.builderKeyboardCursor,
                width: shape.width,
                height: shape.height
            }, rotation, this.builderBlocks)
        }

        this.builderKeyboardRotation = rotation
    }

    advanceBuilderKeyboardCursor(fromBlock) {
        const shape = getBlockShape(this.selectedBlockShape)
        const nextPosition = snapBlockPosition({
            x: fromBlock.x + BUILDER_KEYBOARD_STEP,
            y: fromBlock.y,
            width: shape.width,
            height: shape.height
        }, this.builderKeyboardRotation, this.builderBlocks)
        const candidate = {
            material: this.selectedMaterial,
            shape: this.selectedBlockShape,
            ...nextPosition,
            width: shape.width,
            height: shape.height,
            rotation: this.builderKeyboardRotation
        }

        this.builderKeyboardCursor = findOpenBlockPosition(candidate, this.builderBlocks) || nextPosition
    }

    moveBuilderKeyboardTarget(deltaX, deltaY) {
        this.builderKeyboardActive = true

        if (this.selectedFlag) {
            this.moveSelectedFlagBy(deltaX, deltaY)
            return
        }

        if (this.selectedBlockId) {
            this.moveSelectedBlockBy(deltaX, deltaY)
            return
        }

        const preview = this.getBuilderKeyboardPreviewBlock()
        const nextPosition = snapBlockPosition({
            ...preview,
            x: preview.x + deltaX,
            y: preview.y + deltaY
        }, preview.rotation || 0, this.builderBlocks)

        this.builderKeyboardCursor = nextPosition
        this.status = isBlockPlacementValid({ ...preview, ...nextPosition }, this.builderBlocks)
            ? 'Press Enter to place the block.'
            : 'That grid slot is already occupied.'
        this.renderBuilder()
    }

    moveSelectedBlockBy(deltaX, deltaY) {
        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }

        const block = this.builderBlocks.find((item) => item.id === this.selectedBlockId)
        if (!block) {
            return
        }

        const target = snapBlockPosition({
            ...block,
            x: block.x + deltaX,
            y: block.y + deltaY
        }, block.rotation || 0, this.builderBlocks, block.id)
        const candidate = { ...block, ...target }

        if (!isBlockPlacementValid(candidate, this.builderBlocks, block.id)) {
            this.status = 'That move would overlap another block.'
            this.renderBuilder()
            return
        }

        const moved = Math.hypot(block.x - target.x, block.y - target.y) > 0.001
        if (moved) {
            this.recordBuilderHistory()
        }
        block.x = target.x
        block.y = target.y

        if (this.builderPhysicsEnabled) {
            this.resetBuilderPhysics()
        }

        this.status = moved ? 'Block moved.' : 'Build boundary reached.'
        this.renderBuilder()
    }

    moveSelectedFlagBy(deltaX, deltaY) {
        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }

        const target = clampFlagPosition({
            x: this.builderFlag.x + deltaX,
            y: this.builderFlag.y + deltaY
        })
        const moved = Math.hypot(this.builderFlag.x - target.x, this.builderFlag.y - target.y) > 0.001
        if (moved) {
            this.recordBuilderHistory()
        }
        this.builderFlag = target

        if (this.builderPhysicsEnabled) {
            this.resetBuilderPhysics()
        }

        this.status = moved ? 'Flag moved.' : 'Build boundary reached.'
        this.renderBuilder()
    }

    rotateBuilderKeyboardTarget({ fine = false } = {}) {
        this.builderKeyboardActive = true

        if (this.selectedFlag) {
            this.status = 'The flag does not rotate.'
            this.renderBuilder()
            return
        }

        if (this.selectedBlockId) {
            this.rotateSelectedBlock({ fine })
            return
        }

        const preview = this.getBuilderKeyboardPreviewBlock()
        const nextRotation = snapAngle((preview.rotation || 0) + (fine ? ROTATION_STEP : ROTATION_STEP_COARSE))
        const nextPosition = snapBlockPosition(preview, nextRotation, this.builderBlocks)

        this.builderKeyboardRotation = nextRotation
        this.builderKeyboardCursor = nextPosition
        this.status = fine ? 'Placement cursor rotated 45 degrees.' : 'Placement cursor rotated 90 degrees.'
        this.renderBuilder()
    }

    placeBuilderKeyboardTarget() {
        this.builderKeyboardActive = true

        if (this.selectedFlag) {
            this.selectedFlag = false
            this.status = 'Flag positioned.'
            this.renderBuilder()
            return
        }

        if (this.selectedBlockId) {
            const block = this.builderBlocks.find((item) => item.id === this.selectedBlockId)
            if (block) {
                this.builderKeyboardRotation = block.rotation || 0
                this.advanceBuilderKeyboardCursor(block)
            }
            this.selectedBlockId = null
            this.status = 'Block positioned.'
            this.renderBuilder()
            return
        }

        const preview = this.getBuilderKeyboardPreviewBlock()
        this.placeBuilderBlock({
            ...preview,
            surfaceSnap: false,
            selectPlaced: false
        })
    }

    selectBuilderBlockFromKeyboard(direction = 1) {
        this.builderKeyboardActive = true

        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }

        if (!this.builderBlocks.length) {
            this.selectedBlockId = null
            this.selectedFlag = false
            this.status = 'No blocks on the table yet.'
            this.renderBuilder()
            return
        }

        const currentIndex = this.builderBlocks.findIndex((block) => block.id === this.selectedBlockId)
        const nextIndex = currentIndex === -1
            ? direction > 0 ? 0 : this.builderBlocks.length - 1
            : (currentIndex + direction + this.builderBlocks.length) % this.builderBlocks.length
        const block = this.builderBlocks[nextIndex]

        this.selectedBlockId = block.id
        this.selectedBlockShape = BLOCK_SHAPES[block.shape]
            ? block.shape
            : inferBlockShape(block)
        this.selectedFlag = false
        this.builderKeyboardCursor = { x: block.x, y: block.y }
        this.builderKeyboardRotation = block.rotation || 0
        this.status = `${MATERIALS[block.material].label} selected.`
        this.renderBuilder()
    }

    selectBuilderMaterialFromKeyboard(material) {
        if (!MATERIALS[material]) {
            return
        }

        this.builderKeyboardActive = true
        this.selectedMaterial = material
        this.selectedBlockId = null
        this.selectedFlag = false
        this.ensureBuilderKeyboardCursor()
        this.status = `${MATERIALS[material].label} selected.`
        this.renderBuilder()
    }

    selectBuilderShapeFromKeyboard(index) {
        const shapeKey = BUILDER_SHAPE_SHORTCUTS[index]

        if (!shapeKey || !BLOCK_SHAPES[shapeKey]) {
            return
        }

        this.builderKeyboardActive = true
        this.selectedBlockShape = shapeKey
        this.selectedBlockId = null
        this.selectedFlag = false
        this.ensureBuilderKeyboardCursor()
        this.status = `${BLOCK_SHAPES[shapeKey].label} selected.`
        this.renderBuilder()
    }

    toggleBuilderPhysics() {
        if (this.builderPublishValidation) {
            return
        }

        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState({ snapNearGrid: true })
            this.builderPhysicsEnabled = false
            this.destroyBuilderPhysics()
            this.status = 'Physics off. Static stacking mode enabled.'
        } else {
            this.builderPhysicsEnabled = true
            this.resetBuilderPhysics()
            this.status = 'Physics on. Blocks behave exactly as they will under attack.'
        }

        this.renderBuilder()
    }

    deleteSelectedBlock() {
        if (!this.selectedBlockId || this.builderPublishValidation) {
            return
        }

        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }
        this.recordBuilderHistory()
        this.builderBlocks = this.builderBlocks.filter((block) => block.id !== this.selectedBlockId)
        this.selectedBlockId = null
        if (this.builderPhysicsEnabled) {
            this.resetBuilderPhysics()
        }
        this.status = 'Block deleted.'
        this.renderBuilder()
    }

    restoreBuilderSnapshot() {
        const snapshot = this.builderRestoreSnapshot
        if (!snapshot) {
            return
        }

        this.builderPublishValidation = null
        this.builderPhysicsEnabled = false
        this.destroyBuilderPhysics()
        this.builderBlocks = cloneBuilderBlocks(snapshot.blocks)
        this.builderFlag = { ...snapshot.flag }
        this.selectedBlockId = snapshot.selectedBlockId
        this.selectedFlag = snapshot.selectedFlag
        this.builderRestoreSnapshot = null
        this.status = 'Restored the layout from before the physics test.'
        this.renderBuilder()
    }

    async publishCurrentBuild() {
        if (this.builderPublishValidation) {
            return
        }

        const layoutState = this.prepareBuilderLayoutForPublish()
        if (!layoutState.ok) {
            this.status = layoutState.reason
            this.renderBuilder()
            return
        }

        const stabilityState = await this.verifyBuilderStabilityBeforePublish()
        if (stabilityState.canceled) {
            return
        }

        if (!stabilityState.ok) {
            this.status = stabilityState.reason
            this.renderBuilder()
            return
        }

        this.renderPublishReview(layoutState.adjusted ? 'Adjusted blocks to fit the build grid.' : '')
    }

    async verifyBuilderStabilityBeforePublish() {
        const snapshot = createBuilderLayoutSnapshot({
            blocks: this.builderBlocks,
            flag: this.builderFlag,
            selectedBlockId: this.selectedBlockId,
            selectedFlag: this.selectedFlag
        })
        const validation = {
            id: createId('builder-physics-test'),
            snapshot
        }

        this.builderRestoreSnapshot = snapshot
        this.builderPublishValidation = validation
        this.builderPhysicsEnabled = true
        this.resetBuilderPhysics()
        this.status = 'Testing fortress stability with physics...'
        this.renderBuilder()

        await waitForAnimationTime(BUILDER_PUBLISH_PHYSICS_TEST_MS)

        if (this.builderPublishValidation !== validation || this.mode !== 'builder') {
            return { ok: false, canceled: true }
        }

        this.syncBuilderPhysicsState()
        const stability = analyzeBuilderPublishStability({
            physics: this.builderPhysics,
            snapshot,
            blocks: this.builderBlocks
        })
        this.builderPublishValidation = null

        if (!stability.ok) {
            return {
                ok: false,
                reason: stability.reason === 'unstable'
                    ? 'Some blocks slid or toppled during the physics test. Restore the saved layout or give them better support.'
                    : 'The flag or blocks took too much damage during the physics test. Restore the saved layout or reinforce the fortress.'
            }
        }

        this.builderRestoreSnapshot = null
        this.status = ''
        return { ok: true }
    }

    prepareBuilderLayoutForPublish() {
        this.syncBuilderPhysicsState()
        const repair = repairBuildLayout(this.builderBlocks)
        if (!repair.ok) {
            return { ok: false, reason: 'Fix overlapping or out-of-bounds blocks before publishing.' }
        }

        if (repair.changed) {
            this.builderBlocks = repair.blocks
            if (this.builderPhysicsEnabled) {
                this.resetBuilderPhysics()
            }
        }

        const publishState = canPublishBuild(this.builderBlocks)
        if (!publishState.ok) {
            return publishState
        }

        return { ok: true, adjusted: repair.changed }
    }

    renderPublishReview(message = '') {
        this.setMode('publish-review')
        this.status = message
        const summary = getBuildSummary({
            title: this.builderTitle,
            blocks: this.builderBlocks,
            bombsGranted: calculateBombsGranted(this.builderBlocks)
        })

        this.renderUi(`
            <main class="modal-layer">
                <section class="result-panel publish-panel" aria-labelledby="publish-review-title">
                    <p class="eyebrow">Build recap</p>
                    <h1 id="publish-review-title">Ready to publish?</h1>
                    ${renderBuildRecap(summary)}
                    <form class="identity-form" data-form-action="confirm-publish-build">
                        <label>
                            <span>Build title</span>
                            <input name="buildTitle" maxlength="64" value="${escapeAttribute(this.builderTitle)}" required>
                        </label>
                        ${renderIdentityFields('Builder name', this.profile.displayName, this.profile.xHandle)}
                        <div class="result-actions">
                            <button type="button" data-action="cancel-publish-review">Keep building</button>
                            <button class="primary-button" type="submit">Publish build</button>
                        </div>
                    </form>
                    ${this.status ? `<p class="toast inline">${this.status}</p>` : ''}
                </section>
            </main>
        `)
    }

    async confirmPublishBuild(form) {
        const formData = new FormData(form)
        const displayName = normalizeText(formData.get('displayName'), '', 40)
        const xHandle = String(formData.get('xHandle') || '').trim()
        this.builderTitle = normalizeText(formData.get('buildTitle'), t('defaults.myFortress'), 64)
        this.profile = {
            ...this.profile,
            displayName,
            xHandle
        }

        if (!displayName) {
            this.renderPublishReview('Add a builder name to publish.')
            return
        }

        const layoutState = this.prepareBuilderLayoutForPublish()
        if (!layoutState.ok) {
            this.renderPublishReview(layoutState.reason)
            return
        }

        this.applyUserIdentity({ displayName, xHandle })
        this.status = layoutState.adjusted
            ? 'Adjusted blocks to fit the build grid. Publishing build...'
            : 'Publishing build...'
        this.renderPublishReview(this.status)

        try {
            this.scene.showBuilder(this.builderBlocks, null, this.builderFlag, false)
            const ogImageBlob = await createOpenGraphImage(() => createBuildOpenGraphImage({
                scene: this.scene
            }))
            const previewImageBlob = await createOpenGraphImage(() => createBuildPreviewImage({
                scene: this.scene
            }))
            const build = await publishBuild({
                profile: this.profile,
                title: this.builderTitle,
                blocks: this.builderBlocks,
                flag: this.builderFlag,
                ogImageBlob,
                previewImageBlob
            })
            this.activeBuild = build
            this.shareUrl = createShareUrl(build)
            updateBuildShareMeta(build, this.shareUrl)
            this.status = isBackendConfigured()
                ? build.ogImageUrl ? 'Published to Neon with share image.' : 'Published to Neon. Share image unavailable.'
                : 'Saved locally. Add backend env vars for global sharing.'
            this.setMode('published')
            this.destroyBuilderPhysics()
            this.renderPublishedBuild(build)
        } catch (error) {
            this.status = `Publish failed: ${error.message}`
            this.renderPublishReview(this.status)
        }
    }

    renderPublishedBuild(build) {
        this.setMode('published')
        const summary = getBuildSummary(build)

        this.renderUi(`
            ${renderScreenHomeButton()}
            <main class="result-panel publish-panel">
                <p class="eyebrow">Build published</p>
                <h1>${escapeHtml(build.title)}</h1>
                <p>Challenge link ready.</p>
                ${renderBuildRecap(summary)}
                <input class="share-input" readonly value="${escapeAttribute(this.shareUrl)}">
                <div class="result-actions">
                    <button class="primary-button" data-action="copy-share">Copy link</button>
                    <button data-action="restart-build">Test destroy</button>
                    <button data-action="start-gallery">Browse builds</button>
                    <a class="button-link" href="${createXShareUrl({ build, shareUrl: this.shareUrl })}" target="_blank" rel="noreferrer">Share on X</a>
                </div>
                ${this.status ? `<p class="toast inline">${this.status}</p>` : ''}
            </main>
        `)
    }

    startDestroyer(build) {
        this.clearResultTimer()
        this.builderPublishValidation = null
        this.setMode('destroyer')
        this.cameraPan = null
        this.cameraPanOverride = false
        this.builderPanMode = false
        this.destroyBuilderPhysics()
        this.activeBuild = normalizeBuildRow(build)
        this.resultAttempt = null
        this.resultBombsUsed = 0
        this.resultDurationMs = 0
        this.resultScore = 0
        this.destroyerAmmoOpen = false
        this.status = ''
        void this.audio.resume()
        this.setupPhysics(this.activeBuild)
        this.renderDestroyer()
    }

    duplicateActiveBuildToBuilder() {
        if (!this.activeBuild) {
            this.status = 'Choose a fortress to duplicate.'
            this.renderHome()
            return
        }

        const sourceBuild = normalizeBuildRow(this.activeBuild)
        const sourceTitle = normalizeText(sourceBuild.title, t('defaults.myFortress'), 64)
        const duplicateTitle = `${sourceTitle.slice(0, Math.max(0, 64 - ' Copy'.length))} Copy`
        const flagX = Number(sourceBuild.flag?.x)
        const flagY = Number(sourceBuild.flag?.y)

        this.clearResultTimer()
        this.builderPublishValidation = null
        this.builderRestoreSnapshot = null
        this.builderPhysicsEnabled = false
        this.builderClearConfirmOpen = false
        this.builderPanelCollapsed = false
        this.builderPanMode = false
        this.cameraPan = null
        this.cameraPanOverride = false
        this.destroyerAmmoOpen = false
        this.selectedBlockId = null
        this.selectedFlag = false
        this.builderKeyboardCursor = null
        this.builderKeyboardRotation = 0
        this.builderTitle = duplicateTitle
        this.builderBlocks = cloneBuildBlocksForBuilder(sourceBuild.blocks)
        this.builderFlag = {
            x: Number.isFinite(flagX) ? flagX : WORLD.flagX,
            y: Number.isFinite(flagY) ? flagY : WORLD.flagY
        }
        this.activeBuild = null
        this.clearBuilderHistory()
        this.status = `Duplicated ${sourceTitle}.`
        this.renderBuilder()
    }

    setupPhysics(build) {
        this.destroyPhysics()
        this.scene.setupDestroyScene()

        this.physicsWorld = createStageWorld({
            gravity: getDestroyerGravity(),
            timeScale: destroyerTuning.timeScale,
            onContact: (contact) => this.handleCollision(contact)
        })
        this.physics = {
            blockBodies: new Map(),
            flag: null,
            bombs: new Map(),
            inventory: { ...build.bombsGranted },
            currentBombType: firstAvailableBomb(build.bombsGranted),
            bombsUsed: 0,
            startedAt: this.now(),
            score: 0,
            blocksDestroyed: 0,
            finalBonusApplied: false,
            ended: false,
            structureDisturbed: false,
            settleTimer: 0,
            cameraLaunchedAt: null,
            shotUiHidden: false,
            shotUiRevealAt: 0,
            collisionDamageTimes: new Map(),
            impactStats: {
                directDamage: 0,
                propagatedDamage: 0,
                fallDamage: 0,
                stressDamage: 0,
                collapseCount: 0,
                lastHit: ''
            }
        }

        addDestroyerBounds(this.physicsWorld)

        const floorOffsetY = WORLD.groundY - getBuilderFloorTop()

        // The structure starts asleep: it only moves once something touches it.
        for (const block of build.blocks) {
            const material = MATERIALS[block.material]
            const destroyBlock = {
                ...block,
                y: block.y + floorOffsetY
            }
            const maxHealth = getBlockMaxHealth(destroyBlock)
            const body = addBlockBody(this.physicsWorld, destroyBlock, {
                awake: false,
                game: {
                    origin: {
                        x: destroyBlock.x,
                        y: destroyBlock.y,
                        angle: block.rotation || 0
                    },
                    breakable: material.breakable !== false,
                    maxHealth,
                    health: material.breakable === false ? Infinity : maxHealth
                }
            })
            this.physics.blockBodies.set(block.id, body)
            this.scene.addBlockBodyMesh(destroyBlock)
        }

        const destroyFlag = {
            x: build.flag.x,
            y: build.flag.y + floorOffsetY
        }
        this.physics.flag = addFlagBody(this.physicsWorld, destroyFlag, {
            awake: false,
            game: {
                origin: {
                    x: destroyFlag.x,
                    y: destroyFlag.y,
                    angle: 0
                },
                health: DESTROYER_FLAG_HEALTH
            }
        })
        this.scene.addFlagMesh('flag', destroyFlag.x, destroyFlag.y)
    }

    ensureBuilderPhysics() {
        if (!this.builderPhysicsEnabled) {
            return
        }

        if (this.builderPhysics) {
            return
        }

        this.resetBuilderPhysics()
    }

    resetBuilderPhysics() {
        this.destroyBuilderPhysics()

        if (!this.builderPhysicsEnabled || (this.mode !== 'builder' && this.mode !== 'published')) {
            return
        }

        // Same gravity, time scale and materials as the destroyer, so what
        // stands here stands under attack.
        const world = createStageWorld({
            gravity: getDestroyerGravity(),
            timeScale: destroyerTuning.timeScale,
            onContact: (contact) => this.handleBuilderCollision(contact)
        })
        this.builderPhysics = {
            world,
            bodies: new Map(),
            flag: null,
            collisionDamageTimes: new Map()
        }

        addBuilderBounds(world, getBuilderFloorTop())

        for (const block of this.builderBlocks) {
            const material = MATERIALS[block.material]
            const maxHealth = getBlockMaxHealth(block)
            const body = addBlockBody(world, block, {
                type: 'builder-block',
                game: {
                    breakable: material.breakable !== false,
                    maxHealth,
                    health: material.breakable === false ? Infinity : maxHealth
                }
            })
            this.builderPhysics.bodies.set(block.id, body)
        }

        this.builderPhysics.flag = addFlagBody(world, this.builderFlag, {
            id: 'builder-flag',
            type: 'builder-flag',
            game: { health: DESTROYER_FLAG_HEALTH }
        })
        this.scene.showBuilder(this.builderBlocks, this.selectedBlockId, this.builderFlag, this.selectedFlag, {
            frameCamera: false
        })
    }

    destroyBuilderPhysics() {
        if (!this.builderPhysics) {
            return
        }

        this.builderPhysics.world.destroy()
        this.builderPhysics = null
        this.builderDrag = null
    }

    startBuilderDrag(event, type, id, point) {
        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }
        const historySnapshot = this.captureBuilderLayout()

        if (!this.builderPhysicsEnabled) {
            const source = type === 'flag'
                ? this.builderFlag
                : this.builderBlocks.find((block) => block.id === id)

            if (!source) {
                return
            }

            this.builderDrag = {
                pointerId: event.pointerId,
                type,
                id,
                isStatic: true,
                historySnapshot,
                blocked: false,
                hasMoved: false,
                startPoint: { ...point },
                lastValid: {
                    x: source.x,
                    y: source.y,
                    rotation: source.rotation || 0
                },
                offset: {
                    x: source.x - point.x,
                    y: source.y - point.y
                }
            }
            this.canvas.setPointerCapture(event.pointerId)
            return
        }

        if (!this.builderPhysics) {
            return
        }

        const body = type === 'flag'
            ? this.builderPhysics.flag
            : this.builderPhysics.bodies.get(id)

        if (!body) {
            return
        }

        this.builderDrag = {
            pointerId: event.pointerId,
            type,
            id,
            historySnapshot,
            blocked: false,
            hasMoved: false,
            startPoint: { ...point },
            lastValid: {
                x: body.position.x,
                y: body.position.y,
                rotation: body.angle || 0
            },
            offset: {
                x: body.position.x - point.x,
                y: body.position.y - point.y
            }
        }
        this.canvas.setPointerCapture(event.pointerId)

        if (type === 'block') {
            this.builderDrag.joint = this.builderPhysics.world.createDragJoint(body, point)
        }
    }

    updateBuilderDrag(event) {
        if (!this.builderDrag || event.pointerId !== this.builderDrag.pointerId) {
            return
        }

        const point = this.scene.screenToWorld(event.clientX, event.clientY)
        const rawTarget = {
            x: point.x + this.builderDrag.offset.x,
            y: point.y + this.builderDrag.offset.y
        }
        const moveDistance = Math.hypot(
            point.x - this.builderDrag.startPoint.x,
            point.y - this.builderDrag.startPoint.y
        )

        if (this.builderDrag.isStatic) {
            if (!this.builderDrag.hasMoved && moveDistance < BUILDER_DRAG_START_DISTANCE) {
                return
            }
            this.builderDrag.hasMoved = true

            if (this.builderDrag.type === 'flag') {
                const target = clampFlagPosition(rawTarget)
                this.builderFlag = target
                this.scene.setMeshTransform('builder-flag', target.x, target.y)
                return
            }

            const block = this.builderBlocks.find((item) => item.id === this.builderDrag.id)
            if (!block) {
                return
            }

            const target = getBuilderDragBlockTarget({
                ...rawTarget,
                width: block.width,
                height: block.height
            }, block, this.builderBlocks, block.id)
            const candidate = { ...block, ...target }

            if (!isBlockPlacementValid(candidate, this.builderBlocks, block.id)) {
                this.builderDrag.blocked = true
                return
            }

            this.builderDrag.blocked = false
            this.builderDrag.lastValid = {
                x: target.x,
                y: target.y,
                rotation: block.rotation || 0
            }
            block.x = target.x
            block.y = target.y
            this.scene.setMeshTransform(block.id, block.x, block.y, block.rotation || 0)
            return
        }

        if (!this.builderPhysics) {
            return
        }

        if (!this.builderDrag.hasMoved && moveDistance < BUILDER_DRAG_START_DISTANCE) {
            return
        }
        this.builderDrag.hasMoved = true

        if (this.builderDrag.type === 'flag') {
            this.builderFlag = clampFlagPosition(rawTarget)
            this.builderPhysics.flag.setPosition(this.builderFlag, 0)
            this.builderPhysics.flag.setVelocity({ x: 0, y: 0 })
            this.builderPhysics.flag.setAngularVelocity(0)
            this.scene.syncBody('builder-flag', this.builderPhysics.flag)
            return
        }

        const body = this.builderPhysics.bodies.get(this.builderDrag.id)
        const block = this.builderBlocks.find((item) => item.id === this.builderDrag.id)
        if (!body || !block) {
            return
        }

        if (this.builderDrag.joint) {
            this.builderDrag.joint.setTarget(point)
            return
        }

        const target = snapBlockPosition({
            ...rawTarget,
            width: block.width,
            height: block.height
        }, body.angle || 0, this.builderBlocks, block.id)
        this.builderDrag.blocked = false
        this.builderDrag.lastValid = {
            x: target.x,
            y: target.y,
            rotation: body.angle || 0
        }
        body.setPosition(target)
        body.setVelocity({ x: 0, y: 0 })
        body.setAngularVelocity(0)
        this.syncBuilderPhysicsMeshes()
    }

    finishBuilderDrag(event) {
        if (!this.builderDrag || event.pointerId !== this.builderDrag.pointerId) {
            return
        }

        const drag = this.builderDrag
        this.canvas.releasePointerCapture(event.pointerId)
        this.builderDrag = null

        if (drag.joint && this.builderPhysics) {
            drag.joint.destroy()
        }

        if (drag.isStatic) {
            this.commitBuilderDragHistory(drag)
            this.status = drag.type === 'block'
                ? drag.blocked ? 'That grid slot is already occupied.' : drag.hasMoved ? 'Block moved.' : 'Block selected.'
                : drag.hasMoved ? 'Flag moved.' : 'Flag selected.'
            this.renderBuilder()
            return
        }

        if (drag.type === 'block') {
            this.status = drag.blocked
                ? 'That grid slot is already occupied.'
                : drag.hasMoved ? 'Block released.' : 'Block selected.'
        } else {
            this.status = 'Flag moved.'
        }

        this.syncBuilderPhysicsState()
        this.commitBuilderDragHistory(drag)
        this.renderBuilder()
    }

    commitBuilderDragHistory(drag) {
        if (drag.hasMoved && !isSameBuilderLayout(drag.historySnapshot, this.captureBuilderLayout())) {
            this.recordBuilderHistory(drag.historySnapshot)
        }
    }

    captureBuilderLayout() {
        return {
            blocks: cloneBuilderBlocks(this.builderBlocks),
            flag: { ...this.builderFlag }
        }
    }

    recordBuilderHistory(snapshot = this.captureBuilderLayout()) {
        this.builderHistory.undo.push(snapshot)
        if (this.builderHistory.undo.length > BUILDER_HISTORY_LIMIT) {
            this.builderHistory.undo.shift()
        }
        this.builderHistory.redo = []
    }

    clearBuilderHistory() {
        this.builderHistory = { undo: [], redo: [] }
    }

    undoBuilder() {
        this.stepBuilderHistory('undo', 'redo', 'Undone.')
    }

    redoBuilder() {
        this.stepBuilderHistory('redo', 'undo', 'Redone.')
    }

    stepBuilderHistory(from, to, status) {
        if (this.mode !== 'builder' || this.builderPublishValidation || this.builderDrag) {
            return
        }

        const snapshot = this.builderHistory[from].pop()
        if (!snapshot) {
            return
        }

        if (this.builderPhysicsEnabled) {
            this.syncBuilderPhysicsState()
        }
        this.builderHistory[to].push(this.captureBuilderLayout())
        if (this.builderHistory[to].length > BUILDER_HISTORY_LIMIT) {
            this.builderHistory[to].shift()
        }

        this.builderBlocks = cloneBuilderBlocks(snapshot.blocks)
        this.builderFlag = { ...snapshot.flag }
        if (!this.builderBlocks.some((block) => block.id === this.selectedBlockId)) {
            this.selectedBlockId = null
        }
        if (this.builderPhysicsEnabled) {
            this.resetBuilderPhysics()
        }
        this.status = status
        this.renderBuilder()
    }

    syncBuilderPhysicsMeshes() {
        this.syncBuilderPhysicsState()

        for (const [id, body] of this.builderPhysics.bodies) {
            this.scene.syncBody(id, body)
        }

        if (this.builderPhysics.flag) {
            this.scene.syncBody('builder-flag', this.builderPhysics.flag)
        }
    }

    syncBuilderPhysicsState(options = {}) {
        if (!this.builderPhysics) {
            return
        }

        const snapNearGrid = Boolean(options.snapNearGrid)

        for (const block of this.builderBlocks) {
            const body = this.builderPhysics.bodies.get(block.id)
            if (!body) {
                continue
            }

            const synced = snapNearGrid
                ? getBuilderPhysicsSyncedBlock(block, body, this.builderBlocks)
                : {
                    x: body.position.x,
                    y: body.position.y,
                    rotation: body.angle || 0
                }

            block.x = roundTo(synced.x, 3)
            block.y = roundTo(synced.y, 3)
            block.rotation = roundTo(synced.rotation, 4)
        }

        if (this.builderPhysics.flag) {
            this.builderFlag = {
                x: roundTo(this.builderPhysics.flag.position.x, 3),
                y: roundTo(this.builderPhysics.flag.position.y, 3)
            }
        }
    }

    destroyPhysics() {
        this.clearResultTimer()
        this.cancelBulletDrag()
        if (!this.physicsWorld) {
            return
        }

        this.physicsWorld.destroy()
        this.physicsWorld = null
        this.physics = null
        this.setAimingUi(false)
    }

    selectBullet(type) {
        if (!this.physics || this.physics.inventory[type] <= 0) {
            return
        }

        this.physics.currentBombType = type
        this.destroyerAmmoOpen = false
        this.status = `${BOMBS[type].label} ready for ${formatBulletTarget(BOMBS[type])}.`
        this.renderDestroyer()
    }

    startBulletDrag(event, options = {}) {
        if (!this.physics || this.physics.ended) {
            return
        }

        if (this.isDestroyerShotUiHidden()) {
            return
        }

        if (countInventory(this.physics.inventory) <= 0 || this.physics.bombs.size) {
            this.startCameraPan(event)
            return
        }

        const type = firstAvailableBomb(this.physics.inventory, this.physics.currentBombType)
        if (!type) {
            this.startCameraPan(event)
            return
        }

        const point = this.scene.screenToWorld(event.clientX, event.clientY)
        const anchor = getSlingCenter()
        const allowAnywhere = Boolean(options.allowAnywhere)

        if (!allowAnywhere && !isPointOnReadyBullet(point, type)) {
            this.startCameraPan(event)
            return
        }

        event.preventDefault()
        this.physics.currentBombType = type
        this.cameraPanOverride = false
        this.draggingLaunch = {
            pointerId: event.pointerId,
            anchor,
            type,
            point: anchor,
            grabOffset: {
                x: anchor.x - point.x,
                y: anchor.y - point.y
            },
            captureTarget: getPointerCaptureTarget(event, this.canvas)
        }
        this.draggingLaunch.captureTarget.setPointerCapture?.(event.pointerId)
        this.scene.removeMesh(READY_BULLET_ID)
        this.scene.addBulletMesh(READY_BULLET_ID, type, anchor.x, anchor.y)
        this.scene.clearSlingGuide()
        this.setAimingUi(true)
        this.updateBulletDrag(event)
    }

    updateBulletDrag(event) {
        if (!this.draggingLaunch || event.pointerId !== this.draggingLaunch.pointerId) {
            return
        }

        const point = this.scene.screenToWorld(event.clientX, event.clientY)
        const pullPoint = {
            x: point.x + this.draggingLaunch.grabOffset.x,
            y: point.y + this.draggingLaunch.grabOffset.y
        }
        const clamped = clampBulletPullPoint(pullPoint, this.draggingLaunch.anchor, this.draggingLaunch.type)
        this.draggingLaunch.point = clamped
        const readyMesh = this.scene.meshById.get(READY_BULLET_ID)
        if (readyMesh) {
            readyMesh.position.x = clamped.x
            readyMesh.position.y = clamped.y
        }
        this.scene.setSlingGuide(this.draggingLaunch.anchor, clamped, this.draggingLaunch.type)
        this.scene.addTrajectoryLine(projectBulletTrajectory(clamped, this.draggingLaunch.anchor, this.draggingLaunch.type), this.draggingLaunch.type)
    }

    finishBulletDrag(event) {
        if (!this.draggingLaunch || event.pointerId !== this.draggingLaunch.pointerId) {
            return
        }

        const drag = this.draggingLaunch
        this.cancelBulletDrag()

        if (this.mode !== 'destroyer' || !this.physics || this.physics.ended) {
            return
        }

        const velocity = calculateBulletLaunchVelocity(drag.point, drag.anchor, drag.type)

        if (Math.hypot(velocity.x, velocity.y) < destroyerTuning.minLaunchSpeed) {
            this.status = 'Hold farther to launch.'
            this.renderDestroyer()
            return
        }

        const launchIntensity = clamp(Vector.magnitude(velocity) / 1.1, 0.5, 1.8)
        if (typeof this.audio.playBulletLaunch === 'function') {
            this.audio.playBulletLaunch(drag.type, launchIntensity)
        } else {
            this.audio.playSlingSnap(launchIntensity)
        }
        this.launchBullet(drag.type, drag.point, velocity)
    }

    cancelBulletDrag() {
        const drag = this.draggingLaunch

        if (drag?.captureTarget?.hasPointerCapture?.(drag.pointerId)) {
            drag.captureTarget.releasePointerCapture(drag.pointerId)
        }

        if (drag) {
            this.scene.clearEffects()
        }

        this.scene.removeMesh(READY_BULLET_ID)
        this.scene.clearSlingGuide()
        this.draggingLaunch = null
        this.setAimingUi(false)
    }

    syncReadyBullet() {
        const hasReadyBullet = this.mode === 'destroyer'
            && this.physics
            && !this.physics.ended
            && !this.draggingLaunch
            && !this.isDestroyerShotUiHidden()
            && this.physics.bombs.size === 0
            && countInventory(this.physics.inventory) > 0

        if (!hasReadyBullet) {
            this.scene.removeMesh(READY_BULLET_ID)
            this.scene.clearSlingGuide()
            return
        }

        const type = firstAvailableBomb(this.physics.inventory, this.physics.currentBombType)
        if (!type) {
            this.scene.removeMesh(READY_BULLET_ID)
            this.scene.clearSlingGuide()
            return
        }

        this.physics.currentBombType = type
        const anchor = getSlingCenter()
        const readyMesh = this.scene.meshById.get(READY_BULLET_ID)

        if (!readyMesh || readyMesh.userData.bulletType !== type) {
            this.scene.removeMesh(READY_BULLET_ID)
            this.scene.addBulletMesh(READY_BULLET_ID, type, anchor.x, anchor.y)
            this.scene.clearSlingGuide()
            return
        }

        this.scene.setMeshTransform(READY_BULLET_ID, anchor.x, anchor.y, 0)
        this.scene.clearSlingGuide()
    }

    launchBullet(type, point, velocity) {
        if (this.mode !== 'destroyer' || !this.physics || this.physics.ended) {
            return
        }

        const config = BOMBS[type]
        const id = createId('bullet')
        const launchedAt = this.now()
        const body = this.physicsWorld.addCircle({
            x: point.x,
            y: point.y,
            radius: config.radius,
            density: getBombDensity(type),
            linearDamping: airFrictionToDamping(getBulletAirResistance(type)),
            friction: 0.72,
            restitution: getBulletGroundBounce(type),
            angularDamping: BULLET_ANGULAR_DAMPING,
            bullet: true,
            label: `bullet:${id}`,
            game: {
                id,
                type: 'bullet',
                bulletType: type,
                groundBounce: getBulletGroundBounce(type),
                hitBodies: new Map(),
                groundHits: 0,
                launchedAt,
                removalTimer: null
            }
        })
        body.setVelocity(velocity)
        body.setAngularVelocity(getBulletLaunchSpin(type, velocity))
        this.physics.inventory[type] -= 1
        this.physics.bombsUsed += 1
        this.physics.cameraLaunchedAt = launchedAt
        this.physics.shotUiHidden = true
        this.physics.shotUiRevealAt = launchedAt + getDestroyerWallDuration(SHOT_UI_HIDE_MAX_MS)
        this.cameraPanOverride = false
        this.physics.bombs.set(id, body)
        this.scene.addBulletMesh(id, type, point.x, point.y)
        this.status = ''
        this.renderDestroyer()
    }

    handleCollision(contact) {
        if (!this.physics || this.physics.ended) {
            return
        }

        const { bodyA, bodyB, relativeSpeed } = contact
        const bulletBody = bodyA.plugin.game?.type === 'bullet' ? bodyA : bodyB.plugin.game?.type === 'bullet' ? bodyB : null

        if (bulletBody) {
            const hitBody = bulletBody === bodyA ? bodyB : bodyA
            this.handleBulletCollision(bulletBody, hitBody, relativeSpeed, contact)
            return
        }

        this.handleStructureCollision(contact, relativeSpeed)
    }

    handleStructureCollision(pair, relativeSpeed) {
        const { bodyA, bodyB } = pair
        const typeA = bodyA.plugin.game?.type
        const typeB = bodyB.plugin.game?.type

        if (typeA === 'block' && typeB === 'block') {
            const pairKey = getCollisionPairKey(pair, bodyA, bodyB)
            if (relativeSpeed > 0.72 && !this.isCollisionDamageCoolingDown(`cloud:${pairKey}`, 180)) {
                const contact = getCollisionPoint(pair, bodyA, bodyB)
                this.scene.addImpactCloud(contact.x, contact.y, {
                    material: getHeavierCollisionMaterial(bodyA, bodyB),
                    intensity: clamp(relativeSpeed / 1.2, 0.35, 1.5)
                })
            }
            this.audio.playBlockCollision(
                bodyA.plugin.game.material,
                bodyB.plugin.game.material,
                clamp(relativeSpeed / 1.05, 0, 1.6),
                pairKey
            )
        }

        if (this.isCollisionDamageCoolingDown(`hit:${getCollisionPairKey(pair, bodyA, bodyB)}`, 220)) {
            return
        }

        this.applyCollisionDamage(bodyA, bodyB, relativeSpeed)
        this.applyCollisionDamage(bodyB, bodyA, relativeSpeed)
    }

    // Blocks and the flag take damage from whatever hits them (blocks, ground,
    // walls). Static bodies hit with the target's own weight.
    applyCollisionDamage(targetBody, sourceBody, relativeSpeed) {
        const game = targetBody.plugin.game
        if (targetBody.removed || (game?.type !== 'block' && game?.type !== 'flag')) {
            return
        }

        if (game.type === 'block' && !game.breakable) {
            return
        }

        const targetMaterial = game.type === 'flag' ? 'flag' : game.material || 'wood'
        const sourceMass = sourceBody.isStatic ? targetBody.mass : sourceBody.mass
        const damage = calculateCollisionDamage({ sourceMass, targetMaterial, relativeSpeed })
        if (damage <= 0) {
            return
        }

        const sourceGame = sourceBody.plugin.game
        const label = sourceGame?.type === 'block'
            ? `${MATERIALS[sourceGame.material]?.label || sourceGame.material} impact`
            : 'Fall'

        if (game.type === 'flag') {
            game.health -= damage
            this.recordImpactStat('fallDamage', damage, label)
            this.updateFlagDamageVisual(targetBody)
            return
        }

        game.health -= damage
        this.updateBlockDamageVisual(targetBody)
        this.recordImpactStat('stressDamage', damage, label)

        if (game.health <= 0 && this.physics.blockBodies.has(game.id)) {
            this.breakBlock(targetBody, damage)
        }
    }

    handleBulletCollision(bulletBody, hitBody, relativeSpeed, pair) {
        const bulletGame = bulletBody.plugin.game
        if (!bulletGame || !this.physics.bombs.has(bulletGame.id) || bulletGame.detonated || bulletGame.spent) {
            return
        }

        bulletGame.contacted = true
        const bulletType = bulletGame.bulletType || 'basic'
        const config = getBulletConfig(bulletType)
        const hitType = hitBody.plugin.game?.type
        const speed = Math.max(Vector.magnitude(bulletBody.impactVelocity), relativeSpeed)
        const intensity = clamp(speed / 1.2, 0.32, 2)
        if (hitType === 'block' || hitType === 'flag') {
            this.physics.structureDisturbed = true
            this.applyBulletImpact(bulletBody, hitBody, speed, pair)
            this.playBulletImpact(bulletType, getBodyAudioMaterial(hitBody), intensity)
            if (config.blastRadius) {
                const contact = getCollisionPoint(pair, bulletBody, hitBody)
                this.detonateExplosiveBullet(bulletBody, contact, speed)
                return
            }
            return
        }

        if (hitBody.label === 'ground' || hitBody.label === 'wall') {
            this.playBulletImpact(bulletType, 'stone', intensity)
            if (config.blastRadius && speed > 0.24) {
                const contact = getCollisionPoint(pair, bulletBody, hitBody)
                this.detonateExplosiveBullet(bulletBody, contact, speed)
                return
            }
            this.handleBulletGroundCollision(bulletBody, hitBody, speed, pair)
        }
    }

    handleBulletGroundCollision(bulletBody, hitBody, speed, pair = null) {
        const game = bulletBody.plugin.game

        if (!game) {
            return
        }

        game.groundHits = (game.groundHits || 0) + 1
        const contact = getCollisionPoint(pair, bulletBody, hitBody)
        this.scene.addImpactCloud(contact.x, contact.y, {
            material: 'stone',
            intensity: clamp(speed / 1.45, 0.35, 1.6)
        })
    }

    applyBulletImpact(bulletBody, targetBody, speed, pair = null) {
        const targetGame = targetBody.plugin.game
        const bulletGame = bulletBody.plugin.game
        const targetKey = targetGame?.id || targetBody.id
        const now = this.now()
        const lastHitAt = bulletGame.hitBodies.get(targetKey) || 0

        if (now - lastHitAt < getDestroyerWallDuration(220)) {
            return
        }

        bulletGame.hitBodies.set(targetKey, now)
        targetBody.setAwake(true)

        // Shards are harmless to everything but glass.
        if (bulletGame.fragment && (targetGame?.type !== 'block' || targetGame.material !== 'glass')) {
            return
        }

        const bulletType = bulletGame.bulletType || 'basic'
        const config = BOMBS[bulletType] || BOMBS.basic
        // Momentum transfer comes from the physics contact itself; this only
        // resolves damage and effects.
        const direction = getImpactDirection(bulletBody, targetBody)
        const normalFactor = getCollisionNormalFactor(pair, bulletBody)
        const contact = getCollisionPoint(pair, bulletBody, targetBody)
        this.scene.addImpactCloud(contact.x, contact.y, {
            material: getBodyAudioMaterial(targetBody),
            intensity: clamp(speed / 1.2, 0.4, 2)
        })

        if (targetGame?.type === 'flag') {
            const cover = getFlagCoverScore(this.physics, this.activeBuild)
            const exposedBonus = cover < FLAG_EXPOSED_COVER_THRESHOLD ? 1.35 : 1
            const damage = Math.max(
                calculateBulletDamage(bulletBody, 'flag', speed, normalFactor) * exposedBonus,
                FLAG_HIT_MIN_DAMAGE * exposedBonus * clamp(speed / 0.95, 1, 1.35)
            )
            bulletGame.flagContactApplied = true
            this.physics.hitLog?.push({ bulletType, target: 'flag', speed, damage, ratio: damage / DESTROYER_FLAG_HEALTH })
            this.damageFlag(targetBody, damage, `${config.shortLabel || config.label} -> Flag`)
            return
        }

        if (targetGame?.type !== 'block') {
            return
        }

        const material = targetGame.material || 'wood'
        const damage = calculateBulletDamage(bulletBody, material, speed, normalFactor)
        this.physics.hitLog?.push({ bulletType, target: material, speed, damage, ratio: damage / (targetGame.maxHealth || 1) })
        const healthBefore = Math.max(0, targetGame.health)
        targetGame.health -= damage
        this.updateBlockDamageVisual(targetBody)
        this.recordImpactStat('directDamage', damage, `${config.shortLabel || config.label} -> ${MATERIALS[material]?.label || material}`)
        this.applyHorizontalImpact(targetBody, direction, speed, config, damage)

        if (targetGame.health > 0) {
            return
        }

        this.breakBlock(targetBody, damage, direction)
        this.carryBulletThroughBrokenBlock(bulletBody, healthBefore, damage)
    }

    // The solver has already bounced the bullet off the block it just broke.
    // Let it smash through instead, keeping the energy the block didn't absorb.
    carryBulletThroughBrokenBlock(bulletBody, healthBefore, damage) {
        const config = getBulletConfig(bulletBody.plugin.game?.bulletType)
        if (config.blastRadius || damage <= 0 || bulletBody.removed) {
            return
        }

        const incoming = bulletBody.impactVelocity
        const energyLeft = clamp(1 - healthBefore / damage, 0, 1)
        const keep = Math.sqrt(energyLeft) * BULLET_BREAKTHROUGH_RETENTION
        if (keep <= 0.05) {
            return
        }

        bulletBody.setVelocity(Vector.mult(incoming, keep))
    }

    detonateExplosiveBullet(bulletBody, origin, speed = 1) {
        const bulletGame = bulletBody?.plugin?.game
        const config = getBulletConfig(bulletGame?.bulletType || 'explosive')

        if (!this.physics || !this.physicsWorld || !bulletGame || bulletGame.detonated || !config.blastRadius) {
            return
        }

        bulletGame.detonated = true
        this.physics.structureDisturbed = true
        const blastOrigin = origin || { x: bulletBody.position.x, y: bulletBody.position.y }
        const radius = config.blastRadius
        const baseDamage = config.blastDamage || 80
        const blastKick = config.blastImpulse ?? 1.2
        const blastIntensity = clamp(speed / 1.1, 0.85, 2.1)

        this.scene.addBlast(blastOrigin.x, blastOrigin.y, radius, config.color)
        this.scene.shake(0.28)
        this.scene.addImpactCloud(blastOrigin.x, blastOrigin.y, {
            material: 'stone',
            intensity: blastIntensity
        })

        for (const body of [...this.physics.blockBodies.values()]) {
            const game = body.plugin.game
            if (!game?.breakable) {
                continue
            }

            const distance = Vector.magnitude(Vector.sub(body.position, blastOrigin))
            if (distance > radius + Math.max(game.width || 0, game.height || 0) * 0.45) {
                continue
            }

            const falloff = clamp(1 - distance / radius, 0, 1)
            const material = game.material || 'wood'
            const profile = MATERIALS[material] || MATERIALS.wood
            const matchup = config.materialMultipliers?.[material]
                ?? (config.bonusAgainst === material ? config.materialMultiplier || 1 : config.offMaterialMultiplier || 0.75)
            const damage = baseDamage
                * (0.28 + falloff * 0.92)
                * matchup
                / Math.max(0.35, profile.toughness || 1)
            const direction = getSafeDirection(blastOrigin, body.position, { x: 0.3, y: 0.15 })

            game.health -= damage
            this.updateBlockDamageVisual(body)
            this.recordImpactStat('directDamage', damage, `${config.shortLabel || config.label} blast`)
            body.addVelocity(Vector.mult(direction, blastKick * (0.32 + falloff)))

            if (game.health <= 0 && this.physics.blockBodies.has(game.id)) {
                this.breakBlock(body, damage, direction)
            }
        }

        const flagBody = this.physics.flag
        if (flagBody?.plugin?.game?.type === 'flag') {
            const distance = Vector.magnitude(Vector.sub(flagBody.position, blastOrigin))
            if (distance <= radius + FLAG.width * 0.8) {
                const falloff = clamp(1 - distance / (radius + FLAG.width * 0.4), 0, 1)
                const direction = getSafeDirection(blastOrigin, flagBody.position, { x: 0.2, y: 0.1 })
                const damage = baseDamage * (0.16 + falloff * 0.38)

                flagBody.addVelocity(Vector.mult(direction, blastKick * (0.25 + falloff)))
                this.damageFlag(flagBody, damage, `${config.shortLabel || config.label} blast`)
            }
        }

        this.removeBullet(bulletBody)
    }

    damageFlag(flagBody, damage, label = 'Flag hit') {
        const game = flagBody?.plugin?.game
        if (!game || game.type !== 'flag' || damage <= 0) {
            return
        }

        const flagState = getFlagRuntimeState(this.physics, this.activeBuild)
        const appliedDamage = Math.max(0, damage)
        flagBody.setAwake(true)
        game.health -= appliedDamage
        game.hitCount = (game.hitCount || 0) + 1
        game.exposedHitCount = flagState.exposed
            ? (game.exposedHitCount || 0) + 1
            : (game.exposedHitCount || 0)

        if (flagState.exposed && game.exposedHitCount >= 2) {
            game.health = Math.min(game.health, 0)
        }

        this.recordImpactStat('directDamage', appliedDamage, label)
        this.updateFlagDamageVisual(flagBody)
    }

    applyFlagGrazeDamage(bulletBody, now) {
        const flagBody = this.physics?.flag
        const bulletGame = bulletBody?.plugin?.game

        if (!flagBody || !bulletGame || bulletGame.flagContactApplied || bulletGame.fragment) {
            return
        }

        const speed = Vector.magnitude(bulletBody.velocity)
        if (speed < 0.24) {
            return
        }

        if (now - (bulletGame.lastFlagGrazeAt || 0) < getDestroyerWallDuration(FLAG_GRAZE_COOLDOWN_MS)) {
            return
        }

        const bulletType = bulletGame.bulletType || 'basic'
        const config = getBulletConfig(bulletType)
        const flagBounds = expandBounds(getBodyBounds(flagBody), config.radius + FLAG_GRAZE_MARGIN)

        if (!pointInBounds(bulletBody.position, flagBounds)) {
            return
        }

        const flagState = getFlagRuntimeState(this.physics, this.activeBuild)
        if (!flagState.exposed && flagState.coverScore >= FLAG_EXPOSED_COVER_THRESHOLD) {
            return
        }

        const damage = Math.max(
            calculateBulletDamage(bulletBody, 'flag', speed, 0.82) * 0.62,
            FLAG_GRAZE_MIN_DAMAGE * clamp(speed / 0.9, 0.72, 1.28)
        )
        bulletGame.flagContactApplied = true
        bulletGame.lastFlagGrazeAt = now
        this.damageFlag(flagBody, damage, `${config.shortLabel || config.label} clipped Flag`)
        this.scene.addImpactCloud(bulletBody.position.x, bulletBody.position.y, {
            material: 'flag',
            intensity: clamp(speed / 1.1, 0.45, 1.6)
        })
    }

    applyHorizontalImpact(targetBody, direction, speed, config, directDamage) {
        if (!targetBody?.plugin?.game?.id || !this.physics?.blockBodies?.size) {
            return
        }

        const graph = this.buildStructureGraph()
        const sourceForce = clamp(speed * (config.impactForce || 1) * 0.72, 0, 2.4)
        const propagated = propagateHorizontalImpact(targetBody.plugin.game.id, sourceForce, direction, graph)

        for (const [id, force] of propagated) {
            const body = this.physics.blockBodies.get(id)
            const game = body?.plugin?.game
            if (!body || !game?.breakable) {
                continue
            }

            const material = game.material || 'wood'
            const materialProfile = MATERIALS[material] || MATERIALS.wood
            const damage = force
                * (config.impactStrength || 40)
                * 0.035
                * (config.materialMultipliers?.[material] ?? (config.bonusAgainst === material ? config.materialMultiplier || 1 : config.offMaterialMultiplier || 0.5))
                / Math.max(0.35, materialProfile.toughness || 1)

            if (damage < Math.max(1.25, directDamage * 0.025)) {
                continue
            }

            game.health -= damage
            this.updateBlockDamageVisual(body)
            this.recordImpactStat('propagatedDamage', damage, 'Force chain')

            if (game.health <= 0) {
                this.breakBlock(body, damage, direction)
            }
        }
    }

    breakBlock(targetBody, damage) {
        const targetGame = targetBody.plugin.game
        const material = targetGame.material || 'wood'
        const collapseIds = this.predictCollapseFrom(targetGame.id)
        const score = calculateBlockDestroyScore(targetGame)
        const scorePosition = { x: targetBody.position.x, y: targetBody.position.y }

        // Removing the body wakes everything it touched; whatever loses its
        // support then falls on its own.
        this.physics.structureDisturbed = true
        this.audio.playBlockBreak(material, clamp(damage / (targetGame.maxHealth || MATERIALS[material]?.health || 100), 0.65, 1.9))
        this.physicsWorld.remove(targetBody)
        this.physics.blockBodies.delete(targetGame.id)
        this.scene.shake(material === 'stone' ? 0.12 : 0.07)
        this.scene.destroyBlockMesh(targetGame.id, { material })
        this.awardScore(score, scorePosition)

        if (collapseIds.size) {
            this.recordImpactStat('collapseCount', collapseIds.size, 'Support collapse')
        }
    }

    predictCollapseFrom(blockId) {
        if (!this.physics?.blockBodies?.size) {
            return new Set()
        }

        return predictVerticalImpact(blockId, this.buildStructureGraph())
    }

    buildStructureGraph() {
        return buildPhysicsGraph([...this.physics.blockBodies.values()], { floorY: WORLD.groundY })
    }

    isCollisionDamageCoolingDown(key, cooldownMs) {
        if (!this.physics?.collisionDamageTimes) {
            return false
        }

        const now = this.now()
        const last = this.physics.collisionDamageTimes.get(key) || 0

        if (now - last < getDestroyerWallDuration(cooldownMs)) {
            return true
        }

        this.physics.collisionDamageTimes.set(key, now)
        return false
    }

    updateBlockDamageVisual(body) {
        const game = body?.plugin?.game

        if (!game?.breakable || !Number.isFinite(game.maxHealth) || game.maxHealth <= 0) {
            return
        }

        this.scene.setBlockDamage(game.id, game.health / game.maxHealth, { material: game.material })
    }

    updateFlagDamageVisual(flagBody) {
        const game = flagBody?.plugin?.game

        if (!game || game.type !== 'flag') {
            return
        }

        this.scene.setFlagDamage?.('flag', game.health / DESTROYER_FLAG_HEALTH)
    }

    recordImpactStat(key, amount, label = '') {
        const stats = this.physics?.impactStats

        if (!stats) {
            return
        }

        if (key === 'collapseCount') {
            stats.collapseCount += amount
        } else {
            stats[key] = (stats[key] || 0) + Math.max(0, amount)
        }

        if (label) {
            stats.lastHit = label
        }
    }

    awardScore(points, position = null) {
        if (!this.physics || points <= 0) {
            return
        }

        const earned = Math.round(points)
        this.physics.score = (this.physics.score || 0) + earned
        this.physics.blocksDestroyed = (this.physics.blocksDestroyed || 0) + 1
        this.scene.addScorePopup?.(position?.x ?? WORLD.flagX, position?.y ?? WORLD.flagY, earned)
        this.updateDestroyerLiveHud()
    }

    playBulletImpact(bulletType, material, intensity) {
        if (typeof this.audio.playBulletImpact === 'function') {
            this.audio.playBulletImpact(bulletType, material, intensity)
            return
        }

        this.audio.playProjectileImpact(material, intensity)
    }

    removeBullet(bulletBody, options = {}) {
        if (!this.physics || !this.physicsWorld || !bulletBody?.plugin?.game) {
            return
        }

        const game = bulletBody.plugin.game
        const { id, removalTimer } = game
        if (!this.physics.bombs.has(id)) {
            return
        }

        if (removalTimer) {
            clearTimeout(removalTimer)
            bulletBody.plugin.game.removalTimer = null
        }

        if (options.cloud) {
            const config = getBulletConfig(game.bulletType)
            this.scene.addImpactCloud(bulletBody.position.x, bulletBody.position.y, {
                material: config.soundMaterial || 'stone',
                intensity: 0.72
            })
        }

        this.physicsWorld.remove(bulletBody)
        this.physics.bombs.delete(id)
        if (this.physics.bombs.size === 0) {
            this.physics.shotUiRevealAt = this.now() + getDestroyerWallDuration(SHOT_SETTLE_MAX_MS)
        }
        this.scene.removeMesh(id)
        this.physics.currentBombType = firstAvailableBomb(this.physics.inventory, this.physics.currentBombType)

        if (this.mode === 'destroyer' && !this.physics.ended) {
            this.renderDestroyer()
            this.checkEndState()
        }
    }

    handleBuilderCollision(pair) {
        if (!this.builderPhysics) {
            return
        }

        const { bodyA, bodyB, relativeSpeed } = pair
        const typeA = bodyA.plugin.game?.type
        const typeB = bodyB.plugin.game?.type

        if (typeA === 'builder-block' && typeB === 'builder-block') {
            const pairKey = getCollisionPairKey(pair, bodyA, bodyB)
            this.audio.playBlockCollision(
                bodyA.plugin.game.material,
                bodyB.plugin.game.material,
                clamp(relativeSpeed / 0.85, 0, 1.6),
                pairKey
            )
        }

        if (this.isBuilderCollisionDamageCoolingDown(`hit:${getCollisionPairKey(pair, bodyA, bodyB)}`, 220)) {
            return
        }

        this.applyBuilderCollisionDamage(bodyA, bodyB, relativeSpeed)
        this.applyBuilderCollisionDamage(bodyB, bodyA, relativeSpeed)
    }

    applyBuilderCollisionDamage(targetBody, sourceBody, relativeSpeed) {
        const game = targetBody.plugin.game
        if (game?.type !== 'builder-block' && game?.type !== 'builder-flag') {
            return
        }

        if (game.type === 'builder-block' && !game.breakable) {
            return
        }

        const targetMaterial = game.type === 'builder-flag' ? 'flag' : game.material || 'wood'
        const sourceMass = sourceBody.isStatic ? targetBody.mass : sourceBody.mass
        const damage = calculateCollisionDamage({ sourceMass, targetMaterial, relativeSpeed })
        if (damage <= 0) {
            return
        }

        game.health -= damage
        if (game.type === 'builder-flag') {
            this.updateBuilderFlagDamageVisual(targetBody)
        } else {
            this.updateBuilderBlockDamageVisual(targetBody)
        }
    }

    isBuilderCollisionDamageCoolingDown(key, cooldownMs) {
        if (!this.builderPhysics?.collisionDamageTimes) {
            return false
        }

        const now = this.now()
        const last = this.builderPhysics.collisionDamageTimes.get(key) || 0
        if (now - last < cooldownMs) {
            return true
        }

        this.builderPhysics.collisionDamageTimes.set(key, now)
        return false
    }

    updateBuilderBlockDamageVisual(body) {
        const game = body?.plugin?.game
        if (!game?.breakable || !Number.isFinite(game.maxHealth) || game.maxHealth <= 0) {
            return
        }

        this.scene.setBlockDamage(game.id, game.health / game.maxHealth, { material: game.material })
    }

    updateBuilderFlagDamageVisual(flagBody) {
        const game = flagBody?.plugin?.game
        if (!game || game.type !== 'builder-flag') {
            return
        }

        this.scene.setFlagDamage?.('builder-flag', game.health / DESTROYER_FLAG_HEALTH)
    }


    checkEndState() {
        if (!this.physics || this.physics.ended) {
            return
        }

        const flagState = getFlagRuntimeState(this.physics, this.activeBuild)

        if (flagState.captured) {
            this.finishAttempt(true)
            return
        }

        if (countInventory(this.physics.inventory) <= 0 && this.physics.bombs.size === 0) {
            this.physics.settleTimer += 1

            if (this.physics.settleTimer > 90) {
                this.finishAttempt(false)
            }
        } else {
            this.physics.settleTimer = 0
        }
    }

    finishAttempt(won) {
        if (!this.physics || this.physics.ended) {
            return
        }

        this.physics.ended = true
        this.physics.shotUiHidden = false
        this.physics.shotUiRevealAt = 0
        this.cancelBulletDrag()
        this.resultWon = won
        this.resultAttempt = null
        this.resultBombsUsed = this.physics.bombsUsed
        this.resultDurationMs = Math.round(this.now() - this.physics.startedAt)
        if (won && !this.physics.finalBonusApplied) {
            this.physics.score = (this.physics.score || 0) + FLAG_CAPTURE_SCORE
            this.physics.finalBonusApplied = true
        }
        this.resultScore = this.physics.score || 0
        this.status = ''
        this.clearResultTimer()
        this.resultTimer = setTimeout(() => {
            this.resultTimer = null

            if (this.mode === 'destroyer' && this.physics?.ended) {
                this.renderResult({ won })
            }
        }, DESTROYER_RESULT_DELAY_MS)
    }

    clearResultTimer() {
        if (!this.resultTimer) {
            return
        }

        clearTimeout(this.resultTimer)
        this.resultTimer = null
    }

    async recordDestroyerResult(form) {
        const formData = new FormData(form)
        const displayName = normalizeText(formData.get('displayName'), '', 40)
        const xHandle = String(formData.get('xHandle') || '').trim()

        this.destroyerName = displayName
        this.destroyerXHandle = xHandle

        if (!displayName) {
            this.status = 'Add a destroyer name to record the result.'
            this.renderResult({ won: this.resultWon })
            return
        }

        this.applyUserIdentity({ displayName, xHandle })
        this.status = 'Recording attempt...'
        this.renderResult({ won: this.resultWon })

        const ogImageBlob = this.resultWon
            ? await createOpenGraphImage(() => createResultOpenGraphImage({
                scene: this.scene,
                build: this.activeBuild,
                destroyerName: normalizeText(this.destroyerName, t('defaults.destroyer'), 40),
                bombsUsed: this.resultBombsUsed
            }))
            : null

        try {
            this.resultAttempt = await recordAttempt({
                build: this.activeBuild,
                destroyerName: normalizeText(this.destroyerName, t('defaults.destroyer'), 40),
                destroyerXHandle: this.destroyerXHandle,
                won: this.resultWon,
                bombsUsed: this.resultBombsUsed,
                durationMs: this.resultDurationMs,
                ogImageBlob
            })
            this.status = this.resultWon && isBackendConfigured()
                ? this.resultAttempt?.ogImageUrl ? 'Attempt recorded with share image.' : 'Attempt recorded. Share image unavailable.'
                : 'Attempt recorded.'
        } catch (error) {
            this.status = `Result could not be recorded: ${error.message}`
        }

        this.renderResult({ won: this.resultWon })
    }

    async copyShareUrl() {
        const url = this.shareUrl || createShareUrl(this.activeBuild)
        try {
            await navigator.clipboard.writeText(url)
            this.status = 'Link copied.'
        } catch {
            this.status = 'Copy failed. The link is selected below.'
        }

        if (this.mode === 'result') {
            this.renderResult({ won: this.resultWon })
        } else {
            this.ui.querySelector('.share-input')?.select()
            const toast = this.ui.querySelector('.toast')
            if (toast) {
                toast.textContent = localizeText(this.status)
            }
        }
    }

    tick() {
        const now = this.now()
        const delta = Math.min(0.04, (now - this.lastTime) / 1000)
        this.lastTime = now

        if (this.physicsWorld && this.physics && !this.physics.ended) {
            this.physicsWorld.step(delta * 1000)
            this.syncPhysicsMeshes()
            this.updateCameraFollow()
            this.updateDestroyerShotUi(now)
            this.updateDestroyerLiveHud(now)
            this.checkEndState()
        }

        if (this.mode === 'builder' && this.builderPhysicsEnabled && this.builderPhysics) {
            this.builderPhysics.world.step(delta * 1000)
            this.syncBuilderPhysicsMeshes()
        }

        this.scene.render(delta)
        requestAnimationFrame(() => this.tick())
    }

    syncPhysicsMeshes() {
        for (const [id, body] of this.physics.blockBodies) {
            this.scene.syncBody(id, body)
        }

        if (this.physics.flag) {
            this.scene.syncBody('flag', this.physics.flag)
        }

        const now = this.now()
        const spentBullets = []
        const splitting = []

        for (const [id, body] of this.physics.bombs) {
            if (shouldSplitBullet(body)) {
                splitting.push(body)
                continue
            }

            this.applyFlagGrazeDamage(body, now)
            if (!this.physics.bombs.has(id)) {
                continue
            }

            this.scene.syncBody(id, body)

            if (this.shouldRemoveSpentBullet(body, now)) {
                spentBullets.push({
                    body,
                    cloud: Boolean(body.plugin.game?.removeWithCloud)
                })
            }
        }

        for (const item of spentBullets) {
            this.removeBullet(item.body, { cloud: item.cloud })
        }

        for (const body of splitting) {
            this.splitBullet(body)
        }
    }

    // Cluster shot: replaces the bullet with a fan of small shards that keep
    // its speed. Shards don't collide with each other and only damage glass.
    splitBullet(body) {
        const game = body.plugin.game
        const config = getBulletConfig(game.bulletType)
        const count = config.splitCount || 0
        if (!count || game.split) {
            return
        }

        game.split = true
        const now = this.now()
        const origin = body.position
        const velocity = body.velocity
        const speed = Vector.magnitude(velocity)
        const heading = Math.atan2(velocity.y, velocity.x)
        const spread = ((config.splitSpreadDegrees || 20) * Math.PI) / 180
        const radius = config.fragmentRadius || config.radius * 0.4

        for (let index = 0; index < count; index += 1) {
            const offset = count === 1 ? 0 : (index / (count - 1) - 0.5) * spread
            const angle = heading + offset
            const id = createId('shard')
            const shard = this.physicsWorld.addCircle({
                x: origin.x + Math.cos(angle) * radius,
                y: origin.y + Math.sin(angle) * radius,
                radius,
                density: getBombDensity(game.bulletType),
                linearDamping: airFrictionToDamping(getBulletAirResistance(game.bulletType)),
                angularDamping: BULLET_ANGULAR_DAMPING,
                friction: 0.5,
                bullet: true,
                group: -1,
                label: `bullet:${id}`,
                game: {
                    id,
                    type: 'bullet',
                    bulletType: game.bulletType,
                    fragment: true,
                    split: true,
                    groundBounce: getBulletGroundBounce(game.bulletType),
                    hitBodies: new Map(),
                    groundHits: 0,
                    launchedAt: now,
                    removalTimer: null
                }
            })
            shard.setVelocity({ x: Math.cos(angle) * speed, y: Math.sin(angle) * speed })
            this.physics.bombs.set(id, shard)
            this.scene.addBulletMesh(id, game.bulletType, origin.x, origin.y, { radius })
        }

        this.scene.addImpactCloud(origin.x, origin.y, { material: 'glass', intensity: 0.7 })
        this.playBulletImpact(game.bulletType, 'glass', 0.6)
        this.removeBullet(body)
    }

    // Dev balance lab: plays a sequence of shots against a fresh copy of the
    // build in simulated time, using the real damage and capture rules.
    // shots: [{ bulletType, angle, pull }], angle in radians above horizontal.
    runBalanceTrial(build, shots, { secondsPerShot = 5 } = {}) {
        this.clearResultTimer()
        this.activeBuild = normalizeBuildRow(build)
        this.setupPhysics(this.activeBuild)
        this.physics.hitLog = []
        // Start well past zero so hit cooldowns (which compare against 0 for
        // "never hit") behave as in real play.
        this.trialClock = performance.now() + 60000

        const anchor = getSlingCenter()
        const startingBlocks = this.physics.blockBodies.size
        const stepWallMs = (SIM_STEP_SECONDS / destroyerTuning.timeScale) * 1000
        const maxSteps = Math.ceil(secondsPerShot / SIM_STEP_SECONDS)
        const perShot = []
        let shotsFired = 0

        try {
            for (const shot of shots) {
                if (this.physics.ended) {
                    break
                }

                const pull = Math.min(shot.pull, destroyerTuning.maxPullDistance)
                const point = clampBulletPullPoint({
                    x: anchor.x - Math.cos(shot.angle) * pull,
                    y: anchor.y - Math.sin(shot.angle) * pull
                }, anchor, shot.bulletType)
                this.physics.inventory[shot.bulletType] = (this.physics.inventory[shot.bulletType] || 0) + 1
                this.launchBullet(shot.bulletType, point, calculateBulletLaunchVelocity(point, anchor, shot.bulletType))
                shotsFired += 1

                for (let step = 1; step <= maxSteps && !this.physics.ended; step += 1) {
                    this.physicsWorld.stepOnce()
                    this.trialClock += stepWallMs

                    if (step % 4 === 0) {
                        this.syncPhysicsMeshes()
                        this.checkEndState()
                    }

                    const settled = this.physics.bombs.size === 0 && isDestroyerPhysicsStable(this.physics)
                    if (settled && step > 60) {
                        break
                    }
                }

                const flag = this.physics.flag
                perShot.push({
                    blocksLeft: this.physics.blockBodies.size,
                    flagHealth: Math.round(flag?.plugin.game.health ?? 0)
                })
            }

            const flag = this.physics.flag
            const origin = flag?.plugin.game.origin
            const flagState = getFlagRuntimeState(this.physics, this.activeBuild)
            const displacement = flag && origin ? Math.hypot(flag.position.x - origin.x, flag.position.y - origin.y) : 0
            const tilt = flag ? Math.abs(getAngleDelta(flag.angle, 0)) : 0

            return {
                captured: flagState.captured,
                capturedBy: !flagState.captured ? ''
                    : (flag?.plugin.game.health ?? 0) <= 0 ? 'health'
                        : flag.position.y < WORLD.groundY - 1.1 ? 'fell-off'
                            : displacement > 2.65 ? 'pushed'
                                : 'tipped',
                shotsFired,
                blocksDestroyed: startingBlocks - this.physics.blockBodies.size,
                startingBlocks,
                flagHealth: Math.round(flag?.plugin.game.health ?? 0),
                flagDisplacement: Number(displacement.toFixed(2)),
                flagTilt: Number(tilt.toFixed(2)),
                stats: { ...this.physics.impactStats },
                hitLog: this.physics.hitLog,
                perShot
            }
        } finally {
            this.trialClock = null
            this.clearResultTimer()
        }
    }

    shouldRemoveSpentBullet(body, now) {
        const game = body.plugin.game
        if (!game || game.removalTimer) {
            return false
        }

        const config = getBulletConfig(game.bulletType || 'basic')
        game.launchedAt ||= now
        const age = now - game.launchedAt

        if (hasTrajectoryFinished(body, config.radius)) {
            game.removeWithCloud = false
            return true
        }

        if (age >= getDestroyerWallDuration(destroyerTuning.bulletMaxActiveMs)) {
            game.removeWithCloud = true
            return true
        }

        if (age < getDestroyerWallDuration(destroyerTuning.bulletMinActiveMs)) {
            return false
        }

        // Once it has hit something and lost its punch, it can't do anything
        // useful: retire it instead of waiting for it to stop rolling.
        if (game.contacted && Vector.magnitude(body.velocity) < destroyerTuning.bulletRestSpeed) {
            game.removeWithCloud = true
            return true
        }

        return false
    }

    updateCameraFollow() {
        const activeBomb = [...this.physics.bombs.values()][0]
        const launchElapsed = this.physics.cameraLaunchedAt
            ? this.now() - this.physics.cameraLaunchedAt
            : Infinity

        if (activeBomb || launchElapsed < getDestroyerWallDuration(1200)) {
            const flagX = this.activeBuild?.flag?.x ?? WORLD.flagX
            const travel = activeBomb
                ? clamp((activeBomb.position.x - WORLD.launcherX) / Math.max(1, flagX - WORLD.launcherX), 0, 1)
                : 0
            const timedMove = clamp(launchElapsed / getDestroyerWallDuration(900), 0, 1)
            const progress = easeOutCubic(Math.max(travel, timedMove))
            const overview = this.scene.getCameraTargetForBounds({
                minX: WORLD.launcherX - destroyerTuning.maxPullDistance - 1.2,
                maxX: WORLD.buildMaxX + 1.6,
                minY: WORLD.groundY - 0.85,
                maxY: 2.2
            }, {
                padding: 0.6,
                minZoom: 0.78,
                maxZoom: 1.18
            })
            const buildFocus = this.scene.getCameraTargetForBounds({
                minX: WORLD.buildMinX - 0.75,
                maxX: WORLD.buildMaxX + 0.95,
                minY: WORLD.groundY - 0.9,
                maxY: 2.75
            }, {
                padding: 0.45,
                minZoom: 1.12,
                maxZoom: 2.35
            })
            this.scene.setCameraTarget({
                x: lerp(overview.x, buildFocus.x, progress),
                y: clamp(lerp(overview.y, buildFocus.y, progress) + Math.max(0, (activeBomb?.position.y ?? 0) - 2.5) * 0.12, -3.4, 1.4),
                zoom: lerp(overview.zoom, buildFocus.zoom, progress)
            }, { ease: CAMERA_LAUNCH_FOLLOW_EASE })
            return
        }

        if (this.cameraPanOverride) {
            return
        }

        this.scene.frameStageOverview()
    }
}

async function createOpenGraphImage(factory) {
    try {
        return await factory()
    } catch (error) {
        console.warn('Unable to create OpenGraph image', error)
        return null
    }
}

function getDestroyerGravity() {
    const gravity = Number(destroyerTuning.gravity)
    return Number.isFinite(gravity) ? gravity : DESTROYER_GRAVITY
}

function isDestroyerPhysicsStable(physics) {
    if (!physics) {
        return true
    }

    const bodies = [...physics.blockBodies.values()]
    if (physics.flag) {
        bodies.push(physics.flag)
    }

    return bodies.every((body) => body.isSleeping || (
        Vector.magnitude(body.velocity) < DESTROYER_STABLE_SPEED
        && Math.abs(body.angularVelocity) < DESTROYER_STABLE_ANGULAR_SPEED
    ))
}

function getCameraPanBounds(mode) {
    if (mode === 'destroyer') {
        return {
            minX: WORLD.launcherX - destroyerTuning.maxPullDistance - 2.2,
            maxX: WORLD.buildMaxX + 4,
            minY: WORLD.groundY - 4.4,
            maxY: 4.2
        }
    }

    return {
        minX: WORLD.buildMinX - 3.6,
        maxX: WORLD.buildMaxX + 3.6,
        minY: WORLD.groundY - 4.6,
        maxY: WORLD.buildMaxY + 3.2
    }
}

const BULLET_ART = {
    basic: '/art/projectiles/stone-chip.png',
    fire: '/art/projectiles/wood-breaker.png',
    iron: '/art/projectiles/glass-shatter.png',
    explosive: '/art/projectiles/blast-bomb.png'
}

function renderAmmoTokens(inventory, activeType) {
    const order = ['basic', 'fire', 'iron', 'explosive']

    return order.map((key) => {
        const bomb = BOMBS[key]
        const count = inventory[key] || 0
        const isSelected = activeType === key
        const unavailable = count <= 0
        const label = `${bomb.label}: ${count} left. ${formatBulletTarget(bomb)}`

        return `
            <button
                class="v-ammo-token bullet-${key} ${isSelected ? 'is-selected' : ''} ${unavailable ? 'is-unavailable' : ''}"
                data-action="select-bullet"
                data-bullet="${key}"
                type="button"
                ${unavailable ? 'disabled' : ''}
                aria-pressed="${isSelected}"
                aria-label="${escapeAttribute(label)}"
                title="${escapeAttribute(label)}"
                style="${renderBulletArtStyle(key)}"
            >
                <span class="v-ammo-coin"><span class="bullet-art" aria-hidden="true"></span></span>
                <span class="v-ammo-name">${escapeHtml(bomb.shortLabel || bomb.label)}</span>
                <span class="v-ammo-target">${escapeHtml(formatBulletTarget(bomb))}</span>
                <span class="v-ammo-count">${count}</span>
            </button>
        `
    }).join('')
}

function renderBulletArtStyle(key) {
    return `--bullet-art:url('${BULLET_ART[key] || BULLET_ART.basic}')`
}

function renderTuningLauncher(position = null) {
    return `
        <button class="tuning-reopen-button" type="button" data-action="open-tuning-panel" ${formatTuningFixedPositionStyle(position)} aria-label="Open Temp Tuner">
            <i data-lucide="sliders-horizontal" aria-hidden="true"></i>
            <span>Temp Tuner</span>
        </button>
    `
}

function renderTuningPanel(activeTab = 'physics', activeBulletType = 'basic', activeMaterialKey = 'stone', position = null) {
    const tab = TUNING_TABS.includes(activeTab) ? activeTab : 'physics'

    return `
        <aside class="tuning-panel" data-tuning-panel ${formatTuningFixedPositionStyle(position)} aria-label="Temporary tuning controls">
            <header class="tuning-panel-header" data-tuning-drag-handle>
                <div>
                    <p class="eyebrow">Temp tuner</p>
                    <h3>Physics values</h3>
                </div>
                <div class="tuning-panel-actions">
                    <button type="button" data-action="copy-tuning-values">Copy JSON</button>
                    <button type="button" data-action="reset-tuning">Reset</button>
                    <button class="tuning-close-button" type="button" data-action="close-tuning-panel" aria-label="Close Temp Tuner">
                        <i data-lucide="x" aria-hidden="true"></i>
                    </button>
                </div>
            </header>
            <nav class="tuning-tabs" aria-label="Tuning tabs">
                ${TUNING_TABS.map((item) => `
                    <button type="button" data-action="set-tuning-tab" data-tuning-tab="${item}" class="${tab === item ? 'is-selected' : ''}" aria-pressed="${tab === item}">
                        ${formatTuningTabLabel(item)}
                    </button>
                `).join('')}
            </nav>
            <div class="tuning-body">
                ${tab === 'bullets'
                    ? renderBulletTuning(activeBulletType)
                    : tab === 'materials'
                        ? renderMaterialTuning(activeMaterialKey)
                        : renderPhysicsTuning()}
            </div>
        </aside>
    `
}

function formatTuningFixedPositionStyle(position) {
    if (
        !position
        || !Number.isFinite(Number(position.left))
        || !Number.isFinite(Number(position.top))
    ) {
        return ''
    }

    return `style="left:${Math.round(Number(position.left))}px; top:${Math.round(Number(position.top))}px; right:auto; bottom:auto;"`
}

function renderPhysicsTuning() {
    const damageTuning = getDamageTuning()

    return `
        <section class="tuning-group">
            <h4>General physics</h4>
            ${PHYSICS_TUNING_FIELDS.map((field) => renderTuningSlider({
                ...field,
                scope: 'physics',
                key: field.key,
                value: destroyerTuning[field.key]
            })).join('')}
        </section>
        <section class="tuning-group">
            <h4>Sling</h4>
            ${SLING_TUNING_FIELDS.map((field) => renderTuningSlider({
                ...field,
                scope: 'sling',
                key: field.key,
                value: destroyerTuning[field.key]
            })).join('')}
        </section>
        <section class="tuning-group">
            <h4>Damage</h4>
            ${DAMAGE_TUNING_FIELDS.map((field) => renderTuningSlider({
                ...field,
                scope: 'damage',
                key: field.key,
                value: damageTuning[field.key]
            })).join('')}
        </section>
    `
}

function renderBulletTuning(activeBulletType) {
    const bulletType = BOMBS[activeBulletType] ? activeBulletType : 'basic'
    const bullet = BOMBS[bulletType]
    const fields = getTunableNumericEntries(bullet)

    return `
        <section class="tuning-group">
            <h4>Bullet: ${escapeHtml(bullet.label)}</h4>
            <div class="tuning-chip-row" role="group" aria-label="Bullet type">
                ${Object.entries(BOMBS).map(([key, item]) => `
                    <button type="button" data-action="set-tuning-bullet" data-tuning-bullet="${key}" class="${key === bulletType ? 'is-selected' : ''}" aria-pressed="${key === bulletType}">
                        ${escapeHtml(item.shortLabel || item.label)}
                    </button>
                `).join('')}
            </div>
            ${renderGroupedTuningSliders(fields, BULLET_TUNING_GROUPS, (field) => renderTuningSlider({
                ...getTuningRange('bullet', field.path, field.value),
                scope: 'bullet',
                key: field.path,
                path: field.path,
                bullet: bulletType,
                label: field.label,
                value: field.value
            }))}
        </section>
    `
}

function renderMaterialTuning(activeMaterialKey) {
    const materialKey = MATERIALS[activeMaterialKey] ? activeMaterialKey : 'stone'
    const material = MATERIALS[materialKey]
    const fields = getTunableNumericEntries(material)

    return `
        <section class="tuning-group">
            <h4>Material: ${escapeHtml(material.label)}</h4>
            <div class="tuning-chip-row tuning-material-tabs" role="group" aria-label="Material type">
                ${Object.entries(MATERIALS).map(([key, item]) => `
                    <button type="button" data-action="set-tuning-material" data-tuning-material="${key}" class="${key === materialKey ? 'is-selected' : ''}" aria-pressed="${key === materialKey}">
                        ${escapeHtml(item.label)}
                    </button>
                `).join('')}
            </div>
            <div class="tuning-subgroup tuning-linked-controls">
                <h5>Linked controls</h5>
                ${MATERIAL_LINKED_CONTROLS.map((control) => renderTuningSlider({
                    scope: 'materialMacro',
                    key: control.key,
                    path: control.key,
                    material: materialKey,
                    label: control.label,
                    value: getMaterialLinkedControlValue(material, control),
                    min: 0,
                    max: 100,
                    step: 1
                })).join('')}
            </div>
            ${renderGroupedTuningSliders(fields, MATERIAL_TUNING_GROUPS, (field) => renderTuningSlider({
                ...getTuningRange('material', field.path, field.value),
                scope: 'material',
                key: field.path,
                path: field.path,
                material: materialKey,
                label: field.label,
                value: field.value
            }))}
        </section>
    `
}

function renderGroupedTuningSliders(fields, groups, renderField) {
    const used = new Set()
    const sections = groups.map((group) => {
        const groupFields = fields.filter((field) => tuningFieldMatchesGroup(field, group))
        if (!groupFields.length) {
            return ''
        }

        for (const field of groupFields) {
            used.add(field.path)
        }

        return `
            <div class="tuning-subgroup">
                <h5>${escapeHtml(group.title)}</h5>
                ${groupFields.map(renderField).join('')}
            </div>
        `
    })
    const uncategorized = fields.filter((field) => !used.has(field.path))

    if (uncategorized.length) {
        sections.push(`
            <div class="tuning-subgroup">
                <h5>Advanced</h5>
                ${uncategorized.map(renderField).join('')}
            </div>
        `)
    }

    return sections.join('')
}

function tuningFieldMatchesGroup(field, group) {
    return (group.paths || []).includes(field.path)
        || (group.prefixes || []).some((prefix) => field.path.startsWith(prefix))
}

function renderTuningSlider({ scope, key, path = key, bullet = '', material = '', label, value, min, max, step }) {
    const numericValue = Number(value) || 0
    const helpText = getTuningHelpText(scope, path, label)
    const fieldId = createTuningFieldId(scope, path, bullet, material)
    const helpId = `${fieldId}-help`

    return `
        <div class="tuning-slider">
            <span class="tuning-slider-label" id="${escapeAttribute(fieldId)}">${escapeHtml(label)}</span>
            <input
                type="range"
                min="${escapeAttribute(min)}"
                max="${escapeAttribute(max)}"
                step="${escapeAttribute(step)}"
                value="${escapeAttribute(numericValue)}"
                data-tuning-scope="${escapeAttribute(scope)}"
                data-tuning-key="${escapeAttribute(key)}"
                data-tuning-path="${escapeAttribute(path)}"
                ${bullet ? `data-bullet="${escapeAttribute(bullet)}"` : ''}
                ${material ? `data-material="${escapeAttribute(material)}"` : ''}
                aria-labelledby="${escapeAttribute(fieldId)}"
                aria-describedby="${escapeAttribute(helpId)}"
            >
            <span class="tuning-value-cell">
                <output>${formatTuningValue(numericValue)}</output>
                <span class="tuning-info" tabindex="0" aria-label="${escapeAttribute(helpText)}" aria-describedby="${escapeAttribute(helpId)}">
                    <i data-lucide="info" aria-hidden="true"></i>
                    <span class="tuning-tooltip" id="${escapeAttribute(helpId)}" role="tooltip">${escapeHtml(helpText)}</span>
                </span>
            </span>
        </div>
    `
}

function formatTuningTabLabel(tab) {
    const labels = {
        physics: 'Physics',
        bullets: 'Bullets',
        materials: 'Materials'
    }

    return labels[tab] || tab
}

function formatBulletTarget(bullet) {
    const material = MATERIALS[bullet?.targetMaterial || bullet?.bonusAgainst]
    return material ? material.label : 'Any block'
}

function renderAudioControls(state, open = false) {
    const musicValue = Math.round((state.musicVolume ?? 0) * 100)
    const effectsValue = Math.round((state.effectsVolume ?? 0) * 100)
    const muted = Boolean(state.muted)
    const currentLocale = getLocale()

    return `
        <section class="audio-controller ${muted ? 'is-muted' : ''} ${open ? 'is-open' : ''}" aria-label="Audio controls">
            <button class="audio-panel-toggle" data-action="toggle-audio-panel" type="button" aria-expanded="${open}" aria-label="${open ? 'Close audio controls' : 'Open audio controls'}">
                <i class="speaker-icon" data-lucide="${muted ? 'volume-x' : 'volume-2'}" aria-hidden="true"></i>
            </button>
            <button class="audio-close-button" data-action="close-audio-panel" type="button" aria-label="Close audio controls">
                <i data-lucide="x" aria-hidden="true"></i>
            </button>
            <button class="audio-mute-button" data-action="toggle-audio-muted" type="button" aria-pressed="${muted}" aria-label="${muted ? 'Unmute audio' : 'Mute audio'}">
                <i class="speaker-icon" data-lucide="${muted ? 'volume-x' : 'volume-2'}" aria-hidden="true"></i>
                <b>${muted ? 'Muted' : 'Sound'}</b>
            </button>
            <label class="audio-slider">
                <i class="audio-track-icon music-track-icon" data-lucide="music" aria-hidden="true"></i>
                <span>Music</span>
                <input type="range" min="0" max="100" step="1" value="${musicValue}" data-audio-field="musicVolume" aria-label="Music volume">
                <b>${musicValue}</b>
            </label>
            <label class="audio-slider">
                <i class="audio-track-icon fx-track-icon" data-lucide="sparkles" aria-hidden="true"></i>
                <span>FX</span>
                <input type="range" min="0" max="100" step="1" value="${effectsValue}" data-audio-field="effectsVolume" aria-label="Effects volume">
                <b>${effectsValue}</b>
            </label>
            <div class="language-toggle" role="group" aria-label="Language">
                ${getSupportedLocales().map((locale) => `
                    <button type="button" data-action="set-locale" data-locale="${locale.id}" class="${currentLocale === locale.id ? 'is-selected' : ''}" aria-pressed="${currentLocale === locale.id}" title="${locale.label}">
                        ${locale.shortLabel}
                    </button>
                `).join('')}
            </div>
        </section>
    `
}

function getTargetFortressStats(physics, build) {
    const initialMaterialCounts = calculateMaterialCounts(build?.blocks || [])
    const materialCounts = { stone: 0, wood: 0, glass: 0 }
    const bodies = physics?.blockBodies ? [...physics.blockBodies.values()] : []
    const totalBlocks = build?.blockCount ?? (build?.blocks?.length || 0)
    const remainingBlocks = physics?.blockBodies?.size ?? totalBlocks
    const graph = bodies.length ? buildPhysicsGraph(bodies, { floorY: WORLD.groundY }) : null
    const supportedIds = graph ? getSupportedBlockIdsFromGraph(graph) : new Set()

    if (bodies.length) {
        for (const body of bodies) {
            const material = body.plugin.game?.material
            if (materialCounts[material] !== undefined) {
                materialCounts[material] += 1
            }
        }
    } else {
        Object.assign(materialCounts, initialMaterialCounts)
    }

    const stabilityValues = graph
        ? [...graph.nodes.keys()].map((id) => computeStability(id, graph))
        : []
    const averageStability = stabilityValues.length
        ? stabilityValues.reduce((total, value) => total + value, 0) / stabilityValues.length
        : 0
    const healthRatio = getStructureHealthRatio(bodies, build)
    const supportRatio = remainingBlocks > 0 ? supportedIds.size / remainingBlocks : 0
    const displacementIntegrity = getStructureDisplacementIntegrity(bodies)
    const flagState = getFlagRuntimeState(physics, build)
    const structuralIntegrity = clamp(
        healthRatio * 0.36
        + supportRatio * 0.24
        + averageStability * 0.22
        + displacementIntegrity * 0.18,
        0,
        1
    )
    let defenseIntegrity = flagState.captured
        ? 0
        : clamp(structuralIntegrity * 0.44 + flagState.coverScore * 0.56, 0, 1)

    if (flagState.exposed) {
        defenseIntegrity = Math.min(defenseIntegrity, 0.42)
    }

    if (flagState.healthPercent <= FLAG_LOW_HEALTH_THRESHOLD) {
        defenseIntegrity = Math.min(defenseIntegrity, flagState.healthPercent / 100)
    }
    const impactStats = physics?.impactStats || {}
    const totalPhysicsDamage = Math.round(
        (impactStats.directDamage || 0)
        + (impactStats.propagatedDamage || 0)
        + (impactStats.fallDamage || 0)
        + (impactStats.stressDamage || 0)
    )

    return {
        totalBlocks,
        remainingBlocks,
        destroyedBlocks: Math.max(0, totalBlocks - remainingBlocks),
        materialCounts,
        initialMaterialCounts,
        complexity: build?.complexity || 0,
        bombsUsed: physics?.bombsUsed || 0,
        score: physics?.score || 0,
        stabilityPercent: Math.round(averageStability * 100),
        structuralIntegrityPercent: Math.round(structuralIntegrity * 100),
        defenseIntegrityPercent: Math.round(defenseIntegrity * 100),
        flagCoverPercent: Math.round(flagState.coverScore * 100),
        collapseCount: impactStats.collapseCount || 0,
        totalPhysicsDamage,
        lastHit: impactStats.lastHit || 'No hits yet',
        flagHealthPercent: flagState.healthPercent,
        flagCaptured: flagState.captured,
        flagExposed: flagState.exposed
    }
}

function renderTargetStats(stats) {
    const strength = clamp(Math.ceil((stats.defenseIntegrityPercent / 100) * 10), 0, 10)

    return `
        <div class="v-strength" aria-label="Fortress strength ${strength} of 10">
            <span>Walls</span>
            <span class="v-strength-pips" aria-hidden="true">
                ${Array.from({ length: 10 }, (_, index) => `<i class="${index < strength ? 'is-lit' : ''}"></i>`).join('')}
            </span>
        </div>
    `
}

function getStructureHealthRatio(bodies, build) {
    const initialHealth = (build?.blocks || []).reduce((total, block) => total + getBlockMaxHealth(block), 0)

    if (!initialHealth) {
        return bodies.length ? 1 : 0
    }

    if (!bodies.length) {
        return build?.blocks?.length ? 1 : 0
    }

    const currentHealth = bodies.reduce((total, body) => {
        const game = body.plugin.game || {}
        const maxHealth = Number.isFinite(game.maxHealth) ? game.maxHealth : getBlockMaxHealth(game)
        const health = Number.isFinite(game.health) ? game.health : maxHealth
        return total + clamp(health, 0, maxHealth)
    }, 0)

    return clamp(currentHealth / initialHealth, 0, 1)
}

function getStructureDisplacementIntegrity(bodies) {
    if (!bodies.length) {
        return 0
    }

    const scores = bodies.map((body) => {
        const origin = body.plugin.game?.origin
        if (!origin) {
            return 1
        }

        const distance = Math.hypot(body.position.x - origin.x, body.position.y - origin.y)
        const angleDelta = Math.abs(getAngleDelta(body.angle || 0, origin.angle || 0))
        return 1 - clamp((distance / 2.2) + (angleDelta / Math.PI) * 0.62, 0, 1)
    })

    return scores.reduce((total, value) => total + value, 0) / scores.length
}

function getFlagRuntimeState(physics, build) {
    const flag = physics?.flag
    if (!flag) {
        return {
            healthPercent: 0,
            coverScore: 0,
            captured: true,
            exposed: true
        }
    }

    const flagHealth = clamp(Math.round(flag.plugin.game?.health ?? DESTROYER_FLAG_HEALTH), 0, DESTROYER_FLAG_HEALTH)
    const healthPercent = Math.round((flagHealth / DESTROYER_FLAG_HEALTH) * 100)
    const origin = flag.plugin.game?.origin || getRuntimeFlagOrigin(build)
    const displacement = origin ? Math.hypot(flag.position.x - origin.x, flag.position.y - origin.y) : 0
    const angleDelta = Math.abs(getAngleDelta(flag.angle || 0, origin?.angle || 0))
    const outOfPlay = flag.position.y < WORLD.groundY - 1.1
        || displacement > 2.65
        || angleDelta > 1.15
    const captured = flagHealth <= 0 || outOfPlay
    const coverScore = captured ? 0 : getFlagCoverScore(physics, build)
    const exposed = captured
        || coverScore < FLAG_EXPOSED_COVER_THRESHOLD
        || healthPercent <= FLAG_LOW_HEALTH_THRESHOLD
        || displacement > 0.92
        || angleDelta > 0.52

    return {
        healthPercent,
        coverScore,
        captured,
        exposed
    }
}

function getRuntimeFlagOrigin(build) {
    if (!build?.flag) {
        return { x: WORLD.flagX, y: WORLD.flagY, angle: 0 }
    }

    return {
        x: build.flag.x,
        y: build.flag.y + WORLD.groundY - getBuilderFloorTop(),
        angle: 0
    }
}

function getFlagCoverScore(physics, build) {
    const flag = physics?.flag
    const bodies = physics?.blockBodies ? [...physics.blockBodies.values()] : []

    if (!flag || !bodies.length) {
        return 0
    }

    const flagPoint = getFlagGuardPoint(flag)
    const lineStart = getSlingCenter()
    let lineCover = 0
    let localCover = 0

    for (const body of bodies) {
        const game = body.plugin.game || {}
        const bounds = expandBounds(getBodyBounds(body), WORLD.grid * 0.06)
        const maxHealth = Number.isFinite(game.maxHealth) ? game.maxHealth : getBlockMaxHealth(game)
        const health = Number.isFinite(game.health) ? game.health : maxHealth
        const healthFactor = clamp(health / Math.max(1, maxHealth), 0.15, 1)

        if (lineIntersectsBounds(lineStart, flagPoint, bounds)) {
            lineCover += healthFactor
        }

        const center = {
            x: (bounds.minX + bounds.maxX) / 2,
            y: (bounds.minY + bounds.maxY) / 2
        }
        const onAttackSide = bounds.minX <= flagPoint.x + WORLD.grid * 0.28
        const nearFlag = bounds.maxX > flagPoint.x - 1.65 && bounds.minX < flagPoint.x + WORLD.grid * 0.42
        const verticalOverlap = Math.max(
            0,
            Math.min(bounds.maxY, flagPoint.y + FLAG.height * 0.82)
                - Math.max(bounds.minY, flagPoint.y - FLAG.height * 0.56)
        )

        if (onAttackSide && nearFlag && verticalOverlap > WORLD.grid * 0.18) {
            const xProximity = clamp(1 - Math.abs(center.x - flagPoint.x) / 1.65, 0.18, 1)
            const verticalCoverage = clamp(verticalOverlap / FLAG.height, 0.18, 1)
            localCover += healthFactor * ((verticalCoverage * 0.72) + (xProximity * 0.28))
        }
    }

    return clamp(
        clamp(lineCover / 1.85, 0, 1) * 0.68
        + clamp(localCover / 1.9, 0, 1) * 0.32,
        0,
        1
    )
}

function getFlagGuardPoint(flag) {
    return {
        x: flag.position.x + FLAG.width * 0.08,
        y: flag.position.y + FLAG.height * 0.28
    }
}

function expandBounds(bounds, amount) {
    return {
        minX: bounds.minX - amount,
        maxX: bounds.maxX + amount,
        minY: bounds.minY - amount,
        maxY: bounds.maxY + amount
    }
}

function pointInBounds(point, bounds) {
    return point.x >= bounds.minX
        && point.x <= bounds.maxX
        && point.y >= bounds.minY
        && point.y <= bounds.maxY
}

function lineIntersectsBounds(start, end, bounds) {
    const delta = {
        x: end.x - start.x,
        y: end.y - start.y
    }
    let tMin = 0
    let tMax = 1

    for (const axis of ['x', 'y']) {
        const min = axis === 'x' ? bounds.minX : bounds.minY
        const max = axis === 'x' ? bounds.maxX : bounds.maxY
        const origin = start[axis]
        const direction = delta[axis]

        if (Math.abs(direction) < 0.0001) {
            if (origin < min || origin > max) {
                return false
            }
            continue
        }

        const t1 = (min - origin) / direction
        const t2 = (max - origin) / direction
        tMin = Math.max(tMin, Math.min(t1, t2))
        tMax = Math.min(tMax, Math.max(t1, t2))

        if (tMin > tMax) {
            return false
        }
    }

    return tMax >= 0 && tMin <= 1
}

function getAngleDelta(angle, originAngle = 0) {
    let delta = angle - originAngle
    while (delta > Math.PI) {
        delta -= Math.PI * 2
    }
    while (delta < -Math.PI) {
        delta += Math.PI * 2
    }
    return delta
}

function renderAboutModal() {
    return `
        <section class="modal-layer about-layer" role="dialog" aria-modal="true" aria-labelledby="about-title">
            <article class="result-panel about-panel">
                <header class="about-heading">
                    <div>
                        <p class="eyebrow">About</p>
                        <h2 id="about-title">Build & Destroy</h2>
                    </div>
                    <button class="icon-button" data-action="close-about" type="button" aria-label="Close about dialog">Close</button>
                </header>
                <p>
                    Fortress Duel is a responsive browser game built with Codex as the coding partner: design direction, implementation,
                    generated arcade art, and polish all stay editable as regular web code.
                </p>
                <dl class="about-spec-grid">
                    <div><dt>Game engine</dt><dd>Three.js scene rendering with Planck.js (Box2D) physics for block stacks, projectiles, collisions, and flag capture.</dd></div>
                    <div><dt>App shell</dt><dd>Vite, vanilla JavaScript modules, responsive HTML HUDs, pointer controls, and localStorage player identity fallback.</dd></div>
                    <div><dt>Hosting</dt><dd>Node server deployment with Vite assets, API routes, uploaded share images, and local audio delivery.</dd></div>
                    <div><dt>Backend</dt><dd>Neon Postgres with the local server API for public fortress publishing, result recording, image uploads, and leaderboard reads.</dd></div>
                    <div><dt>Art pipeline</dt><dd>OpenAI Image 2 generated no-text fantasy arcade assets for panorama, mode cards, leaderboard, and social preview.</dd></div>
                    <div><dt>Gameplay</dt><dd>Builder budgets, material-specific ammo rewards, destroyer shots, derived target stats, and shareable fortress challenges.</dd></div>
                    <div><dt>Social</dt><dd>OpenGraph/Twitter metadata, copyable links, social fallback image, and public ranks for builders and destroyers.</dd></div>
                    <div><dt>Design goal</dt><dd>Vintage siege feel: parchment, ink and brass interface, a wooden builder workbench, a living backdrop, and compact controls on phones.</dd></div>
                </dl>
                <div class="about-actions">
                    <a class="button-link" href="https://x.com/domenicodd" target="_blank" rel="noreferrer">Follow @domenicodd</a>
                    <button class="primary-button" data-action="close-about" type="button">Back to game</button>
                </div>
            </article>
        </section>
    `
}

function renderClearBuildModal() {
    return `
        <section class="modal-layer builder-clear-layer" role="dialog" aria-modal="true" aria-labelledby="clear-build-title">
            <section class="result-panel builder-clear-panel">
                <p class="eyebrow">Clear build</p>
                <h1 id="clear-build-title">You're about to delete you build</h1>
                <p>This removes every placed block and resets the flag position.</p>
                <div class="result-actions">
                    <button type="button" data-action="cancel-clear-build">Keep building</button>
                    <button class="primary-button danger-button" type="button" data-action="confirm-clear-build">Delete build</button>
                </div>
            </section>
        </section>
    `
}

function renderIdentityFields(nameLabel, displayName, xHandle) {
    return `
        <div class="field-grid">
            <label>
                <span>${nameLabel}</span>
                <input name="displayName" maxlength="40" value="${escapeAttribute(displayName || '')}" placeholder="Your name" autocomplete="name" required>
            </label>
            <label>
                <span>X handle</span>
                <input name="xHandle" maxlength="30" value="${escapeAttribute(xHandle || '')}" placeholder="@optional" autocomplete="off">
            </label>
        </div>
    `
}

function getBuildSummary(build) {
    const blocks = build.blocks || []
    const materialCounts = calculateMaterialCounts(blocks)
    const bombsGranted = build.bombsGranted || calculateBombsGranted(blocks)
    const bombCount = countInventory(bombsGranted)

    return {
        title: build.title || t('defaults.myFortress'),
        blockCount: build.blockCount ?? blocks.length,
        budgetUsed: build.budgetUsed ?? calculateBudgetUsed(blocks),
        complexity: build.complexity ?? blocks.length + bombCount * 2,
        bombCount,
        materialCounts
    }
}

function renderBuildRecap(summary) {
    return `
        <dl class="recap-grid">
            <div><dt>Blocks</dt><dd>${summary.blockCount}</dd></div>
            <div><dt>Budget</dt><dd>${summary.budgetUsed}/${BUILDER_BUDGET}</dd></div>
            <div><dt>Bullets</dt><dd>${summary.bombCount}</dd></div>
            <div><dt>Complexity</dt><dd>${summary.complexity}</dd></div>
            <div><dt>Stone</dt><dd>${summary.materialCounts.stone || 0}</dd></div>
            <div><dt>Wood</dt><dd>${summary.materialCounts.wood || 0}</dd></div>
            <div><dt>Glass</dt><dd>${summary.materialCounts.glass || 0}</dd></div>
        </dl>
    `
}

function renderDestroyerRecap({ build, won, bombsUsed, durationMs, score = 0 }) {
    return `
        <dl class="recap-grid">
            <div><dt>Result</dt><dd>${won ? 'Captured' : 'Held'}</dd></div>
            <div><dt>Score</dt><dd>${formatScore(score)}</dd></div>
            <div><dt>Bullets fired</dt><dd>${bombsUsed}</dd></div>
            <div><dt>Time</dt><dd>${formatDuration(durationMs)}</dd></div>
            <div><dt>Builder</dt><dd>${escapeHtml(build.builderName)}</dd></div>
            <div><dt>Blocks</dt><dd>${build.blockCount}</dd></div>
            <div><dt>Complexity</dt><dd>${build.complexity}</dd></div>
        </dl>
    `
}

function formatDuration(durationMs) {
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
        return '0s'
    }

    return `${Math.max(1, Math.round(durationMs / 1000))}s`
}

function formatScore(score) {
    return Math.max(0, Math.round(Number(score) || 0)).toLocaleString(getLocaleInfo().htmlLang)
}

function calculateBlockDestroyScore(block) {
    const material = block?.material || 'wood'
    const base = BLOCK_SCORE_BASE[material] || 80
    const width = Number(block?.width || BLOCK_SIZE.width)
    const height = Number(block?.height || BLOCK_SIZE.height)
    const gridArea = Math.max(0.25, (width * height) / (WORLD.grid * WORLD.grid))
    const areaFactor = clamp(Math.sqrt(gridArea / 4), 0.42, 1.35)
    const healthFactor = clamp((Number(block?.maxHealth) || getBlockMaxHealth(block)) / 100, 0.45, 1.8)

    return Math.max(25, Math.round(base * areaFactor * (0.72 + healthFactor * 0.28)))
}

function renderGalleryShell(filter, builds, status, aboutOpen = false) {
    const storageStatus = isBackendConfigured() ? 'backend: connected' : 'backend: local demo'
    const filterItems = ['all', 'low', 'medium', 'high']

    return `
        <main class="gallery-panel selection-panel">
            <header class="gallery-top-bar" aria-label="Destroyer gallery">
                <button class="gallery-home-button" data-action="home" type="button" aria-label="Back home">
                    <i class="gallery-home-icon" data-lucide="home" aria-hidden="true"></i>
                    <b>Home</b>
                </button>
                <div class="gallery-title-panel">
                    <p class="eyebrow">Destroyer gallery</p>
                    <h2>Choose a fortress</h2>
                </div>
            </header>
            <section class="filter-row gallery-filter-row" aria-label="Complexity">
                ${filterItems.map((item) => `
                    <button class="gallery-filter-button${filter === item ? ' is-selected' : ''}" data-action="set-filter" data-filter="${item}" type="button" aria-pressed="${filter === item}">
                        <span>${item}</span>
                    </button>
                `).join('')}
            </section>
            <section class="build-grid">
                ${builds.map((build) => `
                    <article class="build-card">
                        ${renderBuildDifficultyBadge(build)}
                        ${renderBuildPreview(build)}
                        <div class="build-card-copy">
                            <p class="eyebrow">${build.source === 'demo' ? 'Demo' : build.source === 'local' ? 'Local' : 'Public'}</p>
                            <h3>${escapeHtml(build.title)}</h3>
                            <p>by ${escapeHtml(build.builderName)}${build.xHandle ? ` / @${escapeHtml(build.xHandle)}` : ''}</p>
                            <p class="build-id-row"><span>ID</span><code>${escapeHtml(build.id)}</code></p>
                        </div>
                        <dl class="card-stats">
                            <div><dt><i class="stat-icon stat-blocks" data-lucide="blocks" aria-hidden="true"></i><span>Blocks</span></dt><dd>${build.blockCount}</dd></div>
                            <div><dt><i class="stat-icon stat-bullets" data-lucide="bomb" aria-hidden="true"></i><span>Bullets</span></dt><dd>${countInventory(build.bombsGranted)}</dd></div>
                            <div><dt><i class="stat-icon stat-wins" data-lucide="trophy" aria-hidden="true"></i><span>Wins</span></dt><dd>${build.winCount}/${build.playCount}</dd></div>
                        </dl>
                        <button class="primary-button gallery-attack-button" data-action="load-build" data-build-id="${build.id}" type="button">
                            <i data-lucide="swords" aria-hidden="true"></i>
                            <span>Attack</span>
                        </button>
                    </article>
                `).join('')}
            </section>
            <footer class="home-footer gallery-footer" aria-label="Build and service information">
                <span>${storageStatus}</span>
                <span>vibe coded by <a href="https://x.com/domenicodd" target="_blank" rel="noreferrer">@domenicodd</a></span>
                <button class="footer-link" data-action="show-about" type="button">About</button>
            </footer>
            ${status ? `<p class="toast inline">${status}</p>` : ''}
            ${aboutOpen ? renderAboutModal() : ''}
        </main>
    `
}

function renderBuildDifficultyBadge(build) {
    const difficulty = getBuildDifficulty(build)

    return `
        <span class="build-difficulty-badge gallery-filter-${difficulty.id}">
            <span>${difficulty.id}</span>
        </span>
    `
}

function getBuildDifficulty(build) {
    const complexity = Number(build?.complexity) || 0

    if (complexity >= 25) {
        return { id: 'high' }
    }

    if (complexity >= 13) {
        return { id: 'medium' }
    }

    return { id: 'low' }
}

function renderBuildPreview(build) {
    const previewImageUrl = normalizeBuildPreviewUrl(build.previewImageUrl)

    if (previewImageUrl) {
        return `
            <figure class="build-preview">
                <img src="${escapeAttribute(previewImageUrl)}" alt="${escapeAttribute(`${build.title} fortress preview`)}" loading="lazy" decoding="async" referrerpolicy="no-referrer">
            </figure>
        `
    }

    return `
        <figure class="build-preview build-preview-generated" role="img" aria-label="${escapeAttribute(`${build.title} fortress preview`)}">
            <span class="build-preview-zone">
                ${(build.blocks || []).map(renderBuildPreviewBlock).join('')}
                ${renderBuildPreviewFlag(build.flag)}
            </span>
        </figure>
    `
}

function renderBuildPreviewBlock(block) {
    const material = MATERIALS[block.material] || MATERIALS.wood
    const style = [
        getBuildPreviewObjectStyle(block),
        `--block-color:${toCssHexColor(material.color)}`,
        `--block-accent:${material.accent}`
    ].join(';')

    return `<span class="build-preview-block" style="${style}"></span>`
}

function renderBuildPreviewFlag(flag = { x: WORLD.flagX, y: WORLD.flagY }) {
    const style = getBuildPreviewObjectStyle({
        x: flag.x,
        y: flag.y,
        width: FLAG.width,
        height: FLAG.height,
        rotation: 0
    })

    return `<span class="build-preview-flag" style="${style}"><i></i></span>`
}

function getBuildPreviewObjectStyle(item) {
    const zoneWidth = WORLD.buildMaxX - WORLD.buildMinX
    const zoneHeight = WORLD.buildMaxY - WORLD.buildMinY
    const width = item.width || BLOCK_SIZE.width
    const height = item.height || BLOCK_SIZE.height
    const left = ((item.x - width / 2 - WORLD.buildMinX) / zoneWidth) * 100
    const top = ((WORLD.buildMaxY - (item.y + height / 2)) / zoneHeight) * 100

    return [
        `left:${roundTo(left, 3)}%`,
        `top:${roundTo(top, 3)}%`,
        `width:${roundTo((width / zoneWidth) * 100, 3)}%`,
        `height:${roundTo((height / zoneHeight) * 100, 3)}%`,
        `transform:rotate(${roundTo(item.rotation || 0, 4)}rad)`
    ].join(';')
}

function normalizeBuildPreviewUrl(value) {
    const url = String(value || '').trim()

    if (/^data:image\/(?:png|jpeg|jpg|webp);base64,/i.test(url)) {
        return url
    }

    try {
        const parsed = new URL(url)
        return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : ''
    } catch {
        return ''
    }
}

function renderScreenHomeButton() {
    return `
        <button class="screen-home-button" data-action="home" type="button" aria-label="Go home">
            <i class="screen-home-icon" data-lucide="home" aria-hidden="true"></i>
            <b>Home</b>
        </button>
    `
}

function renderLeaderboardsShell(leaderboards, status, activeFilter = 'all', expandedBoards = {}) {
    const filteredLeaderboards = filterLeaderboardsForTab(leaderboards, activeFilter)
    const buildersExpanded = Boolean(expandedBoards.builders)
    const destroyersExpanded = Boolean(expandedBoards.destroyers)
    const builderRows = buildersExpanded
        ? filteredLeaderboards.builders
        : filteredLeaderboards.builders.slice(0, LEADERBOARD_PREVIEW_ROWS)
    const destroyerRows = destroyersExpanded
        ? filteredLeaderboards.destroyers
        : filteredLeaderboards.destroyers.slice(0, LEADERBOARD_PREVIEW_ROWS)

    return `
        <main class="leaderboard-panel">
            <header class="leaderboard-top-rail">
                <button class="leaderboard-home-button" data-action="start-gallery" aria-label="Builds">
                    <i class="leaderboard-home-icon" data-lucide="arrow-left" aria-hidden="true"></i>
                    <b>Builds</b>
                </button>
                <div class="leaderboard-title-stack">
                    <p class="eyebrow">Leaderboards</p>
                    <h2>Who holds. Who breaks.</h2>
                </div>
            </header>
            <section class="leaderboard-filter-row" aria-label="Leaderboard filters">
                ${renderLeaderboardFilterTabs(activeFilter)}
            </section>
            <section class="leaderboard-grid">
                <article class="leaderboard-board leaderboard-board-builders">
                    ${renderLeaderboardBoardHeading('builders')}
                    ${renderLeaderboardStatLabels(['Holds', 'Attacks', 'Builds'])}
                    <div class="leader-list">
                        ${builderRows.length
                            ? builderRows.map((builder, index) => renderBuilderLeaderRow(builder, index)).join('')
                            : '<p class="leader-empty">No builders ranked yet.</p>'}
                    </div>
                    ${renderLeaderboardSeeAllButton('builders', filteredLeaderboards.builders.length, buildersExpanded, 'blue')}
                </article>
                <article class="leaderboard-board leaderboard-board-destroyers">
                    ${renderLeaderboardBoardHeading('destroyers')}
                    ${renderLeaderboardStatLabels(['Wins', 'Best', 'Runs'])}
                    <div class="leader-list">
                        ${destroyerRows.length
                            ? destroyerRows.map((destroyer, index) => renderDestroyerLeaderRow(destroyer, index)).join('')
                            : '<p class="leader-empty">No destroyer wins yet.</p>'}
                    </div>
                    ${renderLeaderboardSeeAllButton('destroyers', filteredLeaderboards.destroyers.length, destroyersExpanded, 'red')}
                </article>
            </section>
            ${status ? `<p class="toast inline">${status}</p>` : ''}
        </main>
    `
}

function renderBuilderLeaderRow(builder, index) {
    return `
        <div class="leader-row leader-row-builder">
            <b class="leader-rank ${getRankTierClass(index)}"><span>${index + 1}</span></b>
            <div class="leader-name">
                <strong>${escapeHtml(builder.name)}</strong>
                ${renderLeaderHandle(builder.xHandle, 'Builder')}
            </div>
            <dl class="leader-stats">
                <div><dt>Holds</dt><dd>${builder.holds}</dd></div>
                <div><dt>Attacks</dt><dd>${builder.attemptsReceived}</dd></div>
                <div><dt>Builds</dt><dd>${builder.buildsPublished}</dd></div>
            </dl>
        </div>
    `
}

function renderDestroyerLeaderRow(destroyer, index) {
    const bestBombs = destroyer.bestBombs === null || destroyer.bestBombs === undefined
        ? '-'
        : destroyer.bestBombs

    return `
        <div class="leader-row leader-row-destroyer">
            <b class="leader-rank ${getRankTierClass(index)}"><span>${index + 1}</span></b>
            <div class="leader-name">
                <strong>${escapeHtml(destroyer.name)}</strong>
                ${renderLeaderHandle(destroyer.xHandle, 'Destroyer')}
            </div>
            <dl class="leader-stats">
                <div><dt>Wins</dt><dd>${destroyer.wins}</dd></div>
                <div><dt>Best</dt><dd>${bestBombs}</dd></div>
                <div><dt>Runs</dt><dd>${destroyer.attempts}</dd></div>
            </dl>
        </div>
    `
}

function renderLeaderHandle(xHandle, fallbackLabel) {
    const displayHandle = String(xHandle || '').trim().replace(/^@+/, '')
    const safeHandle = getSafeXHandle(displayHandle)

    if (!displayHandle) {
        return `<span>${fallbackLabel}</span>`
    }

    if (!safeHandle) {
        return `<span>@${escapeHtml(displayHandle)}</span>`
    }

    return `<a class="leader-x-link" href="https://x.com/${escapeAttribute(safeHandle)}" target="_blank" rel="noopener noreferrer">@${escapeHtml(safeHandle)}</a>`
}

function getSafeXHandle(value) {
    const handle = String(value || '').trim().replace(/^@+/, '')
    return /^[A-Za-z0-9_]{1,30}$/.test(handle) ? handle : ''
}

function renderLeaderboardFilterTabs(activeFilter) {
    return LEADERBOARD_FILTERS.map((filter) => `
        <button class="leaderboard-filter-button ${activeFilter === filter ? 'is-selected' : ''}" data-action="set-leaderboard-filter" data-filter="${filter}" type="button" aria-pressed="${activeFilter === filter}">
            <span class="leaderboard-filter-icon leaderboard-filter-${filter}" aria-hidden="true"></span>
            <b>${getLeaderboardFilterLabel(filter)}</b>
        </button>
    `).join('')
}

function renderLeaderboardBoardHeading(board) {
    const isBuilder = board === 'builders'

    return `
        <header class="leaderboard-heading">
            <span class="leaderboard-standard ${isBuilder ? 'builder-standard' : 'destroyer-standard'}" aria-hidden="true">
                <i data-lucide="${isBuilder ? 'castle' : 'swords'}" aria-hidden="true"></i>
            </span>
            <div>
                <p class="eyebrow">${isBuilder ? 'Builder board' : 'Destroyer board'}</p>
                <h3>${isBuilder ? 'Best defenses' : 'Cleanest captures'}</h3>
            </div>
        </header>
    `
}

function renderLeaderboardStatLabels(labels) {
    return `
        <div class="leaderboard-stat-labels" aria-hidden="true">
            ${labels.map((label) => `<span>${label}</span>`).join('')}
        </div>
    `
}

function renderLeaderboardSeeAllButton(board, rowCount, expanded, tone) {
    if (!rowCount) {
        return ''
    }

    return `
        <button class="leaderboard-see-all ${tone === 'red' ? 'is-red' : 'is-blue'}" data-action="toggle-leaderboard-board" data-board="${board}" type="button" aria-expanded="${expanded}">
            <span>${expanded ? 'Show less' : 'See all'}</span>
            <i data-lucide="arrow-right" aria-hidden="true"></i>
        </button>
    `
}

function getLeaderboardFilterLabel(filter) {
    const labels = {
        all: 'All',
        low: 'Low',
        medium: 'Medium',
        high: 'High'
    }

    return labels[filter] || labels.all
}

function getRankTierClass(index) {
    if (index === 0) {
        return 'rank-gold'
    }

    if (index === 1) {
        return 'rank-silver'
    }

    if (index === 2) {
        return 'rank-bronze'
    }

    return 'rank-iron'
}

function filterLeaderboardsForTab(leaderboards, activeFilter) {
    const builders = Array.isArray(leaderboards.builders) ? leaderboards.builders : []
    const destroyers = Array.isArray(leaderboards.destroyers) ? leaderboards.destroyers : []

    if (activeFilter === 'low') {
        return {
            builders: builders.filter((builder) => Number(builder.avgComplexity || 0) <= 12),
            destroyers
        }
    }

    if (activeFilter === 'medium') {
        return {
            builders: builders.filter((builder) => {
                const complexity = Number(builder.avgComplexity || 0)
                return complexity >= 13 && complexity <= 24
            }),
            destroyers
        }
    }

    if (activeFilter === 'high') {
        return {
            builders: builders.filter((builder) => Number(builder.avgComplexity || 0) >= 25),
            destroyers
        }
    }

    return { builders, destroyers }
}

function renderMaterialSlots(selectedMaterial, materialCounts, selectedShape = DEFAULT_BLOCK_SHAPE) {
    return Object.entries(MATERIALS).map(([key, material]) => {
        const blockCost = getBlockCost({ material: key, shape: selectedShape })

        return `
        <button class="hotbar-slot material-slot material-${key} ${selectedMaterial === key ? 'is-selected' : ''}" data-action="select-material" data-material="${key}" style="--accent:${material.accent}">
            <span class="slot-swatch"></span>
            <span>${material.label}</span>
            <small>${blockCost} pts / Wt ${formatMaterialWeight(material)}</small>
            <b>${materialCounts[key]}</b>
        </button>
    `
    }).join('')
}

function renderShapeButtons(selectedShape, selectedMaterial = 'wood') {
    const material = MATERIALS[selectedMaterial] || MATERIALS.wood

    return Object.entries(BLOCK_SHAPES).map(([key, shape]) => `
        <button class="hotbar-slot shape-slot material-${selectedMaterial} ${selectedShape === key ? 'is-selected' : ''}" data-action="select-shape" data-shape="${key}" data-material="${selectedMaterial}" style="--accent:${material.accent}">
            <span class="shape-preview" style="--shape-w:${shape.gridWidth}; --shape-h:${shape.gridHeight}"></span>
            <span>${shape.label}</span>
            <small>${material.label} / ${formatGridSize(shape)} / ${getBlockCost({ material: selectedMaterial, shape: key })} pts</small>
        </button>
    `).join('')
}

function formatGridSize(shape) {
    return `${shape.gridWidth}x${shape.gridHeight}`
}

function formatMaterialWeight(material) {
    return Math.round(material.density * 1000)
}

function toCssHexColor(value) {
    return `#${Number(value || 0).toString(16).padStart(6, '0').slice(-6)}`
}

function getBlockShape(shapeKey) {
    return BLOCK_SHAPES[shapeKey] || BLOCK_SHAPES[DEFAULT_BLOCK_SHAPE]
}

function makeBuilderBlock(material, shapeKey, x, y, rotation = 0) {
    const shape = getBlockShape(shapeKey)

    return {
        id: createId('block'),
        material,
        shape: shapeKey,
        x,
        y,
        width: shape.width,
        height: shape.height,
        rotation
    }
}

function createBuilderLayoutSnapshot({ blocks, flag, selectedBlockId = null, selectedFlag = false }) {
    const flagX = Number(flag?.x)
    const flagY = Number(flag?.y)

    return {
        blocks: cloneBuilderBlocks(blocks),
        flag: {
            x: Number.isFinite(flagX) ? flagX : WORLD.flagX,
            y: Number.isFinite(flagY) ? flagY : WORLD.flagY
        },
        selectedBlockId,
        selectedFlag
    }
}

function cloneBuilderBlocks(blocks) {
    return (blocks || []).map((block) => ({ ...block }))
}

function cloneBuildBlocksForBuilder(blocks) {
    return cloneBuilderBlocks(blocks).map((block) => ({
        ...block,
        id: createId('block')
    }))
}

function waitForAnimationTime(durationMs) {
    return new Promise((resolve) => {
        const startedAt = performance.now()
        const waitFrame = () => {
            if (performance.now() - startedAt >= durationMs) {
                resolve()
                return
            }

            requestAnimationFrame(waitFrame)
        }

        requestAnimationFrame(waitFrame)
    })
}

function inferBlockShape(block) {
    const width = block.width || BLOCK_SIZE.width
    const height = block.height || BLOCK_SIZE.height
    const match = Object.entries(BLOCK_SHAPES).find(([, shape]) => (
        Math.abs(shape.width - width) < 0.001
        && Math.abs(shape.height - height) < 0.001
    ))

    return match?.[0] || DEFAULT_BLOCK_SHAPE
}

function firstAvailableBomb(inventory, preferred = null) {
    if (preferred && inventory[preferred] > 0) {
        return preferred
    }

    return Object.keys(BOMBS).find((key) => inventory[key] > 0) || 'basic'
}

function countInventory(inventory) {
    return Object.values(inventory || {}).reduce((total, amount) => total + amount, 0)
}

function findBlockAtPoint(blocks, point) {
    return [...blocks].reverse().find((block) => {
        const width = block.width || BLOCK_SIZE.width
        const height = block.height || BLOCK_SIZE.height
        const hitPadding = getBlockHitPadding(block)
        const cos = Math.cos(-(block.rotation || 0))
        const sin = Math.sin(-(block.rotation || 0))
        const dx = point.x - block.x
        const dy = point.y - block.y
        const localX = dx * cos - dy * sin
        const localY = dx * sin + dy * cos
        return Math.abs(localX) <= width / 2 + hitPadding.x
            && Math.abs(localY) <= height / 2 + hitPadding.y
    })
}

function getBlockHitPadding(block) {
    const height = block.height || BLOCK_SIZE.height
    const isBeam = block.shape === 'beam'
    const minHeight = isBeam ? BUILDER_BEAM_HIT_MIN_HEIGHT : Math.min(height, WORLD.grid * 0.35)

    return {
        x: isBeam ? WORLD.grid * 0.08 : 0,
        y: Math.max(0, (minHeight - height) / 2)
    }
}

function isPointOnFlag(flag, point) {
    return point.x >= flag.x - FLAG.width / 2
        && point.x <= flag.x + FLAG.width / 2
        && point.y >= flag.y - FLAG.height / 2
        && point.y <= flag.y + FLAG.height / 2
}

function clampFlagPosition(point) {
    const halfWidth = FLAG.width / 2
    const halfHeight = FLAG.height / 2
    const xMin = WORLD.buildMinX + halfWidth
    const xMax = WORLD.buildMaxX - halfWidth
    const yMin = WORLD.buildMinY + halfHeight
    const yMax = WORLD.buildMaxY - halfHeight

    return {
        x: snapToLaneInRange(point.x, xMin, BUILDER_SNAP_STEP, xMin, xMax),
        y: snapToLaneInRange(point.y, yMin, BUILDER_SNAP_STEP, yMin, yMax)
    }
}

function isInsideBuildZone(point) {
    return point.x >= WORLD.buildMinX
        && point.x <= WORLD.buildMaxX
        && point.y >= WORLD.buildMinY
        && point.y <= WORLD.buildMaxY
}

function snapBlockPosition(point, rotation = 0) {
    const width = point.width || BLOCK_SIZE.width
    const height = point.height || BLOCK_SIZE.height
    const footprint = getBlockFootprint({ width, height, rotation })
    const xMin = WORLD.buildMinX + footprint.width / 2
    const xMax = WORLD.buildMaxX - footprint.width / 2
    const yMin = WORLD.buildMinY + footprint.height / 2
    const yMax = WORLD.buildMaxY - footprint.height / 2

    return {
        x: snapToLaneInRange(point.x, xMin, BUILDER_SNAP_STEP, xMin, xMax),
        y: snapToLaneInRange(point.y, yMin, BUILDER_SNAP_STEP, yMin, yMax)
    }
}

function getBuilderDragBlockTarget(point, block, blocks, ignoreId = null) {
    if (shouldUseCreativeBlockDrag(block)) {
        const rotation = block.rotation || 0
        const target = clampBlockPositionToBuildZone({
            ...block,
            x: point.x,
            y: point.y,
            rotation
        })

        return isBlockPlacementValid({ ...block, ...target }, blocks, ignoreId)
            ? target
            : snapBlockPosition(point, rotation)
    }

    return snapBlockPosition(point, block.rotation || 0)
}

function shouldUseCreativeBlockDrag(block) {
    const rotation = block.rotation || 0
    return block.shape === 'beam'
        || Math.abs(Math.sin(rotation * 2)) > BUILDER_CREATIVE_DRAG_ANGLE_EPSILON
}

function clampBlockPositionToBuildZone(block) {
    const bounds = getBlockBounds(block)
    let x = block.x
    let y = block.y

    if (bounds.minX < WORLD.buildMinX) {
        x += WORLD.buildMinX - bounds.minX
    } else if (bounds.maxX > WORLD.buildMaxX) {
        x += WORLD.buildMaxX - bounds.maxX
    }

    if (bounds.minY < WORLD.buildMinY) {
        y += WORLD.buildMinY - bounds.minY
    } else if (bounds.maxY > WORLD.buildMaxY) {
        y += WORLD.buildMaxY - bounds.maxY
    }

    return {
        x: roundTo(x, 4),
        y: roundTo(y, 4)
    }
}

function snapToLaneInRange(value, origin, step, min, max) {
    const minIndex = Math.ceil((min - origin) / step - 0.0001)
    const maxIndex = Math.floor((max - origin) / step + 0.0001)
    const targetIndex = Math.round((clamp(value, min, max) - origin) / step)
    const index = clamp(targetIndex, minIndex, maxIndex)

    return roundTo(origin + index * step, 4)
}

// Lowers a snapped block until it rests on the highest surface beneath it:
// the build floor or the top of a block it overlaps horizontally. The x
// position is left on the grid and the block never moves up.
function dropBlockOnSurface(block, blocks, ignoreId = null) {
    const bounds = getBlockBounds(block)
    const halfHeight = (bounds.maxY - bounds.minY) / 2
    const restHeights = [getBuilderFloorTop() + halfHeight]

    for (const other of blocks) {
        if (other.id === ignoreId) {
            continue
        }

        const otherBounds = getBlockBounds(other)
        if (otherBounds.maxX <= bounds.minX + BUILDER_LAYOUT_EPSILON || otherBounds.minX >= bounds.maxX - BUILDER_LAYOUT_EPSILON) {
            continue
        }

        restHeights.push(otherBounds.maxY + halfHeight)
    }

    const candidates = restHeights
        .map((y) => roundTo(y, 4))
        .filter((y) => y <= block.y + BUILDER_LAYOUT_EPSILON)
        .sort((a, b) => b - a)
    const resting = candidates.find((y) => isBlockPlacementValid({ ...block, y }, blocks, ignoreId))

    return { x: block.x, y: resting ?? block.y }
}

function isSameBuilderLayout(a, b) {
    return a.blocks.length === b.blocks.length
        && Math.hypot(a.flag.x - b.flag.x, a.flag.y - b.flag.y) <= BUILDER_LAYOUT_EPSILON
        && a.blocks.every((block, index) => {
            const other = b.blocks[index]
            return block.id === other.id
                && Math.hypot(block.x - other.x, block.y - other.y) <= BUILDER_LAYOUT_EPSILON
                && Math.abs((block.rotation || 0) - (other.rotation || 0)) <= BUILDER_LAYOUT_EPSILON
        })
}

function snapAngle(value) {
    return Math.round(value / ROTATION_STEP) * ROTATION_STEP
}

function getBuilderFloorTop() {
    return WORLD.buildMinY
}

function findOpenBlockPosition(block, blocks) {
    const footprint = getBlockFootprint(block)
    const xMin = WORLD.buildMinX + footprint.width / 2
    const xMax = WORLD.buildMaxX - footprint.width / 2
    const yMin = WORLD.buildMinY + footprint.height / 2
    const yMax = WORLD.buildMaxY - footprint.height / 2
    const candidates = []

    for (let y = yMin; y <= yMax + 0.001; y += BUILDER_SNAP_STEP) {
        for (let x = xMin; x <= xMax + 0.001; x += BUILDER_SNAP_STEP) {
            const candidate = {
                ...block,
                x: roundTo(x, 4),
                y: roundTo(y, 4)
            }

            if (isBlockPlacementValid(candidate, blocks, block.id)) {
                candidates.push(candidate)
            }
        }
    }

    if (!candidates.length) {
        return null
    }

    candidates.sort((a, b) => (
        Math.hypot(a.x - block.x, a.y - block.y)
        - Math.hypot(b.x - block.x, b.y - block.y)
    ))

    return {
        x: candidates[0].x,
        y: candidates[0].y
    }
}

function repairBuildLayout(blocks) {
    const repaired = []
    let changed = false

    for (const block of blocks) {
        let candidate = normalizeBuilderBlockGeometry(block)

        if (!isBlockPlacementValid(candidate, repaired)) {
            const snapped = {
                ...candidate,
                ...snapBlockPosition(candidate, candidate.rotation || 0, repaired)
            }

            if (isBlockPlacementValid(snapped, repaired)) {
                candidate = snapped
            } else {
                const openPosition = findOpenBlockPosition(snapped, repaired)
                    || findOpenBlockPosition(candidate, repaired)

                if (openPosition) {
                    candidate = {
                        ...candidate,
                        ...openPosition
                    }
                }
            }
        }

        changed ||= hasBlockLayoutChanged(block, candidate)
        repaired.push(candidate)
    }

    return {
        blocks: repaired,
        changed,
        ok: isBuildLayoutValid(repaired)
    }
}

function normalizeBuilderBlockGeometry(block) {
    const shapeKey = block.shape && BLOCK_SHAPES[block.shape]
        ? block.shape
        : inferBlockShape(block)
    const shape = getBlockShape(shapeKey)
    const x = Number(block.x)
    const y = Number(block.y)
    const rotation = Number(block.rotation || 0)

    return {
        ...block,
        shape: shapeKey,
        width: shape.width,
        height: shape.height,
        x: Number.isFinite(x) ? x : WORLD.flagX,
        y: Number.isFinite(y) ? y : WORLD.flagY,
        rotation: Number.isFinite(rotation) ? rotation : 0
    }
}

function hasBlockLayoutChanged(source, candidate) {
    return source.shape !== candidate.shape
        || hasNumberChanged(source.width ?? candidate.width, candidate.width)
        || hasNumberChanged(source.height ?? candidate.height, candidate.height)
        || hasNumberChanged(source.x, candidate.x)
        || hasNumberChanged(source.y, candidate.y)
        || hasNumberChanged(source.rotation || 0, candidate.rotation)
}

function hasNumberChanged(sourceValue, candidateValue) {
    const sourceNumber = Number(sourceValue)

    return !Number.isFinite(sourceNumber)
        || Math.abs(sourceNumber - candidateValue) > 0.001
}

function isBuildLayoutValid(blocks) {
    return blocks.every((block, index) => (
        isBlockInsideBuildZone(block)
        && !blocks.slice(index + 1).some((other) => blocksOverlap(block, other))
    ))
}

function isBlockPlacementValid(block, blocks, ignoreId = null) {
    return isBlockInsideBuildZone(block)
        && !blocks.some((other) => other.id !== ignoreId && blocksOverlap(block, other))
}

function isBlockInsideBuildZone(block) {
    const corners = getBlockCorners(block)

    return corners.every((corner) => (
        corner.x >= WORLD.buildMinX - 0.001
        && corner.x <= WORLD.buildMaxX + 0.001
        && corner.y >= WORLD.buildMinY - 0.001
        && corner.y <= WORLD.buildMaxY + 0.001
    ))
}

function blocksOverlap(a, b) {
    const polygonA = getBlockCorners(a)
    const polygonB = getBlockCorners(b)
    const axes = [...getPolygonAxes(polygonA), ...getPolygonAxes(polygonB)]

    return axes.every((axis) => {
        const projectionA = projectPolygon(polygonA, axis)
        const projectionB = projectPolygon(polygonB, axis)
        return projectionA.max > projectionB.min + 0.001
            && projectionB.max > projectionA.min + 0.001
    })
}

function getBlockCorners(block) {
    const width = block.width || BLOCK_SIZE.width
    const height = block.height || BLOCK_SIZE.height
    const rotation = block.rotation || 0
    const cos = Math.cos(rotation)
    const sin = Math.sin(rotation)
    const halfWidth = width / 2
    const halfHeight = height / 2

    return [
        { x: -halfWidth, y: -halfHeight },
        { x: halfWidth, y: -halfHeight },
        { x: halfWidth, y: halfHeight },
        { x: -halfWidth, y: halfHeight }
    ].map((corner) => ({
        x: block.x + corner.x * cos - corner.y * sin,
        y: block.y + corner.x * sin + corner.y * cos
    }))
}

function getBlockBounds(block) {
    const corners = getBlockCorners(block)

    return corners.reduce(
        (bounds, corner) => ({
            minX: Math.min(bounds.minX, corner.x),
            maxX: Math.max(bounds.maxX, corner.x),
            minY: Math.min(bounds.minY, corner.y),
            maxY: Math.max(bounds.maxY, corner.y)
        }),
        {
            minX: Infinity,
            maxX: -Infinity,
            minY: Infinity,
            maxY: -Infinity
        }
    )
}

function getBodyBounds(body) {
    return {
        minX: body.bounds.min.x,
        maxX: body.bounds.max.x,
        minY: body.bounds.min.y,
        maxY: body.bounds.max.y
    }
}

function getBuilderPhysicsSyncedBlock(block, body, blocks) {
    let candidate = {
        ...block,
        x: body.position.x,
        y: body.position.y,
        rotation: body.angle || 0
    }

    const snappedRotation = snapAngle(candidate.rotation)
    if (Math.abs(getAngleDelta(candidate.rotation, snappedRotation)) <= BUILDER_PHYSICS_ANGLE_SNAP_EPSILON) {
        candidate.rotation = snappedRotation
    }

    const snappedPosition = snapBlockPosition(candidate, candidate.rotation)
    if (Math.hypot(candidate.x - snappedPosition.x, candidate.y - snappedPosition.y) <= BUILDER_PHYSICS_GRID_SNAP_EPSILON) {
        candidate = {
            ...candidate,
            ...snappedPosition
        }
    }

    return isBlockPlacementValid(candidate, blocks, block.id)
        ? candidate
        : {
            x: body.position.x,
            y: body.position.y,
            rotation: body.angle || 0
        }
}

function analyzeBuilderPublishStability({ physics, snapshot, blocks }) {
    if (!snapshot?.blocks?.length) {
        return { ok: true }
    }

    const blockById = new Map((blocks || []).map((block) => [block.id, block]))
    const savedBlockIds = new Set(snapshot.blocks.map((block) => block.id))
    const flagHealth = physics?.flag?.plugin?.game?.health

    if (
        blockById.size !== savedBlockIds.size
        || !Number.isFinite(flagHealth)
        || flagHealth < DESTROYER_FLAG_HEALTH - BUILDER_PUBLISH_FLAG_FULL_HEALTH_EPSILON
    ) {
        return { ok: false }
    }

    for (const savedBlock of snapshot.blocks) {
        const body = physics?.bodies?.get(savedBlock.id)
        const block = blockById.get(savedBlock.id)

        if (!block || !body) {
            return { ok: false }
        }

        const game = body.plugin.game
        const maxHealth = Number.isFinite(game?.maxHealth) && game.maxHealth > 0
            ? game.maxHealth
            : getBlockMaxHealth(block)
        const health = Number.isFinite(game?.health) ? game.health : maxHealth
        const healthRatio = clamp(health / maxHealth, 0, 1)

        if (healthRatio < BUILDER_PUBLISH_MIN_HEALTH_RATIO) {
            return { ok: false }
        }

        const drift = Math.hypot(body.position.x - savedBlock.x, body.position.y - savedBlock.y)
        const tilt = Math.abs(getAngleDelta(body.angle, savedBlock.rotation || 0))
        if (drift > BUILDER_PUBLISH_MAX_DRIFT || tilt > BUILDER_PUBLISH_MAX_TILT) {
            return { ok: false, reason: 'unstable' }
        }
    }

    for (const body of physics?.bodies?.values?.() || []) {
        const id = body.plugin.game?.id
        if (!savedBlockIds.has(id)) {
            return { ok: false }
        }
    }

    return { ok: true }
}

function getBodyAudioMaterial(body) {
    const type = body.plugin.game?.type

    if (type === 'block' || type === 'builder-block') {
        return body.plugin.game.material || 'wood'
    }

    if (type === 'flag' || type === 'builder-flag') {
        return 'flag'
    }

    if (body.label === 'ground' || body.label === 'builder-ground') {
        return 'ground'
    }

    return 'wood'
}

function getHeavierCollisionMaterial(bodyA, bodyB) {
    const materialA = bodyA.plugin.game?.material || 'wood'
    const materialB = bodyB.plugin.game?.material || 'wood'
    const densityA = MATERIALS[materialA]?.density || 0
    const densityB = MATERIALS[materialB]?.density || 0

    return densityA >= densityB ? materialA : materialB
}

function getCollisionPairKey(pair, bodyA, bodyB) {
    if (pair.id) {
        return pair.id
    }

    return [bodyA.id, bodyB.id].sort((a, b) => a - b).join(':')
}

function getBlockFootprint(block) {
    const width = block.width || BLOCK_SIZE.width
    const height = block.height || BLOCK_SIZE.height
    const rotation = block.rotation || 0
    const cos = Math.abs(Math.cos(rotation))
    const sin = Math.abs(Math.sin(rotation))

    return {
        width: width * cos + height * sin,
        height: width * sin + height * cos
    }
}

function getPolygonAxes(points) {
    return points.map((point, index) => {
        const next = points[(index + 1) % points.length]
        const edge = {
            x: next.x - point.x,
            y: next.y - point.y
        }
        const length = Math.hypot(edge.x, edge.y) || 1

        return {
            x: -edge.y / length,
            y: edge.x / length
        }
    })
}

function projectPolygon(points, axis) {
    let min = Infinity
    let max = -Infinity

    for (const point of points) {
        const projected = point.x * axis.x + point.y * axis.y
        min = Math.min(min, projected)
        max = Math.max(max, projected)
    }

    return { min, max }
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}

function lerp(start, end, progress) {
    return start + (end - start) * progress
}

function easeOutCubic(value) {
    return 1 - (1 - value) ** 3
}

function getPointerCaptureTarget(event, fallback) {
    const launchZone = event.target?.closest?.('[data-launch-zone]')

    if (typeof launchZone?.setPointerCapture === 'function') {
        return launchZone
    }

    return typeof event.currentTarget?.setPointerCapture === 'function'
        ? event.currentTarget
        : fallback
}

function getSlingCenter() {
    return { x: WORLD.launcherX, y: WORLD.launcherY + 1.82 }
}

function getReadyBulletRadius(bulletType) {
    return getBulletConfig(bulletType).radius + 0.3
}

function isPointOnReadyBullet(point, bulletType = 'basic') {
    const anchor = getSlingCenter()
    return Math.hypot(point.x - anchor.x, point.y - anchor.y) <= getReadyBulletRadius(bulletType)
}

function clampBulletPullPoint(point, anchor, bulletType = 'basic') {
    const dx = point.x - anchor.x
    const dy = point.y - anchor.y
    const distance = Math.hypot(dx, dy)
    const minY = WORLD.groundY + getBulletConfig(bulletType).radius * 1.25
    const maxDistance = destroyerTuning.maxPullDistance

    if (distance <= maxDistance) {
        return {
            x: point.x,
            y: Math.max(point.y, minY)
        }
    }

    const scale = maxDistance / distance
    return {
        x: anchor.x + dx * scale,
        y: Math.max(anchor.y + dy * scale, minY)
    }
}

function calculateBulletLaunchVelocity(point, anchor, bulletType = 'basic') {
    const launchPower = destroyerTuning.launchPower * getBulletLaunchFactor(bulletType)

    return {
        x: (anchor.x - point.x) * launchPower,
        y: (anchor.y - point.y) * launchPower
    }
}

function projectBulletTrajectory(point, anchor, bulletType = 'basic') {
    const velocity = calculateBulletLaunchVelocity(point, anchor, bulletType)

    if (Math.hypot(velocity.x, velocity.y) < destroyerTuning.trajectoryMinSpeed) {
        return []
    }

    const config = getBulletConfig(bulletType)
    const startGap = Math.max(config.radius * 1.8, 0.42)
    const points = predictProjectilePath({
        position: point,
        velocity,
        gravity: getDestroyerGravity(),
        linearDamping: airFrictionToDamping(getBulletAirResistance(bulletType)),
        maxSeconds: TRAJECTORY_MAX_SECONDS,
        shouldStop: (sample) => hasTrajectoryFinished({ position: sample }, config.radius)
    }).filter((sample) => Math.hypot(sample.x - point.x, sample.y - point.y) >= startGap)

    if (points.length < 2) {
        return points
    }

    const visiblePointCount = Math.max(2, Math.ceil(points.length * destroyerTuning.trajectoryPreviewRatio))
    const progressDenominator = Math.max(1, points.length - 1)

    return points.slice(0, visiblePointCount).map((sample, index) => ({
        ...sample,
        trajectoryProgress: index / progressDenominator
    }))
}

function getBulletConfig(bulletType) {
    return BOMBS[bulletType] || BOMBS.basic
}

function getBombDensity(bulletType) {
    return getBulletConfig(bulletType).density || DEFAULT_BOMB_DENSITY
}

function getBombWeight(bulletType) {
    const config = getBulletConfig(bulletType)
    return Math.PI * config.radius * config.radius * getBombDensity(bulletType)
}

function getBulletLaunchFactor(bulletType) {
    const weight = getBombWeight(bulletType)
    const basicWeight = getBombWeight('basic')
    const weightFactor = basicWeight > 0 && weight > 0 ? Math.sqrt(basicWeight / weight) : 1

    return clamp(weightFactor, destroyerTuning.launchWeightMinFactor, destroyerTuning.launchWeightMaxFactor)
}

function getBulletAirResistance(bulletType) {
    return getBulletConfig(bulletType).airResistance ?? destroyerTuning.defaultAirResistance
}

function getBulletGroundBounce(bulletType) {
    return getBulletConfig(bulletType).groundBounce ?? 0.08
}

function getBulletLaunchSpin(bulletType, velocity) {
    const config = getBulletConfig(bulletType)
    const speed = Vector.magnitude(velocity)
    const direction = velocity.x >= 0 ? -1 : 1
    return direction * clamp((speed / Math.max(config.radius, 0.1)) * 0.055, 0.12, 0.36)
}

function calculateBulletDamage(bulletBody, material, speed, normalFactor = 1) {
    const bulletGame = bulletBody.plugin.game
    const bulletType = bulletGame?.bulletType || 'basic'
    const baseConfig = getBulletConfig(bulletType)
    const config = bulletGame?.fragment
        ? { ...baseConfig, materialMultipliers: baseConfig.fragmentMaterialMultipliers || baseConfig.materialMultipliers }
        : baseConfig
    const bulletMass = bulletGame?.fragment ? bulletBody.mass : getBombWeight(bulletType)

    if (material === 'flag') {
        return calculateFlagImpactDamage({
            bulletConfig: config,
            bulletMass,
            speed,
            normalFactor
        })
    }

    return calculateImpactDamage({
        bulletConfig: config,
        bulletMass,
        speed,
        targetMaterial: material,
        normalFactor
    })
}

function getBulletSplitX() {
    return (WORLD.launcherX + WORLD.buildMinX) / 2
}

function canBulletSplit(body) {
    const game = body?.plugin?.game
    return Boolean(getBulletConfig(game?.bulletType).splitCount)
        && !game.split
        && !game.contacted
        && !body.removed
}

function shouldSplitBullet(body) {
    return canBulletSplit(body)
        && (body.plugin.game.splitRequested || body.position.x >= getBulletSplitX())
}

function getCollisionNormalFactor(pair, movingBody) {
    const normal = pair?.collision?.normal

    const incoming = movingBody.impactVelocity || movingBody.velocity
    if (!normal || Vector.magnitude(incoming) < 0.0001) {
        return 1
    }

    const velocity = Vector.normalise(incoming)
    return clamp(Math.abs(Vector.dot(velocity, normal)), 0.25, 1)
}

function getCollisionPoint(pair, bodyA, bodyB) {
    const supports = (pair?.collision?.supports || [])
        .filter((support) => Number.isFinite(support?.x) && Number.isFinite(support?.y))

    if (supports.length) {
        const total = supports.reduce((point, support) => ({
            x: point.x + support.x,
            y: point.y + support.y
        }), { x: 0, y: 0 })

        return {
            x: total.x / supports.length,
            y: total.y / supports.length
        }
    }

    if (bodyA?.position && bodyB?.position) {
        return {
            x: (bodyA.position.x + bodyB.position.x) / 2,
            y: (bodyA.position.y + bodyB.position.y) / 2
        }
    }

    return bodyA?.position || bodyB?.position || { x: 0, y: 0 }
}

function getImpactDirection(bulletBody, targetBody) {
    const fromBullet = Vector.sub(targetBody.position, bulletBody.position)

    if (Vector.magnitude(fromBullet) > 0.0001) {
        return Vector.normalise(fromBullet)
    }

    if (Vector.magnitude(bulletBody.velocity) > 0.0001) {
        return Vector.normalise(bulletBody.velocity)
    }

    return { x: 1, y: 0 }
}

function getSafeDirection(from, to, fallback = { x: 1, y: 0 }) {
    const delta = Vector.sub(to, from)

    return Vector.magnitude(delta) > 0.0001
        ? Vector.normalise(delta)
        : fallback
}

function hasTrajectoryFinished(body, radius) {
    return body.position.y <= WORLD.groundY + radius * 0.85
        || body.position.x > WORLD.buildMaxX + 4
        || body.position.x < WORLD.launcherX - destroyerTuning.maxPullDistance - 3
}

function getDestroyerWallDuration(durationMs) {
    return Math.round(durationMs / destroyerTuning.timeScale)
}

function loadRuntimeTuningFromStorage() {
    try {
        const raw = localStorage.getItem(TUNING_STORAGE_KEY)
        if (!raw) {
            return
        }

        applyRuntimeTuningSnapshot(JSON.parse(raw))
    } catch {
        localStorage.removeItem(TUNING_STORAGE_KEY)
    }
}

function applyRuntimeTuningSnapshot(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
        return
    }

    applyDestroyerTuningValues(snapshot.physics)
    applyDestroyerTuningValues(snapshot.sling)

    for (const [key, value] of Object.entries(snapshot.damage || {})) {
        setDamageTuningValue(key, value)
    }

    applyNumericObjectSnapshot(BOMBS, snapshot.bullets)
    applyNumericObjectSnapshot(MATERIALS, snapshot.materials)
}

function saveRuntimeTuning() {
    try {
        localStorage.setItem(TUNING_STORAGE_KEY, JSON.stringify(collectRuntimeTuningSnapshot()))
    } catch {
        // Tuning still works for the current session if storage is unavailable.
    }
}

function resetRuntimeTuning() {
    Object.assign(destroyerTuning, DEFAULT_DESTROYER_TUNING)
    resetDamageTuning()
    replaceObjectContents(BOMBS, ORIGINAL_BOMBS)
    replaceObjectContents(MATERIALS, ORIGINAL_MATERIALS)
    try {
        localStorage.removeItem(TUNING_STORAGE_KEY)
    } catch {
        // Ignore storage failures for the temporary tuner.
    }
}

function collectRuntimeTuningSnapshot() {
    return {
        physics: collectDestroyerTuningValues(PHYSICS_TUNING_FIELDS),
        sling: collectDestroyerTuningValues(SLING_TUNING_FIELDS),
        damage: getDamageTuning(),
        bullets: collectNumericObjectSnapshot(BOMBS),
        materials: collectNumericObjectSnapshot(MATERIALS)
    }
}

function applyDestroyerTuningValues(values) {
    for (const [key, value] of Object.entries(values || {})) {
        if (Object.hasOwn(destroyerTuning, key) && Number.isFinite(Number(value))) {
            destroyerTuning[key] = Number(value)
        }
    }
}

function collectDestroyerTuningValues(fields) {
    return Object.fromEntries(fields.map(({ key }) => [key, destroyerTuning[key]]))
}

function collectNumericObjectSnapshot(source) {
    return Object.fromEntries(Object.entries(source).map(([key, value]) => [
        key,
        collectNumericLeafSnapshot(value)
    ]))
}

function collectNumericLeafSnapshot(source) {
    const result = {}

    for (const [key, value] of Object.entries(source || {})) {
        if (EXCLUDED_TUNING_NUMERIC_KEYS.has(key)) {
            continue
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            result[key] = value
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = collectNumericLeafSnapshot(value)
            if (Object.keys(nested).length) {
                result[key] = nested
            }
        }
    }

    return result
}

function applyNumericObjectSnapshot(target, snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
        return
    }

    for (const [key, values] of Object.entries(snapshot)) {
        if (target[key] && values && typeof values === 'object') {
            applyNumericLeafSnapshot(target[key], values)
        }
    }
}

function applyNumericLeafSnapshot(target, snapshot) {
    for (const [key, value] of Object.entries(snapshot || {})) {
        if (EXCLUDED_TUNING_NUMERIC_KEYS.has(key)) {
            continue
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            target[key] = value
        } else if (target[key] && value && typeof value === 'object') {
            applyNumericLeafSnapshot(target[key], value)
        }
    }
}

function replaceObjectContents(target, source) {
    for (const key of Object.keys(target)) {
        delete target[key]
    }

    for (const [key, value] of Object.entries(source)) {
        target[key] = clonePlain(value)
    }
}

function getTunableNumericEntries(source, prefix = '') {
    const entries = []

    for (const [key, value] of Object.entries(source || {})) {
        if (EXCLUDED_TUNING_NUMERIC_KEYS.has(key)) {
            continue
        }

        const path = prefix ? `${prefix}.${key}` : key
        if (typeof value === 'number' && Number.isFinite(value)) {
            entries.push({
                path,
                label: formatTuningFieldLabel(path),
                value
            })
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            entries.push(...getTunableNumericEntries(value, path))
        }
    }

    return entries
}

async function copyTextToClipboard(text) {
    try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // Fall through to the selection-based copy path below.
    }

    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    textarea.style.top = '0'
    document.body.append(textarea)
    textarea.focus()
    textarea.select()

    try {
        return document.execCommand('copy')
    } catch {
        return false
    } finally {
        textarea.remove()
    }
}

function getTuningRange(scope, path, value) {
    const key = path.split('.').at(-1)

    if (scope === 'bullet') {
        if (key === 'radius') return { min: 0.12, max: 0.8, step: 0.01 }
        if (key === 'density') return { min: 0.001, max: 0.08, step: 0.0005 }
        if (key === 'impactStrength' || key === 'blastDamage') return { min: 0, max: 220, step: 1 }
        if (key === 'impactForce' || key === 'blastImpulse') return { min: 0, max: 4, step: 0.01 }
        if (key === 'blastRadius') return { min: 0, max: 4, step: 0.01 }
        if (key === 'airResistance') return { min: 0, max: 0.012, step: 0.0001 }
        if (key === 'groundBounce') return { min: 0, max: 1, step: 0.01 }
        if (path.includes('materialMultipliers') || key.includes('Multiplier')) return { min: 0, max: 5, step: 0.01 }
    }

    if (scope === 'material') {
        if (key === 'cost' || key === 'health' || key === 'baseHealth') return { min: 0, max: 240, step: 1 }
        if (key === 'density') return { min: 0.001, max: 0.08, step: 0.0005 }
        if (key === 'friction' || key === 'restitution' || key === 'toughness') return { min: 0, max: 3, step: 0.01 }
        if (key === 'frictionAir') return { min: 0, max: 0.02, step: 0.0005 }
        if (key === 'brittleness') return { min: 0, max: 3, step: 0.01 }
        if (key.includes('Strength') || key === 'impactDamping' || key.includes('Multiplier')) return { min: 0, max: 3, step: 0.01 }
        if (key.includes('Threshold')) return { min: 0, max: 1.5, step: 0.001 }
        if (key.includes('Damage') || key === 'fallDamage') return { min: 0, max: 3, step: 0.01 }
        if (key.includes('Speed')) return { min: 0, max: 3, step: 0.01 }
    }

    const numericValue = Math.abs(Number(value) || 0)
    const max = Math.max(1, numericValue * 3)
    return { min: 0, max: roundTo(max, 3), step: 0.01 }
}

function getCurrentTuningInputValue(input) {
    const scope = input.dataset.tuningScope
    const key = input.dataset.tuningKey
    const path = input.dataset.tuningPath || key

    if ((scope === 'physics' || scope === 'sling') && Object.hasOwn(destroyerTuning, key)) {
        return destroyerTuning[key]
    }

    if (scope === 'damage') {
        return getDamageTuning()[key]
    }

    if (scope === 'bullet') {
        return getNestedNumericValue(BOMBS[input.dataset.bullet], path)
    }

    if (scope === 'material') {
        return getNestedNumericValue(MATERIALS[input.dataset.material], path)
    }

    if (scope === 'materialMacro') {
        const material = MATERIALS[input.dataset.material]
        const control = getMaterialLinkedControl(key)
        return material && control ? getMaterialLinkedControlValue(material, control) : NaN
    }

    return NaN
}

function getMaterialLinkedControl(key) {
    return MATERIAL_LINKED_CONTROLS.find((control) => control.key === key)
}

function getMaterialLinkedControlValue(material, control) {
    const scores = control.bindings
        .map((binding) => getMaterialLinkedBindingScore(material, binding))
        .filter(Number.isFinite)

    if (!scores.length) {
        return 0
    }

    const average = scores.reduce((total, score) => total + score, 0) / scores.length
    return Math.round(clamp(average, 0, 1) * 100)
}

function getMaterialLinkedBindingScore(material, binding) {
    const value = getNestedNumericValue(material, binding.path)
    if (!Number.isFinite(value)) {
        return NaN
    }

    const min = Number(binding.min)
    const max = Number(binding.max)
    if (!Number.isFinite(min) || !Number.isFinite(max) || Math.abs(max - min) < 0.000001) {
        return NaN
    }

    return clamp((value - min) / (max - min), 0, 1)
}

function applyMaterialLinkedControl(material, control, sliderValue) {
    const progress = clamp((Number(sliderValue) || 0) / 100, 0, 1)

    for (const binding of control.bindings) {
        const range = getTuningRange('material', binding.path, getNestedNumericValue(material, binding.path))
        const rawValue = Number(binding.min) + (Number(binding.max) - Number(binding.min)) * progress
        setNestedNumericValue(material, binding.path, snapTuningValue(rawValue, range.step))
    }
}

function snapTuningValue(value, step) {
    const numericStep = Number(step)
    if (!Number.isFinite(numericStep) || numericStep <= 0) {
        return roundTo(value, 4)
    }

    const decimals = getStepDecimals(numericStep)
    return roundTo(Math.round(value / numericStep) * numericStep, decimals)
}

function getStepDecimals(step) {
    const text = String(step)
    if (!text.includes('.')) {
        return 0
    }

    return Math.min(6, text.split('.')[1].length)
}

function getNestedNumericValue(source, path) {
    let cursor = source

    for (const key of String(path).split('.').filter(Boolean)) {
        cursor = cursor?.[key]
    }

    const value = Number(cursor)
    return Number.isFinite(value) ? value : NaN
}

function setNestedNumericValue(target, path, value) {
    const keys = String(path).split('.').filter(Boolean)
    let cursor = target

    for (const key of keys.slice(0, -1)) {
        if (!cursor[key] || typeof cursor[key] !== 'object') {
            cursor[key] = {}
        }
        cursor = cursor[key]
    }

    cursor[keys.at(-1)] = value
}

function formatTuningFieldLabel(path) {
    const parts = path.split('.')
    const key = parts.at(-1)
    const label = key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replaceAll('_', ' ')
        .replace(/^./, (char) => char.toUpperCase())

    return parts.length > 1
        ? `${parts.slice(0, -1).map(formatTuningPathPart).join(' ')} ${label}`
        : label
}

function getTuningHelpText(scope, path, label) {
    const key = String(path).split('.').at(-1)

    if (scope === 'materialMacro') {
        return getMaterialLinkedControl(key)?.help || 'Linked material control that moves related material values together.'
    }

    if (String(path).startsWith('materialMultipliers.')) {
        return `Damage multiplier against ${formatTuningPathPart(key).toLowerCase()} blocks for this bullet. Higher values break that material faster.`
    }

    if (TUNING_HELP_TEXT[path]) {
        return TUNING_HELP_TEXT[path]
    }

    if (TUNING_HELP_TEXT[key]) {
        return TUNING_HELP_TEXT[key]
    }

    return `Adjusts ${String(label || key).toLowerCase()} for the current ${scope} tuning group. Higher values increase that effect.`
}

function createTuningFieldId(scope, path, bullet, material) {
    return [
        'tuning',
        scope,
        bullet,
        material,
        path
    ]
        .filter(Boolean)
        .join('-')
        .replace(/[^a-zA-Z0-9_-]+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase()
}

function formatTuningPathPart(value) {
    return String(value)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (char) => char.toUpperCase())
}

function formatTuningValue(value) {
    const numericValue = Number(value) || 0
    if (Math.abs(numericValue) >= 100) {
        return String(Math.round(numericValue))
    }
    if (Math.abs(numericValue) >= 10) {
        return numericValue.toFixed(1).replace(/\.0$/, '')
    }
    if (Math.abs(numericValue) >= 1) {
        return numericValue.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
    }
    return numericValue.toFixed(4).replace(/0+$/, '').replace(/\.$/, '') || '0'
}

function clonePlain(value) {
    return JSON.parse(JSON.stringify(value))
}

function roundTo(value, decimals) {
    const scale = 10 ** decimals
    return Math.round(value * scale) / scale
}

function escapeHtml(value) {
    return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;')
}

function escapeAttribute(value) {
    return escapeHtml(value).replaceAll('`', '&#096;')
}

function hasKeyboardControlSurface() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return true
    }

    return window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

function isTypingTarget(target) {
    if (!target || typeof Element === 'undefined' || !(target instanceof Element)) {
        return false
    }

    return target.matches('input, textarea, select')
        || target.isContentEditable
        || Boolean(target.closest('[contenteditable="true"]'))
}
